import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { MedicalDisclaimer } from "@/components/MedicalDisclaimer";
import portraitScrubsAsset from "@/assets/dr-kukrer-scrubs.webp.asset.json";

const AUTHOR_IMAGE = `https://www.sadikkukrer.com${portraitScrubsAsset.url}`;

interface FAQ {
  q: string;
  a: string;
}

interface Post {
  slug: string;
  title: string;
  description: string;
  metaTitle?: string;
  metaDescription?: string;
  date: string;
  updated?: string;
  readingTime: string;
  keyword: string;
  enSlug?: string;
  tldr?: string[];
  faqs?: FAQ[];
}

const SITE_UPDATED = "2026-09-01";

const POSTS: Record<string, Post> = {
  "hamilelik-belirtileri": {
    slug: "hamilelik-belirtileri",
    title: "Hamilelik Belirtileri: İlk Haftadan İtibaren 15 Erken İşaret",
    description:
      "Adet gecikmesi, bulantı, göğüs hassasiyeti, yorgunluk ve daha fazlası. Erken hamilelik belirtileri ne zaman başlar, ne zaman gebelik testi yapılmalı?",
    date: "2026-07-07",
    updated: SITE_UPDATED,
    readingTime: "8 dk okuma",
    keyword: "hamilelik belirtileri",
    enSlug: "early-pregnancy-symptoms",
    tldr: [
      "En güvenilir erken belirti adet gecikmesidir; 5–7 günden fazla gecikmede test yapılmalıdır.",
      "Belirtilerin çoğu döllenmeden 6–12 gün sonra, implantasyonla birlikte başlar.",
      "İdrar testi beklenen adetten 1 gün sonra %99'a yakın doğrulukla sonuç verir.",
    ],
    faqs: [
      {
        q: "Hamilelik belirtileri ne zaman başlar?",
        a: "Belirtilerin çoğu döllenmeden 6–12 gün sonra, embriyonun rahime yerleşmesiyle ortaya çıkar. Bazı kadınlar adet gecikmesine kadar hiçbir belirti hissetmez; ikisi de normaldir.",
      },
      {
        q: "Hangi belirti en güvenilirdir?",
        a: "Düzenli adet gören bir kadında adet gecikmesi en güvenilir işarettir. 5–7 günden uzun gecikme, evde idrar gebelik testi yapmak için yeterli nedendir.",
      },
      {
        q: "Test negatif çıktı ama belirtiler devam ediyor, ne yapmalıyım?",
        a: "Erken yapılan idrar testleri yanlış negatif verebilir. 3–4 gün sonra sabah ilk idrarla tekrar edin; sonuç hâlâ belirsizse kandan beta-hCG bakılması netleştirir.",
      },
    ],
  },
  "gebelik-haftasi-hesaplama": {
    slug: "gebelik-haftasi-hesaplama",
    title: "Gebelik Haftası Nasıl Hesaplanır? Son Adet Tarihinden Doğum Gününe Adım Adım Rehber",

    metaTitle: "Gebelik Haftası Hesaplama: SAT ve Ultrason Rehberi",
    description:
      "Son adet tarihine göre gebelik haftası ve doğum tarihi (EDD) nasıl hesaplanır? Naegele formülü, ultrason ölçümleri, IVF gebeliklerinde farklılıklar ve trimestrlar.",
    metaDescription:
      "Gebelik haftası ve doğum tarihi (EDD) hesaplama: Naegele formülü, ultrason ölçümleri ve IVF gebeliklerinde farklılıklar.",
    date: "2026-07-14",
    updated: SITE_UPDATED,
    readingTime: "9 dk okuma",
    keyword: "gebelik haftası hesaplama",
    enSlug: "how-to-calculate-pregnancy-week",
    tldr: [
      "Gebelik haftası son adet tarihinin (SAT) ilk gününden itibaren hesaplanır, döllenme tarihinden değil.",
      "Beklenen doğum tarihi (EDD) Naegele formülüyle: SAT + 7 gün − 3 ay + 1 yıl.",
      "İlk trimester ultrasonu (CRL ölçümü) SAT'a göre 5 günden fazla fark varsa haftayı yeniler.",
    ],
    faqs: [
      {
        q: "Gebelik haftası nasıl hesaplanır?",
        a: "Son adet tarihinin (SAT) ilk gününden itibaren geçen tam haftalar gebelik haftasını verir. Örneğin SAT'tan 8 hafta geçmişse 8 haftalık gebeliksiniz.",
      },
      {
        q: "Doğum tarihi nasıl bulunur?",
        a: "Naegele formülü kullanılır: son adet tarihine 7 gün ekleyin, 3 ay çıkarın, 1 yıl ekleyin. Sonuç 280 günlük (40 haftalık) beklenen doğum tarihidir.",
      },
      {
        q: "IVF gebeliğinde hesap farklı mı?",
        a: "Evet. Embriyo transfer tarihi biliniyorsa doğrudan bu tarih üzerinden hesaplanır; SAT kullanılmaz. IVF'te hafta çok daha kesindir.",
      },
    ],
  },
  "gebelik-testi-ne-zaman-nasil-yapilir": {
    slug: "gebelik-testi-ne-zaman-nasil-yapilir",
    title: "Gebelik Testi Ne Zaman ve Nasıl Yapılır? İdrar ve Kan Testleri Rehberi",

    metaTitle: "Gebelik Testi Ne Zaman Yapılır? İdrar ve Kan Testi",
    description:
      "Gebelik testi ne zaman pozitif çıkar? İdrar testi (hCG) ve kan testi (beta-hCG) farkları, doğru zamanlama, yanlış negatif/pozitif nedenleri ve sonrası.",
    date: "2026-07-21",
    updated: SITE_UPDATED,
    readingTime: "8 dk okuma",
    keyword: "gebelik testi ne zaman yapılır",
    enSlug: "when-to-take-pregnancy-test",
    tldr: [
      "İdrar testi en güvenilir sonucu beklenen adetten 1 gün sonra sabah ilk idrarla verir.",
      "Kandan beta-hCG, döllenmeden 8–10 gün sonra pozitifleşebilir; idrardan 2–3 gün daha erkendir.",
      "Erken test, seyreltilmiş idrar veya hCG düşüklüğü yanlış negatife yol açabilir.",
    ],
    faqs: [
      {
        q: "Gebelik testi ne zaman pozitif çıkar?",
        a: "Kan (beta-hCG) testi döllenmeden 8–10 gün sonra pozitifleşebilir. Ev tipi idrar testleri için en güvenli zaman beklenen adet tarihinden 1 gün sonrasıdır.",
      },
      {
        q: "İdrar testi mi kan testi mi daha güvenilir?",
        a: "Kandan bakılan beta-hCG hem daha erken pozitifleşir hem de sayısal değer verdiği için gebelik haftası ve seyri hakkında bilgi sağlar. İdrar testi ise pratik ve %99 doğruluktadır.",
      },
      {
        q: "Yanlış negatif neden çıkar?",
        a: "Testin çok erken yapılması, gün içinde çok sıvı tüketip idrarın seyrelmesi ve nadiren dış gebelik gibi hCG'nin düşük seyrettiği durumlar yanlış negatife yol açabilir.",
      },
    ],
  },
  "gebelikte-kanama": {
    slug: "gebelikte-kanama",
    title: "Gebelikte Kanama: Nedenleri, Ne Zaman Acildir?",
    description:
      "Gebelikte kanama neden olur? İmplantasyon kanaması, düşük tehdidi, dış gebelik, plasenta previa ve dekolman. Hangi kanama acil, hangisi bekleyebilir?",
    date: "2026-07-28",
    updated: SITE_UPDATED,
    readingTime: "9 dk okuma",
    keyword: "gebelikte kanama",
    enSlug: "bleeding-in-pregnancy",
    tldr: [
      "Gebeliklerin %20-30'unda kanama görülür, çoğu sağlıklı sonuçlanır.",
      "Şiddetli ağrı, parlak kırmızı bol kanama, bayılma veya omuz ağrısı acil başvuru gerektirir.",
      "Rh negatif kanı olan annelerde her kanamada anti-D iğne değerlendirmesi gerekir.",
    ],
    faqs: [
      {
        q: "Gebelikte kanama her zaman düşük anlamına gelir mi?",
        a: "Hayır. İlk trimester kanamalarının önemli bölümü implantasyon veya servikal hassasiyet gibi selim nedenlerdir. Yine de her kanama mutlaka değerlendirilmelidir.",
      },
      {
        q: "Gebelikte kanama normal mi, ne zaman normaldir?",
        a: "İmplantasyon sırasında görülen 1-2 günlük pembe-kahverengi lekelenme ve muayene/ilişki sonrası kısa süreli hafif kanama sık ve genellikle zararsızdır. Adet gibi bol kırmızı kanama asla normal kabul edilmez ve değerlendirilmelidir.",
      },
      {
        q: "Gebelikte kanama ne kadar sürer?",
        a: "İmplantasyon kanaması genellikle birkaç saatten 1-2 güne kadar sürer ve kendiliğinden geçer. 2 günden uzun süren, giderek artan veya bir tampon/ped ıslatan kanamalar mutlaka acilen değerlendirilmelidir.",
      },
      {
        q: "Gebelikte lekelenme neden olur ve ne zaman oluşur?",
        a: "En sık nedenler implantasyon (adetin gecikmesinden 1-2 hafta önce), servikal hassasiyet (muayene/ilişki sonrası), progesteron dalgalanmaları ve nadiren düşük tehdididir. İkinci-üçüncü trimesterde plasenta previa akla gelmelidir.",
      },
      {
        q: "İki aylık hamilelikte kan gelmesi tehlikeli midir?",
        a: "8. haftada görülen kanama düşük tehdidi, dış gebelik veya subkoryonik hematom gibi durumları düşündürebilir. Ultrason ve beta-hCG takibi ile ayırıcı tanı yapılmalıdır; kendi kendine karar vermeden mutlaka başvurun.",
      },
      {
        q: "Hangi kanama acildir?",
        a: "Bol, parlak kırmızı kanama; şiddetli tek taraflı ağrı; bayılma, omuz ağrısı veya doku parçası düşürme; ateşle birlikte kokulu akıntı acil hastane başvurusu gerektirir.",
      },
      {
        q: "Dış gebelik kanaması nasıl olur?",
        a: "Genellikle koyu kahverengi lekelenme şeklinde başlar, tek taraflı kasık ağrısı eşlik eder. Beta-hCG değerinin beklenen kadar yükselmemesi tipiktir; acil değerlendirme şarttır.",
      },
    ],
  },
  "gebelikte-beslenme": {
    slug: "gebelikte-beslenme",
    title: "Gebelikte Beslenme: Nelere Dikkat Etmeli, Nelerden Kaçınmalı?",
    metaTitle: "Gebelikte Beslenme: Neler Yemeli, Neler Yenmemeli?",
    description:
      "Gebelikte doğru beslenme rehberi: kalori ihtiyacı, folik asit ve demir, kaçınılması gereken yiyecekler, trimester bazlı öneriler ve sık sorulan sorular.",
    date: "2026-08-04",
    updated: SITE_UPDATED,
    readingTime: "10 dk okuma",
    keyword: "gebelikte beslenme",
    enSlug: "nutrition-in-pregnancy",
    tldr: [
      "Ek kalori ihtiyacı 1. trimesterde yoktur; 2.'de +340 kcal, 3.'te +450 kcal/gün.",
      "Folik asit gebelikten en az 1 ay önce başlanmalı, günlük 400–800 mcg alınmalıdır.",
      "Çiğ balık, çiğ et, pastörize edilmemiş süt ürünleri ve alkol tamamen kaçınılmalıdır.",
    ],
    faqs: [
      {
        q: "Gebelikte günde ne kadar kafein alınabilir?",
        a: "Günde 200 mg'ın altı güvenli kabul edilir (yaklaşık 1 fincan filtre kahve veya 2 fincan çay). Fazlası düşük ve düşük doğum ağırlığı riskini artırabilir.",
      },
      {
        q: "Hangi vitaminleri mutlaka almalıyım?",
        a: "Folik asit (400–800 mcg), D vitamini, iyot ve gerekiyorsa demir. Kalsiyum ve omega-3 çoğu zaman gıdayla karşılanır; doktorunuz kan tetkiklerine göre planlar.",
      },
      {
        q: "Kilo alımı ne kadar olmalı?",
        a: "Normal kilolu gebelerde 11.5–16 kg, fazla kilolularda 7–11.5 kg, obezlerde 5–9 kg önerilir. Zayıf gebelerde 12.5–18 kg hedeflenir.",
      },
    ],
  },
  "yumurtalik-kisti": {
    slug: "yumurtalik-kisti",
    title: "Yumurtalık Kisti: Belirtileri, Tedavisi ve Ne Zaman Ameliyat Gerekir?",

    metaTitle: "Yumurtalık Kisti: Belirtiler ve Tedavi Seçenekleri",
    description:
      "Fonksiyonel kist, endometrioma, dermoid ve PKOS: yumurtalık kistlerinin çeşitleri, belirtileri, takip kriterleri, tedavi seçenekleri ve ameliyat endikasyonları.",
    metaDescription:
      "Fonksiyonel kist, endometrioma, dermoid ve PKOS: belirtileri, takip kriterleri, tedavi seçenekleri ve ameliyat endikasyonları.",
    date: "2026-08-11",
    updated: SITE_UPDATED,
    readingTime: "10 dk okuma",
    keyword: "yumurtalık kisti",
    enSlug: "ovarian-cysts",
    tldr: [
      "Fonksiyonel kistlerin büyük çoğunluğu 2–3 adet döngüsünde kendiliğinden geriler.",
      "5 cm üzeri, kompleks yapıda, hızlı büyüyen veya ağrı yapan kistler cerrahi değerlendirme gerektirir.",
      "Ani şiddetli kasık ağrısı torsiyon (kist dönmesi) olabilir; acil müdahale şarttır.",
    ],
    faqs: [
      {
        q: "Yumurtalık kisti tehlikeli midir?",
        a: "Çoğu kist selimdir ve kendiliğinden kaybolur. Ancak torsiyon, rüptür veya nadiren maligniteye dönüşüm riski nedeniyle boyut, iç yapı ve semptomlar mutlaka değerlendirilmelidir.",
      },
      {
        q: "Hangi durumda ameliyat gerekir?",
        a: "5–6 cm üzeri kalıcı kistler, kompleks/solid alan içerenler, endometrioma, dermoid kistler, torsiyon veya rüptür ve şüpheli görüntüleme bulguları cerrahi endikasyonlarıdır.",
      },
      {
        q: "Kist ameliyatı laparoskopik yapılabilir mi?",
        a: "Evet. Selim kistlerin büyük çoğunluğu laparoskopik (kapalı) yolla çıkarılabilir. İyileşme daha hızlı, kesi izi çok küçüktür ve yumurtalık dokusu korunur.",
      },
    ],
  },
  "vajinal-akinti": {
    slug: "vajinal-akinti",
    title: "Vajinal Akıntı: Normal mi, Enfeksiyon mu?",
    description:
      "Vajinal akıntı ne zaman normaldir, ne zaman enfeksiyon belirtisidir? Renk, koku ve kıvamına göre nedenleri, mantar, bakteriyel vajinozis, trikomoniyaz ve tedavi.",
    metaDescription:
      "Vajinal akıntı normal mi enfeksiyon mu? Renk, koku ve kıvamına göre mantar, bakteriyel vajinozis, trikomoniyaz ve tedavi seçenekleri.",
    date: "2026-08-18",
    updated: SITE_UPDATED,
    readingTime: "9 dk okuma",
    keyword: "vajinal akıntı",
    enSlug: "vaginal-discharge",
    tldr: [
      "Berrak veya süt beyazı, kokusuz akıntı normaldir ve döngü boyunca miktarı değişir.",
      "Kaşıntı + peynirimsi akıntı mantar; balık kokulu gri akıntı bakteriyel vajinozis düşündürür.",
      "Köpüklü sarı-yeşil akıntı trikomoniyaz olabilir; partnerle birlikte tedavi gerekir.",
    ],
    faqs: [
      {
        q: "Vajinal akıntı ne zaman normaldir?",
        a: "Berrak veya süt beyazı, kokusuz, kaşıntı ve yanma yapmayan akıntı fizyolojiktir. Yumurtlama döneminde daha kaygan ve bol, adet öncesi yoğun görülebilir.",
      },
      {
        q: "Mantar enfeksiyonu belirtileri nelerdir?",
        a: "Beyaz, peynirimsi kıvamda, kokusuz akıntıya kaşıntı, yanma ve kızarıklık eşlik eder. Cinsel ilişki ve idrar sırasında rahatsızlık olabilir.",
      },
      {
        q: "Doğal yolla tedavi olur mu?",
        a: "Hafif fizyolojik akıntı için özel tedavi gerekmez, uygun hijyen yeterlidir. Ancak enfeksiyonlar (mantar, BV, trikomoniyaz) mutlaka hekim önerisiyle uygun ilaçla tedavi edilmelidir.",
      },
    ],
  },
  endometriozis: {
    slug: "endometriozis",
    title: "Endometriozis: Belirtileri, Tanısı ve Tedavi Seçenekleri",
    description:
      "Endometriozis nedir, neden olur? Şiddetli adet ağrısı, cinsel ilişkide ağrı, kısırlık ve tanı yöntemleri. İlaç, hormon ve laparoskopik cerrahi tedavi seçenekleri.",
    metaDescription:
      "Endometriozis nedir? Şiddetli adet ağrısı, ilişki ağrısı, kısırlık; tanı, hormonal ilaç ve laparoskopik cerrahi tedavisi.",
    date: "2026-08-25",
    updated: SITE_UPDATED,
    readingTime: "10 dk okuma",
    keyword: "endometriozis",
    enSlug: "endometriosis",
    tldr: [
      "Endometriozis, rahim iç zarına benzer dokunun rahim dışında yerleşmesiyle oluşan kronik bir hastalıktır.",
      "En sık belirtileri şiddetli adet ağrısı, cinsel ilişkide ağrı ve kısırlıktır; üreme çağındaki her 10 kadından 1'ini etkiler.",
      "Kesin tanı laparoskopi ile konur; tedavi ağrı yönetimi, hormonal ilaçlar ve laparoskopik cerrahiyi kapsar.",
    ],
    faqs: [
      {
        q: "Endometriozis nedir?",
        a: "Rahim iç zarına (endometriuma) benzer dokunun; yumurtalıklar, tuba, bağırsak veya karın zarı gibi rahim dışı bölgelerde yerleşmesiyle oluşan östrojen bağımlı, kronik bir hastalıktır.",
      },
      {
        q: "Endometriozis kısırlık yapar mı?",
        a: "Endometriozisli kadınların yaklaşık %30-50'sinde gebelik güçlüğü görülür. Yumurtalık rezervinde azalma, tüplerde yapışıklık ve pelvik inflamasyon üremeyi olumsuz etkiler; ancak tedaviyle gebelik büyük oranda mümkündür.",
      },
      {
        q: "Endometriozis kesin olarak nasıl teşhis edilir?",
        a: "Muayene ve ultrason güçlü şüphe uyandırır; endometrioma ('çikolata kisti') genellikle ultrasonda görülür. Ancak kesin tanı laparoskopi sırasında lezyonların görülmesi ve biyopsi ile konur.",
      },
      {
        q: "İlaçsız veya doğal yolla tedavi olur mu?",
        a: "Hafif olgularda ağrı kesiciler, sıcak uygulama ve yaşam tarzı değişiklikleri belirtileri azaltabilir. Ancak hastalığın ilerlemesini durdurmak için hormonal tedavi veya cerrahi çoğu olguda gereklidir.",
      },
    ],
  },
  miyom: {
    slug: "miyom",
    title: "Miyom (Uterin Fibroid): Belirtileri, Tanısı ve Tedavi Seçenekleri",
    metaTitle: "Miyom (Fibroid): Belirtileri, Tanı ve Tedavi",
    description:
      "Miyom nedir, neden olur? Yoğun adet kanaması, kasık ağrısı, kısırlık, tanı yöntemleri ve ilaç, histeroskopik/laparoskopik cerrahi tedavi seçenekleri.",
    metaDescription:
      "Miyom nedir? Yoğun kanama, kasık ağrısı, kısırlık; tanı, ilaç ve histeroskopik/laparoskopik cerrahi tedavisi.",
    date: "2026-09-01",
    updated: SITE_UPDATED,
    readingTime: "10 dk okuma",
    keyword: "miyom",
    enSlug: "uterine-fibroids",
    tldr: [
      "Miyomlar, rahmin kas tabakasından köken alan iyi huylu tümörlerdir; üreme çağındaki kadınların yaklaşık %70'inde görülür.",
      "En sık belirtileri yoğun ve uzun süren adet kanaması, kasık ağrısı, sık idrara çıkma ve bazen kısırlıktır; birçok miyom belirtisiz seyreder.",
      "Tedavi; miyomun boyutu, yerleşim yeri, belirtiler ve gebelik isteğine göre bireysel planlanır: takip, ilaç, histeroskopik veya laparoskopik miyomektomi seçenekleri vardır.",
    ],
    faqs: [
      {
        q: "Miyom nedir?",
        a: "Rahmin kas tabakasından (miyometrium) köken alan, östrojen ve progesterona duyarlı, iyi huylu (benign) düz kas tümörleridir. Nadiren (%0.1'den az) kötü huylu bir sürece dönüşür.",
      },
      {
        q: "Miyom kısırlık yapar mı?",
        a: "Rahim boşluğunu bozan submüköz (rahim içine bakan) miyomlar ve büyük intramural miyomlar kısırlığa neden olabilir. Uygun yerleşimli ve küçük miyomlar genellikle doğurganlığı etkilemez.",
      },
      {
        q: "Her miyom ameliyat gerektirir mi?",
        a: "Hayır. Belirtisiz, küçük miyomlar düzenli aralıklarla takip edilir. Belirti veren, hızla büyüyen, gebelik planlarını engelleyen veya şüpheli görünen miyomlarda cerrahi düşünülür.",
      },
      {
        q: "Miyomlar tekrar eder mi?",
        a: "Miyomektomi sonrası 5 yıl içinde yeni miyom oluşma olasılığı %15–30 civarındadır. Menopoz sonrası östrojen düşüşüyle mevcut miyomlar genellikle küçülür.",
      },
    ],
  },
  "hpv-asisi": {
    slug: "hpv-asisi",
    title: "HPV Aşısı: Kimlere, Ne Zaman ve Neden Yapılır? Güncel Rehber",
    description:
      "HPV nedir, hangi tip kanserlere yol açar? HPV aşısı çeşitleri (Gardasil 9), yaş aralıkları, doz şeması, güvenilirlik, yan etkiler ve erkeklerde aşılama.",
    metaTitle: "HPV Aşısı: Kimlere Yapılır? Yaş, Doz ve Güvenirlik",
    metaDescription:
      "HPV aşısı kimlere yapılır, doz şeması nedir? Gardasil 9, yaş sınırları, erkeklerde aşılama, güvenlik ve yan etkiler.",
    date: "2026-09-08",
    updated: SITE_UPDATED,
    readingTime: "10 dk okuma",
    keyword: "HPV aşısı",
    enSlug: "hpv-vaccine",
    tldr: [
      "HPV aşısı; rahim ağzı (serviks), vajina, vulva, anüs, penis ve orofarenks kanserleri ile genital siğillerin büyük çoğunluğunu önler.",
      "En etkili yaş 9–14'tür; bu yaş grubunda 2 doz yeterlidir. 15 yaşından sonra ve bağışıklığı baskılanan kişilerde 3 doz uygulanır.",
      "Aşı hem kızlara hem erkeklere önerilir; 45 yaşına kadar bireysel değerlendirmeyle uygulanabilir. Gebelikte önerilmez, ancak emzirme döneminde güvenlidir.",
    ],
    faqs: [
      {
        q: "HPV aşısı kimlere yapılmalı?",
        a: "İdeal olarak cinsel aktivite başlamadan önce, 9–14 yaş aralığındaki kız ve erkek çocuklara yapılır. 15–26 yaş grubuna rutin, 27–45 yaş grubuna bireysel değerlendirme ile önerilir.",
      },
      {
        q: "HPV aşısı kaç doz yapılır?",
        a: "9–14 yaş arasında 2 doz (0. ve 6–12. ay) yeterlidir. 15 yaş ve üzerinde veya bağışıklığı baskılanmış kişilerde 3 doz (0, 2 ve 6. ay) uygulanır.",
      },
      {
        q: "HPV aşısı erkeklere de yapılır mı?",
        a: "Evet. Erkeklerde de anüs, penis ve baş-boyun (orofarenks) kanserlerine ve genital siğillere karşı koruyucudur. Ayrıca partner koruması sağlar.",
      },
      {
        q: "HPV aşısı güvenli mi, yan etkileri nedir?",
        a: "Dünya Sağlık Örgütü ve FDA tarafından güvenli kabul edilir. En sık yan etki enjeksiyon yerinde ağrı, kızarıklık ve hafif ateştir. Ciddi yan etki oldukça nadirdir.",
      },
      {
        q: "HPV bulaşmış birine aşı fayda sağlar mı?",
        a: "Evet. Aşı 9 farklı HPV tipine karşı korur (Gardasil 9); bir tiple karşılaşmış olsanız bile diğer tiplere karşı koruma sağlar. Tedavi edici değil önleyicidir.",
      },
    ],
  },
  pcos: {
    slug: "pcos",
    title: "PCOS (Polikistik Over Sendromu): Belirtiler, Tanı ve Güncel Tedavi",

    metaTitle: "PCOS Nedir? Belirtiler, Tanı Kriterleri ve Tedavi",
    description:
      "Adet düzensizliği, tüylenme, akne ve kısırlık: PCOS'un Rotterdam kriterlerine göre tanısı, insülin direnci, PCOM ayrımı ve güncel tedavi seçenekleri.",
    metaDescription:
      "PCOS (Polikistik Over Sendromu): 2023 uluslararası kılavuz ışığında belirtiler, tanı, PCOM ayrımı, insülin direnci ve tedavi seçenekleri.",
    date: "2026-09-15",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "PCOS polikistik over sendromu",
    enSlug: "pcos",
    tldr: [
      "PCOS; adet düzensizliği, klinik/biyokimyasal hiperandrojenizm ve polikistik over görünümü (PCOM) kriterlerinden en az ikisiyle konur (Rotterdam kriterleri).",
      "PCOM (ultrasonda çok folliküllü over) yalnızca bir bulgudur; her PCOM'lu kadında PCOS yoktur. 2023 kılavuzu ultrason yerine AMH ölçümüne de izin verir.",
      "Tedavinin temeli yaşam tarzı düzenlemesi, kilo kontrolü ve insülin direncinin yönetimidir; ilaç tedavisi hedefe (gebelik, kanama düzeni, cilt bulguları) göre kişiselleştirilir.",
    ],
    faqs: [
      {
        q: "PCOS ile PCOM aynı şey midir?",
        a: "Hayır. PCOM (Polikistik Over Morfolojisi) yalnızca ultrasonda çok sayıda küçük follikül görülmesidir — bir bulgudur. PCOS ise adet düzensizliği ve hiperandrojenizm gibi klinik bulguların da eşlik ettiği bir sendromdur. Sağlıklı kadınların yaklaşık %20-30'unda PCOM bulunabilir ancak bu PCOS anlamına gelmez.",
      },
      {
        q: "PCOS tanısı nasıl konur?",
        a: "Rotterdam kriterlerine göre şu üç bulgudan en az ikisi olmalıdır: (1) ovulasyon bozukluğu / adet düzensizliği, (2) klinik veya biyokimyasal hiperandrojenizm (tüylenme, akne, yüksek testosteron), (3) ultrasonda PCOM veya yüksek AMH. Benzer tabloya yol açan tiroid, prolaktin ve adrenal hastalıkların dışlanması şarttır.",
      },
      {
        q: "PCOS'ta insülin direncinin rolü nedir?",
        a: "PCOS'lu kadınların yaklaşık %70'inde insülin direnci vardır ve bu, androjen üretimini artırarak tüylenme, akne ve kilo alımını körükler. İnsülin direncinin yönetimi (kilo verme, egzersiz, gerektiğinde metformin) sendromun temel tedavisidir.",
      },
      {
        q: "PCOS'lu bir kadın gebe kalabilir mi?",
        a: "Evet. PCOS kısırlığın en sık nedenlerinden biridir ancak tedavi edilebilir. %5-10 kilo kaybı bile ovulasyonu başlatabilir. İhtiyaç halinde letrozol veya klomifen sitrat ile ovulasyon indüksiyonu yapılır; yanıt alınmayan olgularda gonadotropinler veya tüp bebek uygulanır.",
      },
      {
        q: "PCOS uzun vadede hangi sağlık sorunlarına yol açar?",
        a: "Tedavi edilmezse tip 2 diyabet, kardiyovasküler hastalık, uyku apnesi, karaciğer yağlanması, depresyon ve endometrium (rahim iç zarı) kanseri riski artar. Bu nedenle PCOS sadece bir üreme sağlığı sorunu değil, ömür boyu takip gerektiren metabolik bir durumdur.",
      },
    ],
  },
  histeroskopi: {
    slug: "histeroskopi",
    enSlug: "hysteroscopy",
    title: "Histeroskopi Nedir? Nasıl Yapılır, Kimlere Uygulanır?",
    metaTitle: "Histeroskopi Nedir? Nasıl Yapılır, Kimlere Uygulanır?",
    description:
      "Histeroskopi; rahim iç boşluğunun ince bir kamera ile incelenmesidir. Endikasyonları, tanısal ve operatif histeroskopi arasındaki farklar, işlem süreci, iyileşme ve riskler.",
    metaDescription:
      "Histeroskopi nedir, nasıl yapılır? Tanısal ve operatif histeroskopi, polip ve miyom rezeksiyonu, endikasyonlar, iyileşme süreci ve olası riskler.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "10 dk okuma",
    keyword: "histeroskopi",
    tldr: [
      "Histeroskopi, rahim iç boşluğunun (kavitesinin) ince ışıklı bir kamera ile doğrudan görüntülenmesidir; hem tanı hem tedavi amaçlı yapılabilir.",
      "Anormal kanama, tekrarlayan gebelik kayıpları, kısırlık araştırması, endometrial polip, submukoz miyom, uterin septum ve rahim içi yapışıklık (Asherman) en sık endikasyonlardır.",
      "Ofis (tanısal) histeroskopi genellikle anestezisiz veya hafif sedasyonla 5–10 dakikada tamamlanır; operatif histeroskopi ameliyathane koşullarında genel/spinal anestezi ile yapılır.",
    ],
    faqs: [
      {
        q: "Histeroskopi ağrılı mı?",
        a: "Tanısal (ofis) histeroskopi genellikle iyi tolere edilir; kısa süreli, adet krampına benzer bir sıkışma hissi olabilir. Operatif histeroskopi anestezi altında yapıldığı için işlem sırasında ağrı hissedilmez.",
      },
      {
        q: "İşlem ne kadar sürer?",
        a: "Tanısal histeroskopi 5–15 dakika sürer. Operatif histeroskopi, giderilecek patolojinin türüne göre 20–60 dakika arasında değişir.",
      },
      {
        q: "Histeroskopiden sonra ne zaman işe/normal yaşama dönebilirim?",
        a: "Tanısal histeroskopi sonrası aynı gün, operatif histeroskopi sonrası genellikle 1–2 gün içinde günlük yaşama dönülebilir. Ağır egzersiz, cinsel ilişki ve tampon/kaplıca-havuz kullanımı için 1–2 hafta beklenmesi önerilir.",
      },
      {
        q: "Histeroskopi gebeliğe engel olur mu, gebeliği geciktirir mi?",
        a: "Aksine, birçok olguda gebelik şansını artırır. Endometrial polip, submukoz miyom, uterin septum veya rahim içi yapışıklıkların histeroskopik olarak çıkarılması gebelik oranlarını iyileştirir. İşlemden 1–2 adet sonrası gebelik denemelerine başlanabilir.",
      },
      {
        q: "Histeroskopi ile küretaj aynı şey mi?",
        a: "Hayır. Küretaj (D&C) rahim içinin körlemesine kazınmasıdır ve odak halindeki bir lezyonu (polip, miyom) atlayabilir. Histeroskopide ise rahim içi doğrudan görülür ve patoloji hedefli olarak çıkarılır; bu yüzden histeroskopi hem daha doğru hem daha az travmatiktir.",
      },
      {
        q: "Histeroskopi hangi durumlarda yapılamaz?",
        a: "Aktif pelvik enfeksiyon, aktif ağır kanama, gebelik ve bilinen rahim ağzı kanseri histeroskopinin kesin kontrendikasyonlarıdır. Bu durumlarda önce sorun tedavi edilir, ardından gerekiyorsa işlem yapılır.",
      },
    ],
  },
  menopoz: {
    slug: "menopoz",
    enSlug: "menopause",
    title: "Menopoz ve Hormon Tedavisi: Belirtiler, Tanı ve Tedavi Seçenekleri",
    metaTitle: "Menopoz Belirtileri ve Hormon Tedavisi (HRT) Rehberi",
    description:
      "Menopoz nedir, kaç yaşında başlar? Ateş basması, uyku bozukluğu, kemik erimesi ve hormon replasman tedavisi (HRT) kimlere uygundur, riskleri nelerdir?",
    metaDescription:
      "Menopoz belirtileri, tanısı ve hormon replasman tedavisi (HRT): kimlere uygun, hangi riskleri taşır, hormonsuz alternatifler nelerdir?",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "menopoz",
    tldr: [
      "Menopoz, adetin 12 ay boyunca hiç görülmemesiyle geriye dönük olarak konan bir tanıdır; Türkiye'de ortalama yaş 48–51'dir.",
      "Ateş basması, gece terlemesi, uyku bozukluğu, vajinal kuruluk ve ruh hâli değişiklikleri en sık şikâyetlerdir; kemik kaybı ise sessiz ilerler.",
      "Hormon replasman tedavisi (HRT) 60 yaş altında ve menopozun ilk 10 yılında başlandığında, uygun hastada fayda/risk dengesi olumludur.",
    ],
    faqs: [
      {
        q: "Menopoz kaç yaşında başlar?",
        a: "Doğal menopoz çoğunlukla 45–55 yaş arasında, ortalama 48–51 yaşında görülür. 40 yaşından önce adetin kesilmesi 'prematüre ovaryan yetmezlik' olarak adlandırılır ve mutlaka araştırılmalıdır.",
      },
      {
        q: "Menopoz tanısı için hormon testi şart mı?",
        a: "45 yaş üstünde tipik şikâyetleri olan bir kadında tanı kliniktir; rutin hormon testi gerekmez. FSH ve estradiol ölçümü daha çok 45 yaş altı, rahmi alınmış ya da hormonal doğum kontrolü kullanan kadınlarda yardımcı olur.",
      },
      {
        q: "Hormon replasman tedavisi (HRT) meme kanseri yapar mı?",
        a: "Yalnız östrojen içeren tedavide (rahmi alınmış kadınlarda) meme kanseri riskinde anlamlı artış gösterilmemiştir. Östrojen+progesteron kombinasyonunda risk, 5 yıldan uzun kullanımda küçük ama gerçek bir artış gösterir. Bu nedenle tedavi kişiye özel planlanır ve yılda bir yeniden değerlendirilir.",
      },
      {
        q: "HRT kimlere verilemez?",
        a: "Aktif ya da geçirilmiş meme kanseri, östrojene duyarlı kanserler, açıklanamayan vajinal kanama, aktif karaciğer hastalığı, geçirilmiş derin ven trombozu/pulmoner emboli, inme ve kalp krizi öyküsü başlıca kontrendikasyonlardır.",
      },
      {
        q: "Hormon kullanmak istemiyorum; alternatifim var mı?",
        a: "Evet. Ateş basmasında SSRI/SNRI grubu ilaçlar, gabapentin ve yeni geliştirilen nonhormonal NK3 reseptör antagonistleri etkilidir. Vajinal kurulukta nemlendirici ve kayganlaştırıcılar ile çok düşük doz lokal östrojen kullanılabilir. Egzersiz, kilo kontrolü, sigaranın bırakılması ve D vitamini/kalsiyum desteği her hastada önerilir.",
      },
      {
        q: "Menopozda kemik erimesi nasıl takip edilir?",
        a: "65 yaş üstü tüm kadınlarda, risk faktörü taşıyan daha genç kadınlarda ise daha erken DEXA (kemik yoğunluğu) ölçümü önerilir. Kalsiyum, D vitamini, yürüyüş ve direnç egzersizleri temel korumadır.",
      },
    ],
  },
  "kisirlik-infertilite": {
    slug: "kisirlik-infertilite",
    enSlug: "infertility",
    title: "Kısırlık (İnfertilite): Nedenleri, Tetkikleri ve Tedavi Seçenekleri",
    metaTitle: "Kısırlık (İnfertilite): Nedenleri, Testleri ve Tedavisi",
    description:
      "Bir yıl denemeye rağmen gebelik olmuyorsa ne yapmalı? Kadın ve erkek kaynaklı infertilite nedenleri, hangi tetkikler yapılır, aşılama ve tüp bebek ne zaman gerekir?",
    metaDescription:
      "İnfertilite nedenleri, kadın ve erkekte yapılan tetkikler, yumurtlama tedavisi, aşılama (IUI) ve tüp bebek (IVF) seçenekleri.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "kısırlık infertilite",
    tldr: [
      "35 yaş altında 12 ay, 35 yaş ve üzerinde 6 ay korunmasız ilişkiye rağmen gebelik olmuyorsa değerlendirme başlatılmalıdır.",
      "Nedenlerin yaklaşık üçte biri kadına, üçte biri erkeğe aittir; kalan kısım karma veya açıklanamayan infertilitedir.",
      "İlk basamak tetkikler basittir: sperm analizi, yumurtlamanın gösterilmesi ve tüplerin açıklığının değerlendirilmesi.",
    ],
    faqs: [
      {
        q: "Ne kadar süre sonra kısırlık araştırması yapılmalı?",
        a: "35 yaşın altındaki çiftlerde düzenli ve korunmasız ilişkiye rağmen 12 ay, 35 yaş ve üzerinde 6 ay gebelik olmaması durumunda araştırma başlatılır. Adet düzensizliği, geçirilmiş pelvik enfeksiyon, endometriozis veya bilinen erkek faktörü varsa beklenmeden değerlendirme yapılır.",
      },
      {
        q: "Kısırlığın en sık nedenleri nelerdir?",
        a: "Kadında yumurtlama bozuklukları (en sık PCOS), tüp tıkanıklığı, endometriozis, miyom ve rahim içi yapışıklıklar; erkekte sperm sayısı, hareketi veya yapısındaki bozukluklar ve varikosel öne çıkar. Çiftlerin yaklaşık %10–15'inde tüm testler normaldir; buna açıklanamayan infertilite denir.",
      },
      {
        q: "Hangi testler yapılır?",
        a: "Kadında adetin 2–4. günü hormon profili, AMH ile yumurtalık rezervi, ultrasonografi ve tüplerin açıklığı için ilaçlı rahim filmi (HSG) veya HyCoSy; erkekte 2–5 günlük cinsel perhiz sonrası sperm analizi ilk basamaktır. Gerekirse histeroskopi veya laparoskopi eklenir.",
      },
      {
        q: "Tüp bebek her çift için gerekli mi?",
        a: "Hayır. Yumurtlama sorunu olan birçok kadında ilaçla yumurtlama tedavisi tek başına yeterlidir. Hafif erkek faktörü ve açıklanamayan infertilitede aşılama (IUI) denenir. Tüp bebek (IVF); iki taraflı tüp tıkanıklığı, ağır erkek faktörü, ileri yaş veya önceki tedavilerin başarısız olduğu durumlarda gündeme gelir.",
      },
      {
        q: "Yaşın gebelik şansına etkisi nedir?",
        a: "Yumurta sayısı ve kalitesi 32 yaşından sonra yavaş, 37 yaşından sonra belirgin biçimde azalır. Bu nedenle 35 yaş üstünde değerlendirmenin ve tedavinin geciktirilmemesi, gebelik şansını doğrudan etkiler.",
      },
      {
        q: "Yaşam tarzı gebelik şansını değiştirir mi?",
        a: "Evet. Sigaranın bırakılması, vücut kitle indeksinin 19–29 aralığına getirilmesi, alkolün azaltılması, folik asit kullanımı ve düzenli uyku hem doğal gebelik hem de tedavi başarısını artırır. Erkekte aşırı sıcak ortam ve sigara sperm kalitesini olumsuz etkiler.",
      },
    ],
  },
  "gebelikte-tarama-testleri": {
    slug: "gebelikte-tarama-testleri",
    enSlug: "prenatal-screening-tests",
    title: "Gebelikte Tarama Testleri: İkili, Üçlü, Dörtlü Test ve NIPT Rehberi",
    metaTitle: "Gebelikte Tarama Testleri: İkili Test, NIPT ve Detaylı Ultrason",
    description:
      "İkili test, üçlü ve dörtlü test, NIPT (anne kanından DNA testi), ense kalınlığı ölçümü ve detaylı ultrason ne zaman yapılır? Risk çıkarsa ne anlama gelir, kesin tanı testleri nelerdir?",
    metaDescription:
      "Gebelikte tarama testleri takvimi: ikili test, NIPT, dörtlü test, ense kalınlığı, detaylı ultrason ve şeker yükleme testi. Yüksek risk ne demek?",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "gebelikte tarama testleri",
    tldr: [
      "Tarama testleri tanı koymaz; yalnızca bir olasılık (risk) hesaplar. Yüksek risk, bebeğin hasta olduğu anlamına gelmez.",
      "İkili test + ense kalınlığı 11–14. hafta, dörtlü test 16–18. hafta, NIPT 10. haftadan itibaren, detaylı ultrason ise 20–22. hafta arasında yapılır.",
      "Kesin tanı yalnızca koryon villus biyopsisi (CVS) veya amniyosentez ile konur; bu testler yüksek riskli sonuçlarda önerilir.",
    ],
    faqs: [
      {
        q: "Gebelikte tarama testleri zorunlu mu?",
        a: "Hayır, tarama testleri isteğe bağlıdır. Ancak Down sendromu başta olmak üzere kromozom anomalilerinin ve bazı yapısal sorunların erken saptanabilmesi için tüm gebelere önerilir. Karar, bilgilendirme sonrası anne adayına aittir.",
      },
      {
        q: "İkili test ne zaman yapılır, neyi ölçer?",
        a: "11 hafta 0 gün ile 13 hafta 6 gün arasında yapılır. Anne kanında PAPP-A ve serbest beta-hCG ölçülür; ultrasonda ense kalınlığı (NT) ve baş-popo mesafesi (CRL) değerlendirilir. Anne yaşı ile birleştirilerek Down sendromu (trizomi 21), trizomi 18 ve 13 riski hesaplanır. Yakalama oranı yaklaşık %90'dır.",
      },
      {
        q: "NIPT (anne kanından DNA testi) nedir, kimlere önerilir?",
        a: "NIPT, anne kanındaki plasenta kaynaklı serbest fetal DNA'yı inceler ve 10. haftadan itibaren yapılabilir. Trizomi 21'i yakalama oranı %99'un üzerindedir ve yanlış pozitiflik oranı çok düşüktür. İleri anne yaşı, ikili testte yüksek/ara risk, önceki gebelikte kromozom anomalisi öyküsü olanlara özellikle önerilir; isteyen her gebeye uygulanabilir. NIPT bir tarama testidir, kesin tanı testi değildir.",
      },
      {
        q: "Test sonucum 'yüksek risk' çıktı, bebeğim hasta mı?",
        a: "Hayır. Yüksek risk, yalnızca olasılığın eşik değerin üzerinde olduğunu gösterir. Örneğin 1/100 risk, bebeğin %99 ihtimalle etkilenmediği anlamına gelir. Bu durumda ileri değerlendirme, NIPT veya kesin tanı için CVS/amniyosentez konuşulur.",
      },
      {
        q: "Amniyosentez riskli mi?",
        a: "Deneyimli ellerde amniyosenteze bağlı gebelik kaybı riski günümüzde yaklaşık binde 1–3 olarak bildirilmektedir. İşlem 16–20. haftalar arasında, ultrason eşliğinde yapılır ve kromozom analizi için kesin sonuç verir.",
      },
      {
        q: "Detaylı ultrason (ikinci düzey USG) ne zaman yapılır?",
        a: "20–22. gebelik haftaları arasında yapılır. Bebeğin beyin, kalp, omurga, böbrek, mide, eller ve ayakları dahil tüm organ sistemleri, plasenta ve amniyon sıvısı ayrıntılı olarak değerlendirilir. Kromozom testlerinin yerini tutmaz; onları tamamlar.",
      },
      {
        q: "Şeker yükleme testi (OGTT) gerekli mi?",
        a: "24–28. haftalar arasında gestasyonel diyabet taraması tüm gebelere önerilir. Riskli gebelerde (obezite, önceki gebelikte şeker öyküsü, ailede diyabet) ilk trimesterda da bakılabilir.",
      },
    ],
  },
  "dogum-kontrol-yontemleri": {
    slug: "dogum-kontrol-yontemleri",
    enSlug: "birth-control-methods",
    title: "Doğum Kontrol Yöntemleri: Hangi Yöntem Kime Uygun?",
    metaTitle: "Doğum Kontrol Yöntemleri: Hap, Spiral, İğne ve Kalıcı Yöntemler",
    description:
      "Doğum kontrol hapı, spiral (RİA), aylık/üç aylık iğne, implant, prezervatif ve kalıcı yöntemler: etkinlik oranları, yan etkiler, kimlere uygun olduğu ve doğurganlığa dönüş süresi.",
    metaDescription:
      "Doğum kontrol yöntemleri karşılaştırması: hap, hormonlu ve bakırlı spiral, implant, iğne, prezervatif. Gebe kalma oranları, yan etkiler ve seçim rehberi.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "doğum kontrol yöntemleri",
    tldr: [
      "En etkili yöntemler kullanıcı hatasına açık olmayanlardır: spiral (RİA), kol altı implant ve kalıcı yöntemler; yıllık başarısızlık oranları %1'in altındadır.",
      "Doğum kontrol hapı doğru kullanıldığında çok etkilidir; ancak unutulan haplar nedeniyle gerçek kullanımda gebelik oranı yaklaşık %7'dir.",
      "Hiçbir hormonal yöntem cinsel yolla bulaşan enfeksiyonlardan korumaz; koruma için prezervatif gereklidir.",
      "Spiral ve implant çıkarıldıktan sonra doğurganlık hızla geri döner; yalnızca üç aylık iğnede dönüş birkaç ay gecikebilir.",
    ],
    faqs: [
      {
        q: "En etkili doğum kontrol yöntemi hangisidir?",
        a: "Kullanıcıya bağımlı olmayan uzun etkili yöntemler en etkilidir: hormonlu spiral, bakırlı spiral ve kol altı implantın yıllık gebelik oranı %1'in altındadır. Doğum kontrol hapı ideal kullanımda %99'un üzerinde koruma sağlar; ancak unutulan haplar nedeniyle gerçek yaşamda etkinlik %93 civarına iner.",
      },
      {
        q: "Spiral (RİA) taktırmak ağrılı mıdır, kimlere uygundur?",
        a: "Spiral takılması birkaç dakika süren poliklinik işlemidir; adet sancısına benzer kramp hissedilebilir. Doğum yapmış ya da yapmamış her yaştaki kadına uygulanabilir. Bakırlı spiral 5–10 yıl, hormonlu spiral 5–8 yıl korur; hormonlu spiral ayrıca adet kanamasını belirgin biçimde azaltır.",
      },
      {
        q: "Doğum kontrol hapı kilo aldırır mı, kısırlık yapar mı?",
        a: "Modern düşük dozlu haplarla kalıcı kilo artışı gösterilmemiştir; ilk aylarda ödeme bağlı geçici değişiklik olabilir. Hap kısırlık yapmaz; bırakıldıktan sonra yumurtlama genellikle ilk 1–3 ay içinde normale döner.",
      },
      {
        q: "Emzirirken hangi yöntem kullanılabilir?",
        a: "Emzirme döneminde östrojen içermeyen yöntemler tercih edilir: yalnız progesteron içeren hap, kol altı implant, üç aylık iğne ve spiral güvenle kullanılabilir. Spiral genellikle doğumdan 4–6 hafta sonra takılır.",
      },
      {
        q: "Ertesi gün hapı ne kadar sürede etkilidir?",
        a: "Korunmasız ilişkiden sonra ne kadar erken alınırsa o kadar etkilidir; levonorgestrel içeren hap ilk 72 saatte, ulipristal 120 saate kadar kullanılabilir. Bakırlı spiralin 5 gün içinde takılması ise en etkili acil korunma yöntemidir. Ertesi gün hapı düzenli bir yöntem değildir.",
      },
      {
        q: "Hangi durumlarda hormonlu yöntem kullanılmamalı?",
        a: "Aurası olan migren, kontrolsüz yüksek tansiyon, geçirilmiş damar tıkanıklığı (tromboz), aktif meme kanseri, ağır karaciğer hastalığı ve 35 yaş üzeri sigara kullanımı östrojen içeren yöntemler için engeldir. Bu durumlarda progesteron içeren veya hormonsuz yöntemler önerilir.",
      },
    ],
  },
  "normal-dogum-mu-sezaryen-mi": {
    slug: "normal-dogum-mu-sezaryen-mi",
    enSlug: "vaginal-birth-or-c-section",
    title: "Normal Doğum mu, Sezaryen mi? Karar Verirken Bilinmesi Gerekenler",
    metaTitle: "Normal Doğum mu Sezaryen mi? Riskler, Avantajlar ve Karar Rehberi",
    description:
      "Normal doğum ve sezaryenin avantajları, riskleri, iyileşme süreleri; sezaryenin tıbbi zorunlu olduğu durumlar, sezaryen sonrası normal doğum (VBAC) ve doğum planı hazırlama.",
    metaDescription:
      "Normal doğum mu sezaryen mi? Tıbbi endikasyonlar, iyileşme süresi, riskler, epidural ve sezaryen sonrası normal doğum (VBAC) hakkında uzman rehberi.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "normal doğum mu sezaryen mi",
    tldr: [
      "Tıbbi bir engel yoksa vajinal doğum, anne için daha kısa iyileşme ve daha az cerrahi risk anlamına gelir; bebekte solunum uyumu da genellikle daha iyidir.",
      "Sezaryen bazı durumlarda tartışmasız hayat kurtarır: plasenta previa, makat/duruş bozuklukları, fetal distres, ilerlemeyen doğum eylemi ve önceki klasik kesi.",
      "Her sezaryen bir sonraki gebeliğin riskini artırır (yapışıklık, plasenta yerleşim bozuklukları); bu nedenle çok sayıda doğum planlayanlarda karar daha önemlidir.",
      "Tek bir alt segment sezaryen geçirmiş uygun adaylarda sezaryen sonrası normal doğum (VBAC) %60–80 oranında başarılıdır.",
    ],
    faqs: [
      {
        q: "Sezaryen hangi durumlarda tıbbi olarak zorunludur?",
        a: "Plasenta previa (plasentanın doğum kanalını kapatması), plasenta dekolmanı, kordon sarkması, enine duruş, bazı makat gelişler, önceki klasik (dikey) uterus kesisi, ilerlemeyen doğum eylemi, baş-pelvis uyumsuzluğu ve fetal distres başlıca zorunlu nedenlerdir. Aktif genital herpes ve tedavisiz HIV gibi bazı enfeksiyonlarda da sezaryen tercih edilir.",
      },
      {
        q: "Normal doğumun avantajları nelerdir?",
        a: "Hastanede kalış 1–2 gün, tam iyileşme genellikle 1–2 haftadır. Enfeksiyon, kanama ve tromboz riski daha düşüktür; emzirmeye başlama daha kolaydır. Doğum kanalından geçiş bebeğin akciğer sıvısının atılmasına ve sağlıklı bağırsak florasının oluşmasına katkı sağlar.",
      },
      {
        q: "Sezaryen sonrası iyileşme ne kadar sürer?",
        a: "Hastanede kalış genellikle 2–3 gün, yara iyileşmesi 2–4 haftadır. Ağır kaldırma ve araç kullanımı için 4–6 hafta beklenmesi önerilir. Erken ayağa kalkmak, tromboz riskini azalttığı için ilk 24 saat içinde teşvik edilir.",
      },
      {
        q: "Sezaryen sonrası normal doğum (VBAC) mümkün mü?",
        a: "Evet. Tek bir alt segment (yatay) kesi ile sezaryen olmuş, bebeği baş gelişte ve gebeliğinde ek risk bulunmayan kadınlarda VBAC denenebilir; başarı oranı %60–80'dir. Uterus rüptürü riski yaklaşık binde 5'tir ve bu nedenle acil sezaryen olanağı olan bir merkezde doğum yapılmalıdır.",
      },
      {
        q: "Epidural anestezi doğumu uzatır mı, bebeğe zarar verir mi?",
        a: "Epidural, ağrıyı güvenli biçimde azaltan en etkili yöntemdir. Doğumun ikinci evresini bir miktar uzatabilir ancak sezaryen oranını artırmadığı gösterilmiştir. Bebeğe geçen ilaç miktarı çok düşüktür ve zararlı etki bildirilmemiştir.",
      },
      {
        q: "İsteğe bağlı sezaryen doğru bir tercih mi?",
        a: "Tıbbi bir gereklilik yoksa, sezaryen bir ameliyattır ve kanama, enfeksiyon, mesane-bağırsak yaralanması, tromboz ile sonraki gebeliklerde plasenta yerleşim bozukluğu riski taşır. Karar, korkular ve beklentiler konuşulduktan sonra hekimle birlikte verilmelidir; doğum korkusu için danışmanlık çoğu zaman etkilidir.",
      },
    ],
  },
  "gestasyonel-diyabet": {
    slug: "gestasyonel-diyabet",
    enSlug: "gestational-diabetes",
    title: "Gestasyonel Diyabet (Gebelik Şekeri): Tarama, Beslenme ve Tedavi",
    metaTitle: "Gebelik Şekeri (Gestasyonel Diyabet): Şeker Yükleme Testi ve Tedavi",
    description:
      "Gebelik şekeri nedir, kimlerde görülür? Şeker yükleme testi (OGTT) değerleri, hedef kan şekeri aralıkları, beslenme planı, insülin tedavisi ve doğum sonrası izlem.",
    metaDescription:
      "Gestasyonel diyabet rehberi: 24-28. hafta şeker yükleme testi, tanı değerleri, beslenme, egzersiz, insülin ve doğum sonrası diyabet riski.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "gestasyonel diyabet",
    tldr: [
      "Gebelik şekeri, gebeliğe bağlı insülin direnci nedeniyle ortaya çıkar ve gebeliklerin yaklaşık %7–14'ünde görülür.",
      "Tarama tüm gebelere 24–28. haftalar arasında şeker yükleme testi (OGTT) ile yapılır; riskli gebelerde ilk trimesterda da bakılır.",
      "Kadınların yaklaşık %70–85'i yalnızca beslenme düzenlemesi ve egzersizle hedef değerlere ulaşır; gerekirse insülin eklenir.",
      "Doğumdan 6–12 hafta sonra tekrar test gerekir: gestasyonel diyabet geçiren kadınlarda ilerleyen yıllarda tip 2 diyabet riski belirgin biçimde artar.",
    ],
    faqs: [
      {
        q: "Şeker yükleme testi ne zaman ve nasıl yapılır?",
        a: "Tarama 24–28. gebelik haftaları arasında yapılır. Tek aşamalı yöntemde 8 saatlik açlık sonrası açlık kan şekeri alınır, 75 gram glukoz içirilir ve 1. ile 2. saatte kan şekeri ölçülür. İki aşamalı yöntemde ise önce açlık gerektirmeyen 50 gram tarama testi yapılır, yüksek çıkarsa 100 gram doğrulama testi uygulanır.",
      },
      {
        q: "Tanı için hangi değerler kullanılır?",
        a: "75 gram OGTT'de açlık ≥92 mg/dL, 1. saat ≥180 mg/dL veya 2. saat ≥153 mg/dL değerlerinden yalnızca birinin aşılması tanı için yeterlidir. 50 gram tarama testinde 1. saat değerinin 140 mg/dL'nin üzerinde olması, doğrulama testi gerektirir.",
      },
      {
        q: "Gebelik şekeri bebeğe zarar verir mi?",
        a: "Kontrol altına alınmadığında bebeğin aşırı büyümesi (makrozomi), doğum travması, erken doğum, doğum sonrası bebekte düşük kan şekeri ve sarılık riskleri artar. Annede ise preeklampsi ve sezaryen olasılığı yükselir. Kan şekeri hedeflerde tutulduğunda bu risklerin çoğu normal düzeye iner.",
      },
      {
        q: "Hedef kan şekeri değerleri nedir?",
        a: "Genel olarak açlıkta 95 mg/dL'nin altı, yemekten 1 saat sonra 140 mg/dL'nin altı, 2 saat sonra ise 120 mg/dL'nin altı hedeflenir. Ölçümler günde 4 kez (açlık ve üç ana öğün sonrası) yapılır.",
      },
      {
        q: "Nasıl beslenmeliyim?",
        a: "Günlük karbonhidrat üç ana ve 2–3 ara öğüne bölünmelidir. Şekerli içecekler, beyaz un ve hazır tatlılar kesilir; tam tahıl, baklagil, sebze ve yeterli protein tercih edilir. Karbonhidratı protein veya sağlıklı yağla birlikte tüketmek şeker yükselmesini yavaşlatır. Her öğünden sonra 15–20 dakikalık yürüyüş tokluk şekerini belirgin biçimde düşürür.",
      },
      {
        q: "İnsülin gerekli olursa bebeğe zararlı mı?",
        a: "Hayır. İnsülin plasentayı geçmez ve gebelikte en güvenli tedavidir. Beslenme ve egzersize rağmen 1–2 hafta içinde hedeflere ulaşılamazsa insülin başlanır; genellikle doğumdan hemen sonra kesilir.",
      },
      {
        q: "Doğumdan sonra şekerim düzelir mi?",
        a: "Çoğu kadında kan şekeri doğumdan sonra normale döner. Ancak 6–12 hafta sonra 75 gram OGTT ile kontrol gerekir ve sonrasında 1–3 yılda bir tarama önerilir; emzirme, kilo kontrolü ve düzenli egzersiz tip 2 diyabet riskini azaltır.",
      },
    ],
  },
  "laparoskopi-nedir": {
    slug: "laparoskopi-nedir",
    enSlug: "gynaecological-laparoscopy",
    title: "Jinekolojik Laparoskopi (Kapalı Ameliyat) Nedir? Hangi Hastalıklarda Uygulanır?",
    metaTitle: "Laparoskopi (Kapalı Ameliyat) Nedir? Jinekolojik Laparoskopi Rehberi",
    description:
      "Laparoskopi nasıl yapılır, hangi jinekolojik hastalıklarda uygulanır? Ameliyat öncesi hazırlık, işlem süresi, riskleri, iyileşme süreci ve açık ameliyata göre avantajları.",
    metaDescription:
      "Jinekolojik laparoskopi rehberi: kapalı ameliyat nasıl yapılır, hangi hastalıklarda uygulanır, riskleri nelerdir ve iyileşme ne kadar sürer?",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 dk okuma",
    keyword: "laparoskopi nedir",
    tldr: [
      "Laparoskopi, göbek deliği çevresinden yerleştirilen kamera ve 5–10 mm'lik birkaç küçük kesiyle yapılan kapalı ameliyat yöntemidir.",
      "Yumurtalık kisti, endometriozis, miyom, dış gebelik, kısırlık araştırması ve rahim alınması gibi pek çok jinekolojik durumda uygulanabilir.",
      "Açık ameliyata göre daha az ağrı, daha az kanama, daha kısa hastanede kalış ve belirgin biçimde hızlı işe dönüş sağlar.",
      "Ciddi komplikasyon oranı %1'in altındadır; en önemli riskler damar, bağırsak ve mesane yaralanmasıdır ve deneyimli ellerde nadirdir.",
    ],
    faqs: [
      {
        q: "Laparoskopi (kapalı ameliyat) nedir?",
        a: "Laparoskopi, karın duvarına açılan 5–10 mm'lik birkaç küçük kesiden kamera ve ince aletler yerleştirilerek yapılan bir ameliyat yöntemidir. Karın boşluğu karbondioksit gazıyla şişirilir, organlar yüksek çözünürlüklü kamerayla büyütülerek görüntülenir ve ameliyat monitör eşliğinde yapılır.",
      },
      {
        q: "Hangi hastalıklarda laparoskopi uygulanır?",
        a: "Yumurtalık kistleri, endometriozis ve çikolata kisti, miyomlar, dış gebelik, yumurtalık torsiyonu, açıklanamayan kısırlık ve pelvik ağrı araştırması, tüplerin açıklığının değerlendirilmesi, rahim alınması (histerektomi) ve seçilmiş jinekolojik kanser ameliyatları laparoskopiyle yapılabilir.",
      },
      {
        q: "Laparoskopi ameliyatı ne kadar sürer?",
        a: "Süre yapılan işleme göre değişir. Tanısal laparoskopi 20–40 dakika, yumurtalık kisti çıkarılması 45–60 dakika, miyom veya ileri endometriozis cerrahisi ile laparoskopik histerektomi 1,5–3 saat sürebilir.",
      },
      {
        q: "İyileşme süreci ne kadardır?",
        a: "Hastaların çoğu aynı gün veya ertesi gün taburcu olur. İlk 2–3 gün kesi yerlerinde ağrı ve gaza bağlı omuz ağrısı olabilir; basit ağrı kesicilerle geçer. Masa başı işe dönüş genellikle 5–10 gün, ağır işlere ve spora dönüş 3–4 haftadır. Büyük ameliyatlarda bu süre 4–6 haftaya uzayabilir.",
      },
      {
        q: "Laparoskopinin riskleri nelerdir?",
        a: "Ciddi komplikasyon oranı %1'in altındadır. Nadir de olsa damar, bağırsak, mesane veya üreter yaralanması, kanama, enfeksiyon, fıtık ve anesteziye bağlı sorunlar görülebilir. Yapışıklık, çok yüksek vücut kitle indeksi veya geçirilmiş çoklu ameliyat varlığında işlem açık ameliyata çevrilebilir.",
      },
      {
        q: "Ameliyattan sonra iz kalır mı?",
        a: "Kesiler 5–10 mm olduğu için izler oldukça küçüktür; göbek içindeki kesi zamanla neredeyse görünmez hale gelir. Diğer izler ilk aylarda pembemsi kalır, bir yıl içinde belirgin biçimde soluklaşır.",
      },
      {
        q: "Laparoskopi doğurganlığı etkiler mi?",
        a: "Doğru endikasyonla yapıldığında laparoskopi doğurganlığı çoğu zaman korur, hatta artırır. Endometriozis odaklarının temizlenmesi, yapışıklıkların açılması ve kistin sağlıklı yumurtalık dokusu korunarak çıkarılması gebelik şansını iyileştirebilir. Yumurtalık dokusunun aşırı çıkarılması ise rezervi azaltabileceğinden cerrahi deneyim önemlidir.",
      },
    ],
  },
  "laparoskopik-histerektomi": {
    slug: "laparoskopik-histerektomi",
    enSlug: "laparoscopic-hysterectomy",
    title: "Laparoskopik Histerektomi (Kapalı Rahim Alınması): Süreç ve İyileşme",
    metaTitle: "Laparoskopik Histerektomi: Kapalı Rahim Alma Ameliyatı",
    description:
      "Kapalı yöntemle rahim alınması hangi durumlarda gerekir? Ameliyat teknikleri, yumurtalıkların korunması, süresi, riskleri, iyileşme süreci ve ameliyat sonrası yaşam.",
    metaDescription:
      "Laparoskopik histerektomi rehberi: kimlere yapılır, yumurtalıklar alınır mı, ameliyat ne kadar sürer, iyileşme ve cinsel yaşam nasıl etkilenir?",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "12 dk okuma",
    keyword: "laparoskopik histerektomi",
    tldr: [
      "Laparoskopik histerektomi, rahmin küçük kesilerden kamera eşliğinde alınmasıdır; açık ameliyata göre daha az ağrı ve daha hızlı iyileşme sağlar.",
      "En sık nedenler: tedaviye yanıt vermeyen miyomlar, aşırı adet kanaması, adenomyozis, ileri endometriozis, rahim sarkması ve bazı kanser öncesi lezyonlardır.",
      "Rahmin alınması yumurtalıkların alınması anlamına gelmez; menopoz öncesi kadınlarda yumurtalıklar mümkün olduğunca korunur.",
      "Hastaneden çıkış genellikle 1–2 gün, tam iyileşme 4–6 haftadır; ameliyat sonrası adet kanaması sona erer, gebelik mümkün olmaz.",
    ],
    faqs: [
      {
        q: "Laparoskopik histerektomi nedir?",
        a: "Rahmin (uterusun) karın duvarındaki 5–10 mm'lik birkaç kesiden kamera ve ince aletler yardımıyla alınmasıdır. Rahim genellikle vajinal yoldan çıkarılır ve vajina üst ucu laparoskopik olarak dikilir. Açık ameliyattaki büyük karın kesisine gerek kalmaz.",
      },
      {
        q: "Hangi durumlarda rahim alınması gerekir?",
        a: "İlaç ve daha küçük girişimlere yanıt vermeyen aşırı adet kanaması, çok sayıda veya büyük miyom, adenomyozis, ileri evre endometriozis, rahim sarkması, tekrarlayan rahim içi kanama ve bazı kanser öncesi veya kanser tanıları başlıca nedenlerdir. Doğurganlığın korunması isteniyorsa önce rahmi koruyan tedaviler değerlendirilir.",
      },
      {
        q: "Yumurtalıklar da alınır mı, menopoza girer miyim?",
        a: "Yumurtalıklar korunduğunda hormon üretimi sürer ve cerrahi menopoz gelişmez; yalnızca adet kanaması sona erer. Menopoz öncesi kadınlarda tıbbi bir gereklilik yoksa yumurtalıklar korunur. Yumurtalıkların da alınması gerekirse menopoz aniden başlar ve hormon tedavisi gündeme gelebilir.",
      },
      {
        q: "Ameliyat ne kadar sürer, hastanede ne kadar kalırım?",
        a: "Ameliyat genellikle 1,5–3 saat sürer. Hastaların çoğu 1–2 gün içinde taburcu olur; seçilmiş olgularda aynı gün taburculuk da mümkündür.",
      },
      {
        q: "İyileşme süreci nasıl ilerler?",
        a: "İlk hafta ağrı basit ağrı kesicilerle kontrol altına alınır ve günlük yürüyüş önerilir. 2–4 hafta içinde günlük yaşama, 4–6 hafta içinde ağır iş ve spora dönülür. Vajinal dikişlerin iyileşmesi için 6 hafta boyunca cinsel ilişki, tampon kullanımı ve ağır kaldırma önerilmez. Bu dönemde hafif lekelenme normaldir.",
      },
      {
        q: "Cinsel yaşam olumsuz etkilenir mi?",
        a: "Çalışmalar, iyileşme tamamlandıktan sonra cinsel işlevin çoğu kadında değişmediğini veya ağrı ve kanamanın ortadan kalkması nedeniyle iyileştiğini göstermektedir. Yumurtalıklar korunduğunda hormonal değişiklik beklenmez.",
      },
      {
        q: "Laparoskopik yöntemin açık ameliyata üstünlüğü nedir?",
        a: "Daha az kan kaybı, daha az ameliyat sonrası ağrı, daha düşük yara enfeksiyonu oranı, daha kısa hastanede kalış ve belirgin biçimde hızlı işe dönüş sağlar. Çok büyük rahim, yaygın yapışıklık veya bazı kanser olgularında açık cerrahi hâlâ daha uygun olabilir.",
      },
      {
        q: "Ameliyattan sonra smear testi yaptırmam gerekir mi?",
        a: "Rahim ağzı da alındıysa ve öncesinde anormal smear veya kanser öyküsü yoksa rutin smear taramasına gerek kalmaz. Rahim ağzı korunduysa (subtotal histerektomi) veya öncesinde yüksek riskli HPV/anormal hücre öyküsü varsa tarama hekiminizin önerdiği aralıklarla sürdürülür.",
      },
    ],
  },
  preeklampsi: {
    slug: "preeklampsi",
    enSlug: "preeclampsia",
    title: "Preeklampsi (Gebelik Zehirlenmesi): Belirtiler, Risk Faktörleri ve Korunma",
    metaTitle: "Preeklampsi Nedir? Belirtileri, Riskleri ve Aspirinle Korunma",
    description:
      "Gebelikte yüksek tansiyon ve preeklampsi: kimler risk altında, hangi belirtiler acildir, düşük doz aspirin kimlere önerilir, tanı, takip ve doğum zamanlaması.",
    metaDescription:
      "Preeklampsi rehberi: belirtiler, risk faktörleri, düşük doz aspirinle korunma, tanı kriterleri, takip ve doğum zamanlaması.",
    date: "2026-10-06",
    readingTime: "10 dk okuma",
    keyword: "preeklampsi",
    tldr: [
      "Preeklampsi, gebeliğin 20. haftasından sonra ortaya çıkan yüksek tansiyon ile birlikte idrarda protein veya organ etkilenmesi bulgularıdır; gebeliklerin yaklaşık %2–8'inde görülür.",
      "Şiddetli baş ağrısı, görme bulanıklığı, sağ üst karın ağrısı, ani yüz/el şişliği ve nefes darlığı acil başvuru gerektirir.",
      "Yüksek riskli gebelerde 12–16. haftalar arasında başlanan düşük doz aspirin, preeklampsi riskini belirgin biçimde azaltır.",
      "Kesin tedavi plasentanın doğumudur; doğum zamanı, gebelik haftası ile anne ve bebeğin durumuna göre planlanır.",
    ],
    faqs: [
      {
        q: "Preeklampsi nedir?",
        a: "Gebeliğin 20. haftasından sonra ilk kez ortaya çıkan, 140/90 mmHg ve üzerindeki tansiyona idrarda protein veya böbrek, karaciğer, pıhtılaşma, beyin ya da plasenta işlevini etkileyen bulguların eşlik etmesidir. Plasentanın gelişimindeki bozukluklardan kaynaklandığı düşünülmektedir.",
      },
      {
        q: "Preeklampsinin belirtileri nelerdir?",
        a: "Hafif olgularda belirti olmayabilir ve tanı rutin tansiyon ölçümüyle konur. Geçmeyen şiddetli baş ağrısı, görmede bulanıklık veya ışık çakmaları, sağ üst karın ya da mide ağrısı, bulantı-kusma, yüzde ve ellerde ani şişlik, nefes darlığı ve idrar miktarında azalma uyarıcı belirtilerdir.",
      },
      {
        q: "Kimler risk altındadır?",
        a: "Daha önceki gebelikte preeklampsi, kronik hipertansiyon, gebelik öncesi diyabet, böbrek hastalığı, lupus veya antifosfolipid sendromu, çoğul gebelik, ilk gebelik, 35 yaş üstü, obezite, tüp bebek gebeliği ve ailede preeklampsi öyküsü risk faktörleridir.",
      },
      {
        q: "Aspirin preeklampsiyi önler mi?",
        a: "Yüksek riskli gebelerde 12–16. haftalar arasında başlanıp 36. haftaya kadar sürdürülen düşük doz aspirinin preeklampsi, özellikle erken başlangıçlı ağır preeklampsi riskini azalttığı gösterilmiştir. Aspirin yalnızca hekim önerisiyle ve önerilen dozda kullanılmalıdır.",
      },
      {
        q: "Preeklampsi nasıl tedavi edilir?",
        a: "Kesin tedavi bebeğin ve plasentanın doğumudur. 37. haftadan sonra genellikle doğum önerilir. Daha erken haftalarda tansiyon ilaçları, nöbetleri önlemek için magnezyum sülfat ve bebeğin akciğer gelişimi için kortikosteroid ile yakın takip yapılır; ağır bulgularda haftadan bağımsız olarak doğum gerekebilir.",
      },
      {
        q: "Preeklampsi bebeği nasıl etkiler?",
        a: "Plasenta kan akımının azalmasına bağlı olarak bebeğin büyümesi gerileyebilir, amniyon sıvısı azalabilir ve erken doğum gerekebilir. Nadiren plasenta ayrılması (dekolman) gelişebilir. Bu nedenle ultrason ve Doppler ile bebeğin büyümesi ve iyilik hâli izlenir.",
      },
      {
        q: "Doğumdan sonra preeklampsi geçer mi?",
        a: "Bulgular çoğunlukla doğumdan sonraki günler ve haftalar içinde geriler; ancak preeklampsi doğumdan sonraki ilk 6 hafta içinde de başlayabilir veya ağırlaşabilir. Preeklampsi geçiren kadınlarda ileride hipertansiyon ve kalp-damar hastalığı riski arttığından düzenli tansiyon ve sağlık kontrolü önerilir.",
      },
    ],
  },
  "smear-testi-hpv-taramasi": {
    slug: "smear-testi-hpv-taramasi",
    enSlug: "cervical-screening-smear-hpv",
    title: "Smear Testi ve HPV Taraması: Ne Zaman Yapılır, Sonuçlar Ne Anlama Gelir?",
    metaTitle: "Smear Testi ve HPV Taraması: Rahim Ağzı Kanseri Rehberi",
    description:
      "Rahim ağzı kanseri taraması: smear ve HPV testi kaç yaşında, hangi aralıkla yapılır? ASCUS, LSIL, HSIL ve HPV pozitif sonuçları, kolposkopi ve tedavi.",
    metaDescription:
      "Smear ve HPV testi rehberi: tarama yaşları, hazırlık, ASCUS/LSIL/HSIL ve HPV pozitif sonuçların anlamı, kolposkopi ve takip.",
    date: "2026-10-06",
    readingTime: "10 dk okuma",
    keyword: "smear testi",
    tldr: [
      "Rahim ağzı kanserinin neredeyse tamamı kalıcı yüksek riskli HPV enfeksiyonuna bağlıdır; düzenli tarama ile kanser öncesi değişiklikler yıllar önceden yakalanabilir.",
      "Türkiye'de ulusal programda 30–65 yaş arası kadınlara 5 yılda bir HPV testi ve smear önerilir; uluslararası rehberler 21–25 yaşta başlayan taramayı tanımlar.",
      "HPV pozitif sonuç kanser anlamına gelmez; çoğu enfeksiyon 1–2 yılda kendiliğinden temizlenir.",
      "HPV 16/18 pozitifliği veya anormal hücre saptanması durumunda kolposkopi ile rahim ağzı büyütülerek incelenir.",
    ],
    faqs: [
      {
        q: "Smear testi nedir?",
        a: "Rahim ağzından (serviks) yumuşak bir fırça ile hücre örneği alınarak mikroskop altında incelenmesidir. Kanser öncesi hücre değişikliklerini saptar. Aynı örnekten HPV testi de çalışılabilir. İşlem birkaç dakika sürer ve genellikle ağrısızdır.",
      },
      {
        q: "Smear ve HPV testi kaç yaşında, ne sıklıkla yapılmalı?",
        a: "Türkiye'de Sağlık Bakanlığı programı 30–65 yaş arası kadınlara 5 yılda bir HPV testi ve smear önerir; KETEM'lerde ve aile hekimliklerinde ücretsizdir. Uluslararası rehberler 21–25 yaşta smear ile başlayıp 25–30 yaştan sonra 5 yılda bir HPV temelli taramayı önerir. Bağışıklığı baskılanmış kadınlarda daha sık tarama gerekir.",
      },
      {
        q: "Test öncesi nelere dikkat etmeliyim?",
        a: "Adet döneminin dışında yaptırmanız önerilir. Testten önceki 48 saat içinde cinsel ilişki, vajinal duş, vajinal ilaç veya kayganlaştırıcı kullanımından kaçınmak sonucun güvenilirliğini artırır.",
      },
      {
        q: "HPV pozitif çıktı, kanser miyim?",
        a: "Hayır. HPV çok yaygın bir enfeksiyondur ve cinsel olarak aktif kişilerin çoğu hayatlarının bir döneminde karşılaşır. Enfeksiyonların büyük çoğunluğu 1–2 yıl içinde bağışıklık sistemi tarafından temizlenir. Önemli olan yüksek riskli tiplerin uzun süre kalıcı olmasıdır; bu nedenle sonuç tipine ve smear bulgusuna göre takip planlanır.",
      },
      {
        q: "ASCUS, LSIL, HSIL ne anlama gelir?",
        a: "ASCUS önemi belirsiz atipik hücreler, LSIL düşük dereceli hücre değişiklikleri, HSIL ise yüksek dereceli kanser öncesi değişiklikler anlamına gelir. ASCUS ve LSIL çoğunlukla HPV durumuna göre takip veya kolposkopi ile değerlendirilir; HSIL'de kolposkopi ve gerektiğinde tedavi önerilir.",
      },
      {
        q: "Kolposkopi nedir, ağrılı mıdır?",
        a: "Rahim ağzının özel bir büyüteçle incelenmesidir. Asetik asit uygulanarak anormal alanlar belirlenir ve gerekirse küçük biyopsiler alınır. Muayene smear gibi yapılır, 10–15 dakika sürer; biyopsi sırasında kısa süreli kramp hissedilebilir.",
      },
      {
        q: "HPV aşısı olduysam tarama yaptırmam gerekir mi?",
        a: "Evet. Aşı en sık kanser yapan HPV tiplerine karşı güçlü koruma sağlar, ancak tüm tipleri kapsamaz. Aşılı kadınlar da yaşlarına uygun taramaya devam etmelidir.",
      },
    ],
  },
  "anormal-rahim-kanamasi": {
    slug: "anormal-rahim-kanamasi",
    enSlug: "abnormal-uterine-bleeding",
    title: "Anormal Rahim Kanaması ve Adet Düzensizliği: Nedenleri ve Tedavisi",
    metaTitle: "Anormal Rahim Kanaması: Adet Düzensizliği Nedenleri ve Tedavi",
    description:
      "Ara kanama, aşırı adet kanaması ve düzensiz adet: normal adetin sınırları, PALM-COEIN nedenleri, tanı yöntemleri, ilaç ve histeroskopik tedavi seçenekleri.",
    metaDescription:
      "Anormal rahim kanaması rehberi: normal adetin sınırları, polip, miyom, hormonal nedenler, menopoz sonrası kanama, tanı ve tedavi.",
    date: "2026-10-06",
    readingTime: "10 dk okuma",
    keyword: "anormal rahim kanaması",
    tldr: [
      "Normal adet döngüsü 24–38 gün, kanama süresi 8 günden kısadır; bu sınırların dışındaki, ara dönemdeki veya aşırı miktardaki kanamalar anormal rahim kanaması olarak değerlendirilir.",
      "Nedenler yapısal (polip, adenomyozis, miyom, kanser öncesi değişiklik) ve yapısal olmayan (pıhtılaşma bozukluğu, yumurtlama bozukluğu, ilaçlar) olarak sınıflandırılır.",
      "Değerlendirmede gebelik testi, ultrason, kan tetkikleri ve gerekirse endometrial biyopsi veya histeroskopi kullanılır.",
      "Menopoz sonrası her kanama mutlaka değerlendirilmelidir.",
    ],
    faqs: [
      {
        q: "Hangi kanama anormaldir?",
        a: "Adet aralığının 24 günden kısa veya 38 günden uzun olması, kanamanın 8 günden uzun sürmesi, döngüler arasında 7–9 günden fazla değişkenlik, adetler arası veya cinsel ilişki sonrası kanama, saatlik ped değiştirmeyi gerektiren ya da büyük pıhtılı kanama ve menopoz sonrası her türlü kanama anormal kabul edilir.",
      },
      {
        q: "Anormal rahim kanamasının en sık nedenleri nelerdir?",
        a: "Uluslararası PALM-COEIN sınıflamasına göre yapısal nedenler polip, adenomyozis, miyom ve kanser öncesi değişiklikler veya kanserdir. Yapısal olmayan nedenler ise pıhtılaşma bozuklukları, yumurtlama bozuklukları (PCOS, tiroid hastalıkları, prolaktin yüksekliği, stres), rahim iç zarına ait nedenler, ilaçlar ve spiral gibi tıbbi nedenlerdir.",
      },
      {
        q: "Hangi tetkikler yapılır?",
        a: "Önce gebelik olasılığı dışlanır. Jinekolojik muayene, transvajinal ultrason, tam kan sayımı ve demir depoları, gerekirse tiroid, prolaktin ve pıhtılaşma testleri yapılır. 45 yaş üstü, risk faktörü olan veya tedaviye yanıt vermeyen kadınlarda endometrial biyopsi; polip ya da rahim içi miyom şüphesinde histeroskopi önerilir.",
      },
      {
        q: "Aşırı adet kanaması kansızlık yapar mı?",
        a: "Evet. Uzun süreli yoğun kanama demir eksikliği anemisinin en sık nedenlerinden biridir; halsizlik, çarpıntı, saç dökülmesi ve konsantrasyon güçlüğüne yol açabilir. Tedaviye demir desteği eklenir.",
      },
      {
        q: "Tedavi seçenekleri nelerdir?",
        a: "Nedene göre değişir. Hormonsuz seçenekler arasında traneksamik asit ve steroid olmayan ağrı kesiciler, hormonal seçenekler arasında doğum kontrol hapları, progesteron ve hormonlu rahim içi araç (levonorgestrel salınımlı spiral) bulunur. Polip ve rahim içi miyomlar histeroskopiyle çıkarılır. Tedaviye yanıt vermeyen olgularda endometrial ablasyon veya histerektomi değerlendirilebilir.",
      },
      {
        q: "Menopoz sonrası kanama tehlikeli midir?",
        a: "Menopoz sonrası kanamaların çoğu rahim iç zarı incelmesi veya polip gibi iyi huylu nedenlere bağlıdır; ancak endometrium kanserinin en önemli belirtisi olduğu için her menopoz sonrası kanama ultrason ve gerekirse biyopsiyle değerlendirilmelidir.",
      },
      {
        q: "Ne zaman hemen doktora başvurmalıyım?",
        a: "Bir saatte bir veya daha sık ped değiştirmeyi gerektiren kanama, baş dönmesi ve bayılma hissi, gebelik olasılığı varken kanama ve şiddetli kasık ağrısı acil değerlendirme gerektirir.",
      },
    ],
  },
  "dis-gebelik": {
    slug: "dis-gebelik",
    enSlug: "ectopic-pregnancy",
    title: "Dış Gebelik (Ektopik Gebelik): Belirtiler, Tanı ve Tedavi",
    metaTitle: "Dış Gebelik Nedir? Belirtileri, Tanısı ve Tedavi Seçenekleri",
    description:
      "Dış gebelik nedir, hangi belirtiler acildir? Beta-hCG ve ultrason ile tanı, metotreksat tedavisi, laparoskopik cerrahi ve sonraki gebelik şansı.",
    metaDescription:
      "Dış gebelik rehberi: risk faktörleri, acil belirtiler, beta-hCG ve ultrasonla tanı, ilaç ve laparoskopik tedavi, sonraki gebelik.",
    date: "2026-10-14",
    readingTime: "9 dk okuma",
    keyword: "dış gebelik",
    tldr: [
      "Dış gebelik, döllenmiş yumurtanın rahim boşluğu dışında, en sık tüplerde yerleşmesidir; gebeliklerin yaklaşık %1–2'sinde görülür.",
      "Adet gecikmesiyle birlikte kasık ağrısı ve vajinal kanama en sık belirtilerdir; şiddetli ağrı, bayılma hissi veya omuz ağrısı acil durumdur.",
      "Tanı, kanda beta-hCG takibi ve transvajinal ultrason ile konur.",
      "Uygun olgularda metotreksat ile ilaç tedavisi, diğerlerinde laparoskopik cerrahi uygulanır; çoğu kadın sonrasında sağlıklı gebelik elde eder.",
    ],
    faqs: [
      {
        q: "Dış gebelik nedir?",
        a: "Döllenmiş yumurtanın rahim iç zarı yerine başka bir yere, büyük çoğunlukla fallop tüpüne yerleşmesidir. Daha nadiren yumurtalık, rahim ağzı, sezaryen skarı veya karın boşluğunda görülür. Bu gebelikler sağlıklı biçimde gelişemez ve tedavi edilmezse tüp yırtılarak iç kanamaya yol açabilir.",
      },
      {
        q: "Belirtileri nelerdir?",
        a: "Adet gecikmesi veya pozitif gebelik testiyle birlikte tek taraflı ya da yaygın kasık ağrısı ve lekelenme tarzında kanama en sık belirtilerdir. Şiddetli karın ağrısı, baş dönmesi, bayılma, omuz ucu ağrısı ve makata baskı hissi iç kanamayı düşündürür ve acil başvuru gerektirir.",
      },
      {
        q: "Kimler risk altındadır?",
        a: "Önceki dış gebelik, tüp ameliyatı veya tüp ligasyonu, geçirilmiş pelvik enfeksiyon (özellikle klamidya), endometriozis, tüp bebek tedavisi, sigara kullanımı ve rahim içi araç varken gebe kalma riski artırır. Ancak dış gebeliklerin yaklaşık yarısında belirgin bir risk faktörü yoktur.",
      },
      {
        q: "Nasıl tanı konur?",
        a: "Kanda beta-hCG düzeyi ölçülür ve 48 saat arayla tekrarlanır. Transvajinal ultrasonda rahim içinde gebelik kesesi görülmemesi, tüp bölgesinde kitle veya karın içinde sıvı saptanması tanıyı destekler. Bazen kesin tanı için birkaç kez ölçüm ve ultrason gerekir; bu dönemde 'yeri belirlenemeyen gebelik' olarak yakın takip yapılır.",
      },
      {
        q: "Metotreksat tedavisi kimlere uygundur?",
        a: "Hemodinamisi stabil, iç kanama bulgusu olmayan, beta-hCG düzeyi görece düşük ve ultrasonda küçük kitle saptanan kadınlarda metotreksat iğnesi ile tüp korunarak tedavi mümkündür. Tedavi sonrası beta-hCG sıfırlanana kadar düzenli izlenir; bu dönemde alkol, folik asit içeren vitaminler ve cinsel ilişkiden kaçınılır.",
      },
      {
        q: "Ne zaman ameliyat gerekir?",
        a: "İç kanama, tüp yırtılması şüphesi, yüksek beta-hCG düzeyi, ultrasonda bebek kalp atımı görülmesi veya ilaç tedavisine yanıtsızlıkta laparoskopik cerrahi uygulanır. Tüp durumuna göre yalnızca gebelik dokusu çıkarılır (salpingostomi) ya da tüp alınır (salpenjektomi).",
      },
      {
        q: "Dış gebelikten sonra tekrar gebe kalabilir miyim?",
        a: "Evet. Tek tüpü alınan kadınların büyük çoğunluğu diğer tüp aracılığıyla kendiliğinden gebe kalabilir. Sonraki gebelikte tekrar dış gebelik riski biraz arttığından, gebelik testi pozitif olur olmaz erken ultrason ile gebeliğin yeri doğrulanmalıdır.",
      },
    ],
  },
  "dusuk-tekrarlayan-gebelik-kaybi": {
    slug: "dusuk-tekrarlayan-gebelik-kaybi",
    enSlug: "miscarriage-recurrent-pregnancy-loss",
    title: "Düşük ve Tekrarlayan Gebelik Kaybı: Nedenleri, Tetkikleri ve Tedavisi",
    metaTitle: "Düşük ve Tekrarlayan Gebelik Kaybı: Nedenler ve Tetkikler",
    description:
      "Erken gebelik kaybı neden olur, belirtileri nelerdir? Bekleme, ilaç ve küretaj seçenekleri; tekrarlayan düşüklerde yapılan tetkikler ve sonraki gebelik.",
    metaDescription:
      "Düşük rehberi: belirtiler, nedenler, tedavi seçenekleri, tekrarlayan gebelik kaybında tetkikler ve tedavi, sonraki gebelik için öneriler.",
    date: "2026-10-14",
    readingTime: "10 dk okuma",
    keyword: "düşük",
    tldr: [
      "Bilinen gebeliklerin yaklaşık %10–15'i ilk 12 haftada düşükle sonuçlanır; en sık neden bebekteki rastlantısal kromozom farklılıklarıdır.",
      "Erken düşüklerin büyük çoğunluğu annenin yaptığı bir şeyle (çalışma, cinsel ilişki, egzersiz, hafif stres) ilişkili değildir.",
      "Tedavi seçenekleri bekleyerek izlem, ilaç tedavisi ve vakum aspirasyonudur.",
      "İki veya daha fazla gebelik kaybında nedene yönelik araştırma önerilir; uygun tedaviyle çoğu çift sonraki gebelikte sağlıklı bebek sahibi olur.",
    ],
    faqs: [
      {
        q: "Düşük ne kadar sık görülür?",
        a: "Gebelik olduğu bilinen kadınların yaklaşık %10–15'inde ilk 12 haftada düşük görülür. Risk anne yaşıyla birlikte artar; 35 yaşından sonra belirgin biçimde yükselir.",
      },
      {
        q: "Düşüğün nedenleri nelerdir?",
        a: "Erken düşüklerin yaklaşık yarısı bebekteki rastlantısal kromozom farklılıklarından kaynaklanır ve tekrarlaması beklenmez. Diğer nedenler arasında ileri anne yaşı, kontrolsüz diyabet ve tiroid hastalığı, antifosfolipid sendromu, rahim içi yapısal sorunlar (septum, polip, rahim içi miyom), sigara, aşırı alkol ve bazı enfeksiyonlar sayılabilir.",
      },
      {
        q: "Düşük belirtileri nelerdir?",
        a: "Vajinal kanama, adet sancısına benzer veya daha şiddetli kasık ağrısı, doku parçalarının gelmesi ve bulantı, göğüs hassasiyeti gibi gebelik belirtilerinde ani azalma görülebilir. Bazı düşükler ise belirti vermeden rutin ultrasonda saptanır (gecikmiş düşük).",
      },
      {
        q: "Düşükten sonra küretaj şart mı?",
        a: "Hayır. Uygun olgularda dokunun kendiliğinden atılması beklenebilir veya misoprostol gibi ilaçlarla süreç hızlandırılabilir. Yoğun kanama, enfeksiyon bulgusu, ilaçla tamamlanamayan düşük veya hastanın tercihi durumunda vakum aspirasyonu uygulanır. Seçim, hekimle birlikte yarar ve risklere göre yapılır.",
      },
      {
        q: "Tekrarlayan gebelik kaybı nedir?",
        a: "Avrupa İnsan Üremesi ve Embriyoloji Derneği (ESHRE) iki veya daha fazla gebelik kaybını tekrarlayan gebelik kaybı olarak tanımlar. Bu durumda neden araştırması önerilir.",
      },
      {
        q: "Tekrarlayan düşüklerde hangi tetkikler yapılır?",
        a: "Antifosfolipid antikorları, tiroid fonksiyonları ve antikorları, şeker ve gerekirse prolaktin bakılır. Rahim yapısı 3 boyutlu ultrason, histeroskopi veya MR ile değerlendirilir. Seçilmiş çiftlerde kromozom analizi ve düşük dokusunun genetik incelemesi önerilir. Olguların yaklaşık yarısında belirgin bir neden bulunamaz.",
      },
      {
        q: "Ne zaman tekrar gebe kalabilirim?",
        a: "Fiziksel olarak hazır hissettiğinizde ve kanama sona erdikten sonra yeniden denenebilir; birçok kadın bir adet beklemeyi tercih eder. Folik asit kullanımına devam edilmesi önerilir. Nedeni açıklanamayan tekrarlayan düşüklerde bile sonraki gebelikte canlı doğum şansı yüksektir.",
      },
    ],
  },
  "lohusalik-dogum-sonrasi": {
    slug: "lohusalik-dogum-sonrasi",
    enSlug: "postpartum-recovery",
    title: "Lohusalık (Doğum Sonrası Dönem): İyileşme, Uyarı Belirtileri ve Kontroller",
    metaTitle: "Lohusalık Dönemi: Doğum Sonrası İyileşme ve Uyarı Belirtileri",
    description:
      "Doğum sonrası ilk 6 hafta: lohusa kanaması, dikiş ve sezaryen yarası bakımı, emzirme, doğum sonrası depresyon, acil belirtiler, kontrol takvimi ve doğum kontrolü.",
    metaDescription:
      "Lohusalık rehberi: loşi kanaması, dikiş bakımı, sezaryen sonrası iyileşme, emzirme, doğum sonrası depresyon, acil belirtiler ve kontroller.",
    date: "2026-10-14",
    readingTime: "10 dk okuma",
    keyword: "lohusalık",
    tldr: [
      "Lohusalık, doğumdan sonraki yaklaşık 6 haftalık dönemdir; rahim bu sürede gebelik öncesi boyutuna döner.",
      "Lohusa kanaması (loşi) ilk günlerde kırmızı ve yoğundur, giderek azalıp açılır ve 4–6 haftada sona erer.",
      "Yoğun kanama, ateş, şiddetli baş ağrısı, göğüs ağrısı, nefes darlığı veya bacakta şişlik acil başvuru gerektirir.",
      "Doğum sonrası hüzün yaygındır ve 2 haftada geçer; daha uzun süren veya ağır belirtiler doğum sonrası depresyonu düşündürür ve tedavi edilebilir.",
    ],
    faqs: [
      {
        q: "Lohusa kanaması ne kadar sürer?",
        a: "Loşi adı verilen lohusa kanaması ilk 3–4 gün kırmızı ve adet kanamasından yoğun olabilir; ardından pembe-kahverengiye, sonra sarımsı-beyaza döner ve genellikle 4–6 haftada sona erer. Emzirme sırasında artması normaldir. Saatte bir pedi dolduran kanama, büyük pıhtılar veya kötü kokulu akıntı acil değerlendirme gerektirir.",
      },
      {
        q: "Dikiş (epizyotomi) bakımı nasıl yapılır?",
        a: "Bölge her tuvaletten sonra ılık suyla önden arkaya doğru yıkanıp kurulanmalı, pedler sık değiştirilmelidir. İlk günlerde soğuk uygulama şişliği azaltır. Dikişler genellikle kendiliğinden erir ve 2–3 haftada iyileşir. Artan ağrı, kızarıklık, akıntı veya ateş enfeksiyon belirtisi olabilir.",
      },
      {
        q: "Sezaryen sonrası iyileşme nasıl olur?",
        a: "İlk gün ayağa kalkmak önerilir. Yara genellikle 1–2 haftada kapanır; banyo yapılabilir, yara kurulanmalıdır. İlk 6 hafta bebeğinizden ağır yük kaldırmaktan kaçının. Yarada kızarıklık, akıntı, açılma veya ateş varsa hekiminize başvurun.",
      },
      {
        q: "Doğum sonrası hüzün ile depresyon arasındaki fark nedir?",
        a: "Annelerin büyük bölümü ilk günlerde ağlamaklılık, duygu dalgalanması ve kaygı yaşar; bu 'doğum sonrası hüzün' genellikle 2 hafta içinde geçer. İki haftadan uzun süren mutsuzluk, zevk alamama, uyku ve iştah bozukluğu, değersizlik duyguları veya kendine ya da bebeğe zarar verme düşünceleri doğum sonrası depresyonu düşündürür; tedavi edilebilir bir durumdur ve hemen yardım alınmalıdır.",
      },
      {
        q: "Hangi belirtiler acildir?",
        a: "Yoğun kanama, 38°C üzeri ateş, şiddetli veya geçmeyen baş ağrısı, görme bozukluğu, göğüs ağrısı, nefes darlığı, bir bacakta ağrılı şişlik, memede kızarık sertlik ve ateş, idrar yaparken yanma ve şiddetli karın ağrısı acil değerlendirme gerektirir. Preeklampsi doğumdan sonra da başlayabilir.",
      },
      {
        q: "Doğum sonrası kontroller ne zaman yapılır?",
        a: "İlk haftalarda tansiyon, kanama, yara ve emzirme kontrolü; 6. hafta civarında kapsamlı muayene önerilir. Bu muayenede rahim ve dikişler, ruh hâli, pelvik taban, doğum kontrol yöntemi ve gebelikte saptanan diyabet ya da hipertansiyon gibi sorunların takibi değerlendirilir.",
      },
      {
        q: "Doğumdan sonra ne zaman gebe kalabilirim?",
        a: "Emzirmeyen kadınlarda yumurtlama doğumdan 3–4 hafta sonra başlayabilir. Emzirme tek başına güvenilir korunma sağlamaz. Sağlıklı bir sonraki gebelik için iki doğum arasında en az 18 ay olması önerilir; emzirmeyle uyumlu yöntemler hekiminizle seçilmelidir.",
      },
    ],
  },
};

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = POSTS[params.slug];
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Yazı bulunamadı | Op. Dr. Sadık Kükrer" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `https://www.sadikkukrer.com/blog/${post.slug}`;
    const modified = post.updated && post.updated > post.date ? post.updated : post.date;
    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          image: [AUTHOR_IMAGE],
          datePublished: post.date,
          dateModified: modified,
          inLanguage: "tr-TR",
          url,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".article-tldr", "h1", "h2"],
          },
          author: {
            "@type": "Physician",
            name: "Op. Dr. Sadık Kükrer",
            medicalSpecialty: ["Gynecology", "Obstetrics"],
            url: "https://www.sadikkukrer.com/",
            worksFor: {
              "@type": "Hospital",
              name: "SBÜ Adana Şehir Eğitim ve Araştırma Hastanesi",
            },
          },
          publisher: {
            "@type": "Physician",
            name: "Op. Dr. Sadık Kükrer",
            url: "https://www.sadikkukrer.com/",
          },
          about: { "@type": "MedicalCondition", name: post.keyword },
          keywords: `${post.keyword}, Adana kadın doğum, Op. Dr. Sadık Kükrer`,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Ana Sayfa",
              item: "https://www.sadikkukrer.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.sadikkukrer.com/blog",
            },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        }),
      },
    ];
    if (post.faqs && post.faqs.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    const metaTitle = post.metaTitle ?? post.title;
    const metaDescription = post.metaDescription ?? post.description;
    return {
      meta: [
        { title: metaTitle },
        { name: "description", content: metaDescription },
        { name: "keywords", content: `${post.keyword}, Adana kadın doğum, Op. Dr. Sadık Kükrer` },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:author", content: "Op. Dr. Sadık Kükrer" },
        { property: "article:published_time", content: post.date },
        { property: "article:modified_time", content: modified },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: metaDescription },
      ],
      links: (() => {
        const l: Array<{ rel: string; href: string; hrefLang?: string }> = [
          { rel: "canonical", href: url },
          { rel: "alternate", hrefLang: "tr", href: url },
        ];
        if (post.enSlug) {
          l.push({
            rel: "alternate",
            hrefLang: "en",
            href: `https://www.sadikkukrer.com/en/blog/${post.enSlug}`,
          });
        }
        return l;
      })(),
      scripts,
    };
  },
  notFoundComponent: PostNotFound,
  component: BlogPost,
});

function PostNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8">
      <h1 className="font-display text-3xl font-semibold">Yazı bulunamadı</h1>
      <p className="mt-3 text-[color:var(--muted-foreground)]">
        Aradığınız blog yazısı bulunamadı ya da kaldırılmış olabilir.
      </p>
      <Link
        to="/blog"
        className="mt-6 inline-flex rounded-full bg-[color:var(--primary)] px-6 py-2.5 text-sm font-medium text-[color:var(--primary-foreground)]"
      >
        Blog'a dön
      </Link>
    </div>
  );
}

function BlogPost() {
  const { post } = Route.useLoaderData();
  const slug = post.slug;

  if (slug === "hamilelik-belirtileri") {
    return <HamilelikBelirtileriPost post={post} />;
  }
  if (slug === "gebelik-haftasi-hesaplama") {
    return <GebelikHaftasiHesaplamaPost post={post} />;
  }
  if (slug === "gebelik-testi-ne-zaman-nasil-yapilir") {
    return <GebelikTestiPost post={post} />;
  }
  if (slug === "gebelikte-kanama") {
    return <GebelikteKanamaPost post={post} />;
  }
  if (slug === "gebelikte-beslenme") {
    return <GebelikteBeslenmePost post={post} />;
  }
  if (slug === "yumurtalik-kisti") {
    return <YumurtalikKistiPost post={post} />;
  }
  if (slug === "vajinal-akinti") {
    return <VajinalAkintiPost post={post} />;
  }
  if (slug === "endometriozis") {
    return <EndometriozisPost post={post} />;
  }
  if (slug === "miyom") {
    return <MiyomPost post={post} />;
  }
  if (slug === "hpv-asisi") {
    return <HpvAsisiPost post={post} />;
  }
  if (slug === "pcos") {
    return <PcosPost post={post} />;
  }
  if (slug === "histeroskopi") {
    return <HisteroskopiPost post={post} />;
  }
  if (slug === "menopoz") {
    return <MenopozPost post={post} />;
  }
  if (slug === "kisirlik-infertilite") {
    return <KisirlikPost post={post} />;
  }
  if (slug === "gebelikte-tarama-testleri") {
    return <TaramaTestleriPost post={post} />;
  }
  if (slug === "dogum-kontrol-yontemleri") {
    return <DogumKontrolPost post={post} />;
  }
  if (slug === "normal-dogum-mu-sezaryen-mi") {
    return <DogumSekliPost post={post} />;
  }
  if (slug === "gestasyonel-diyabet") {
    return <GestasyonelDiyabetPost post={post} />;
  }
  if (slug === "laparoskopi-nedir") {
    return <LaparoskopiPost post={post} />;
  }
  if (slug === "laparoskopik-histerektomi") {
    return <LaparoskopikHisterektomiPost post={post} />;
  }

  if (slug === "preeklampsi") {
    return <PreeklampsiPost post={post} />;
  }
  if (slug === "smear-testi-hpv-taramasi") {
    return <SmearHpvPost post={post} />;
  }
  if (slug === "anormal-rahim-kanamasi") {
    return <AnormalKanamaPost post={post} />;
  }
  if (slug === "dis-gebelik") {
    return <DisGebelikPost post={post} />;
  }
  if (slug === "dusuk-tekrarlayan-gebelik-kaybi") {
    return <DusukPost post={post} />;
  }
  if (slug === "lohusalik-dogum-sonrasi") {
    return <LohusalikPost post={post} />;
  }
  return null;
}

function ArticleShell({ post, children }: { post: Post; children: React.ReactNode }) {
  return (
    <article className="mx-auto max-w-3xl px-5 py-16 md:px-8 md:py-24">
      <nav className="mb-8 flex items-center justify-between text-xs text-[color:var(--muted-foreground)]">
        <Link to="/blog" className="hover:text-[color:var(--primary-text)]">
          ← Tüm yazılar
        </Link>
        {post.enSlug && (
          <Link
            to="/en/blog/$slug"
            params={{ slug: post.enSlug }}
            className="rounded-full border border-[color:var(--border)] px-3 py-1 uppercase tracking-[0.2em] hover:text-[color:var(--gold-light)]"
            aria-label="Read in English"
          >
            Read in English →
          </Link>
        )}
      </nav>

      <header className="mb-10">
        <div className="flex flex-wrap items-center gap-3 text-xs text-[color:var(--muted-foreground)]">
          <time dateTime={post.date}>
            Yayın:{" "}
            {new Date(post.date).toLocaleDateString("tr-TR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </time>
          {post.updated && post.updated > post.date && (
            <>
              <span>·</span>
              <time dateTime={post.updated}>
                Son güncelleme:{" "}
                {new Date(post.updated).toLocaleDateString("tr-TR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </time>
            </>
          )}
          <span>·</span>
          <span>{post.readingTime}</span>
        </div>
        <h1 className="mt-4 font-display text-3xl font-semibold leading-tight md:text-5xl">
          {post.title}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-[color:var(--muted-foreground)]">
          {post.description}
        </p>
        <div className="mt-6 flex items-center gap-3 border-t border-[color:var(--border)] pt-5 text-sm">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--primary)] font-display font-semibold text-[color:var(--primary-foreground)]">
            SK
          </div>
          <div>
            <p className="font-medium">Op. Dr. Sadık Kükrer</p>
            <p className="text-xs text-[color:var(--muted-foreground)]">
              Kadın Hastalıkları ve Doğum Uzmanı · Adana
            </p>
          </div>
        </div>
      </header>

      {post.tldr && post.tldr.length > 0 && (
        <aside
          className="article-tldr mb-10 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6"
          aria-label="Kısa özet"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--rose)]">
            Kısa özet (TL;DR)
          </p>
          <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-[color:var(--foreground)]">
            {post.tldr.map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--primary)]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <div className="prose-content space-y-6 text-[15.5px] leading-[1.85] text-[color:var(--foreground)]">
        {children}
      </div>

      {post.faqs && post.faqs.length > 0 && (
        <section className="mt-14" aria-labelledby="sss-baslik">
          <h2 id="sss-baslik" className="font-display text-2xl font-semibold">
            Sıkça sorulan sorular
          </h2>
          <div className="mt-6 divide-y divide-[color:var(--border)] rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)]">
            {post.faqs.map((faq, i) => (
              <details key={i} className="group p-5 open:bg-[color:var(--darkest)]">
                <summary className="cursor-pointer list-none font-medium text-[color:var(--foreground)] marker:hidden">
                  <span className="inline-flex w-full items-start justify-between gap-4">
                    <span>{faq.q}</span>
                    <span className="mt-1 shrink-0 text-[color:var(--primary-text)] transition-transform group-open:rotate-45">
                      +
                    </span>
                  </span>
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--muted-foreground)]">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      <MedicalDisclaimer />

      <div className="mt-10 rounded-2xl bg-[color:var(--primary)] p-8 text-center text-[color:var(--primary-foreground)]">
        <p className="font-display text-2xl font-semibold">
          Muayene randevusu almak ister misiniz?
        </p>
        <p className="mt-2 opacity-90">
          Adana'da gebelik takibi, kadın hastalıkları ve jinekolojik cerrahi için Op. Dr. Sadık
          Kükrer'e ulaşın.
        </p>
        <Link
          to="/"
          hash="iletisim"
          className="mt-5 inline-flex rounded-full bg-[color:var(--darkest)] px-6 py-2.5 text-sm font-medium text-[color:var(--foreground)]"
        >
          İletişim bilgileri
        </Link>
      </div>
    </article>
  );
}

function HamilelikBelirtileriPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Hamilelik, kadın vücudunda hormonal ve fiziksel değişimlerin çok hızlı yaşandığı bir
        süreçtir. Bu değişimlerin bir kısmı henüz gebelik testi pozitife dönmeden, hatta beklenen
        adet tarihinden önce fark edilebilir. Bu yazıda, kliniğimize en sık sorulan{" "}
        <strong>erken hamilelik belirtileri</strong>, hangi haftada ortaya çıktıkları ve ne zaman
        gebelik testi yapılması gerektiği konularını sade bir dille özetliyorum.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Hamilelik belirtileri ne zaman başlar?
      </h2>
      <p>
        Belirtilerin çoğu, döllenmeden yaklaşık 6–12 gün sonra, embriyonun rahim duvarına
        yerleşmesiyle (implantasyon) birlikte ortaya çıkmaya başlar. Bazı kadınlar bu değişimleri
        çok erken hisseder, bazıları ise adet gecikmesine kadar hiçbir belirti fark etmez. İkisi de
        tamamen normaldir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        15 sık görülen erken hamilelik belirtisi
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Adet gecikmesi</h3>
      <p>
        En güvenilir ilk işarettir. Düzenli adet gören bir kadında 5–7 günden fazla gecikme, gebelik
        testi yapmak için yeterli bir nedendir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. İmplantasyon kanaması</h3>
      <p>
        Beklenen adet tarihinden 1–2 gün önce görülebilen, açık pembe veya kahverengi renkte, damla
        şeklinde çok hafif bir lekelenmedir. Genellikle 1–2 gün sürer ve adet kanamasıyla
        karıştırılabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Göğüslerde hassasiyet ve dolgunluk
      </h3>
      <p>
        Artan östrojen ve progesteron hormonları göğüs dokusunda kan akımını artırır. Göğüsler daha
        ağır, hassas, hatta bazen ağrılı hissedilebilir; meme uçları koyulaşabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Aşırı yorgunluk ve halsizlik</h3>
      <p>
        Progesteron hormonu uykuyu tetikler. İlk trimestrda pek çok kadın gün içinde şiddetli uyku
        ihtiyacı ve efor kapasitesinde belirgin düşüş yaşar.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Bulantı ve sabah hastalığı</h3>
      <p>
        Genellikle 6. haftadan itibaren başlar, 12–14. haftalarda azalır. Yalnızca sabahları değil,
        gün içinde herhangi bir saatte de ortaya çıkabilir. Belirli kokulara karşı hassasiyet
        bulantıyı tetikleyebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Sık idrara çıkma</h3>
      <p>
        Hem hormonal değişiklikler hem büyüyen rahimin mesaneye baskısı nedeniyle gece ve gündüz
        idrara çıkma sıklığı artar.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">7. Tat ve koku değişiklikleri</h3>
      <p>
        Daha önce sevilen yemeklerden tiksinme, ağızda metalik bir tat ya da bazı kokulara aşırı
        hassaslaşma sık karşılaşılan belirtilerdir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">8. Karında kramp</h3>
      <p>
        Rahmin genişlemesi ve implantasyona bağlı olarak adet krampına benzer hafif ağrılar
        hissedilebilir. Şiddetli ve tek taraflı ağrı varsa mutlaka değerlendirilmelidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">9. Baş dönmesi</h3>
      <p>
        Kan basıncındaki hafif düşüş ve genişleyen damar sistemine bağlı olarak özellikle ayağa
        hızlı kalkıldığında baş dönmesi olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">10. Bazal vücut ısısı yüksekliği</h3>
      <p>
        Sabahları yataktan çıkmadan ölçülen bazal ısının 18 günden uzun süre yüksek kalması
        gebeliğin erken işareti olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">11. Ruh hali değişiklikleri</h3>
      <p>
        Hormonal dalgalanmalar, ani üzüntü, sinirlilik veya ağlama isteği gibi duygusal değişimlere
        yol açabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">12. Şişkinlik ve kabızlık</h3>
      <p>
        Progesteron bağırsak hareketlerini yavaşlatır. Karın şişliği ve kabızlık gebeliğin ilk
        haftalarında sıkça görülür.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">13. Vajinal akıntı artışı</h3>
      <p>
        Berrak veya süt beyazı, kokusuz akıntı miktarında artış normaldir. Kaşıntı, yanma veya kötü
        koku varsa enfeksiyon açısından değerlendirilmelidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">14. Baş ağrısı</h3>
      <p>
        Hormonal değişiklikler ve artan kan hacmi baş ağrısını tetikleyebilir. Hidrasyon ve uyku
        düzeni çoğunlukla yardımcı olur.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">15. Aşerme (yeme isteği)</h3>
      <p>
        Belirli yiyeceklere karşı ani, güçlü istek de erken hamilelik belirtilerinden biridir;
        genellikle ilk trimestrda belirgindir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman gebelik testi yapmalı?</h2>
      <p>
        <strong>İdrardan yapılan gebelik testleri</strong>, beklenen adet tarihinden 1 gün sonra
        %99'a yakın doğrulukta sonuç verir. Beklenen adetten önce yapılırsa yanlış negatif çıkma
        ihtimali yüksektir. En güvenilir sonuç için:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Gecikmenin ilk sabahı, günün ilk idrarı ile evde test yapılabilir.</li>
        <li>
          Kesin sonuç için kandan beta-hCG bakılması, hem gebeliği hem de haftasını daha net
          gösterir.
        </li>
        <li>
          Test pozitifse 6–8. haftada ultrason ile gebelik kesesi ve kalp atımı değerlendirilir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Hangi durumlarda hemen doktora başvurulmalı?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Şiddetli, tek taraflı kasık ağrısı</li>
        <li>Parlak kırmızı, adet gibi yoğun kanama</li>
        <li>Bayılma, aşırı halsizlik veya omuz ağrısı (dış gebelik uyarısı olabilir)</li>
        <li>Kesilmeyen kusma, sıvı alamama</li>
        <li>Ateş, ürperme veya kötü kokulu akıntı</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Gebelik takibinde ilk adım</h2>
      <p>
        Gebelik testinin pozitif çıkması sürecin başlangıcıdır. İlk muayenede gebeliğin rahim içinde
        olup olmadığı, tekiz mi çoğul mu olduğu, kalp atımının varlığı ve gebelik haftası
        netleştirilir; folik asit ve gerekli takviyeler planlanır. Adana'da gebelik takibinizi
        güvenli, kanıta dayalı ve size özel şekilde yürütmek için Op. Dr. Sadık Kükrer ile iletişime
        geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function GebelikHaftasiHesaplamaPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Gebelik haftası, hem takibin planlanması hem de anne ve bebek sağlığı açısından pek çok
        kararın temelini oluşturur. Tarama testlerinin zamanı, ultrason ölçümleri, beklenen doğum
        tarihi ve bebeğin gelişim değerlendirmesi hep gebelik haftası üzerinden yapılır. Bu yazıda{" "}
        <strong>gebelik haftası nasıl hesaplanır</strong>, hangi yöntemler güvenilirdir ve doğum
        tarihi neden bir aralık olarak verilir, adım adım anlatıyorum.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Gebelik haftası nedir?</h2>
      <p>
        Tıpta gebelik, gerçek döllenme tarihinden değil,{" "}
        <strong>son adet kanamasının ilk gününden (LMP)</strong> itibaren hesaplanır. Bunun nedeni,
        gerçek yumurtlama gününün her kadında farklı ve çoğu zaman bilinemeyecek olmasıdır. Bu
        yüzden gebelik "resmi olarak" döllenmeden yaklaşık 2 hafta önce başlamış kabul edilir;
        toplam süre 40 haftadır (280 gün).
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Yöntem 1: Son adet tarihine göre hesaplama (Naegele formülü)
      </h2>
      <p>
        En yaygın kullanılan yöntemdir. Düzenli, 28 günlük adet döngüsü olan bir kadında beklenen
        doğum tarihi (EDD) şöyle bulunur:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Son adet tarihine <strong>1 yıl</strong> ekleyin.
        </li>
        <li>
          Bu tarihten <strong>3 ay</strong> geri gidin.
        </li>
        <li>
          Ardından <strong>7 gün</strong> ekleyin.
        </li>
      </ul>
      <p>
        <strong>Örnek:</strong> Son adet 1 Ocak 2026 ise → 1 Ocak 2027 → 1 Ekim 2026 →{" "}
        <strong>8 Ekim 2026</strong>. Beklenen doğum tarihi budur.
      </p>
      <p>
        Bugünün tarihi ile son adet tarihi arasındaki gün sayısını 7'ye bölerek de o an kaç haftalık
        gebe olduğunuzu bulabilirsiniz. Örneğin son adetin üzerinden 63 gün geçmişse, 63 ÷ 7 = 9.
        gebelik haftasındasınız demektir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Yöntem 2: Ultrasondan hesaplama (CRL ölçümü)
      </h2>
      <p>
        Özellikle <strong>ilk trimestrde (6–13. hafta)</strong> yapılan ultrasonda bebeğin baş-popo
        mesafesi (CRL — crown-rump length) ölçülür. Bu ölçüm, gebelik haftasını ± 3–5 gün doğrulukla
        verir ve son adet tarihinden çok daha güvenilirdir.
      </p>
      <p>
        Adet tarihinize göre hesaplanan hafta ile ultrasondaki CRL ölçümü{" "}
        <strong>7 günden fazla</strong> farklıysa, doğum tarihi ultrasona göre güncellenir. Bu, hem
        takip hem de tarama testlerinin (ikili test, NIPT, detaylı ultrason) doğru zamanda
        yapılabilmesi için önemlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Düzensiz adet gören kadınlarda hesaplama
      </h2>
      <p>
        Adet döngüsü 28 günden farklı (örneğin 35 günlük) olan ya da düzensiz adet gören kadınlarda
        Naegele formülü yanıltıcı olabilir; çünkü yumurtlama daha geç bir güne kayar. Bu durumda
        gebelik haftası mutlaka <strong>erken dönem ultrason</strong> ile netleştirilmelidir. İlk
        trimestr ultrasonu, düzensiz adet gören kadınlarda gebelik takibinin temel taşıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        IVF (tüp bebek) gebeliklerinde hesap farkı
      </h2>
      <p>
        Tüp bebek gebeliklerinde döllenme tarihi kesin olarak bilinir. Bu nedenle hesap son adet
        tarihine göre değil, embriyo transfer tarihine göre yapılır:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>3. gün embriyo transferi:</strong> Transfer gününe 263 gün eklenir.
        </li>
        <li>
          <strong>5. gün blastosist transferi:</strong> Transfer gününe 261 gün eklenir.
        </li>
      </ul>
      <p>Bu hesap, doğal gebeliklere göre çok daha kesindir; ± 1–2 gün hata payı vardır.</p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Trimestrlar ve her birinde neler olur?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>1. Trimestr (0–13. hafta):</strong> Organ gelişimi, kalp atımının başlaması,
          tarama testlerinin (ikili test, NIPT) planlanması.
        </li>
        <li>
          <strong>2. Trimestr (14–27. hafta):</strong> Detaylı ultrason (20–22. hafta), bebeğin
          hareketlerinin hissedilmesi, cinsiyet öğrenilebilir.
        </li>
        <li>
          <strong>3. Trimestr (28–40. hafta):</strong> Bebeğin hızlı büyüdüğü dönem; doğum
          hazırlığı, kan basıncı ve büyüme takibi ön plandadır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Beklenen doğum tarihi neden bir aralık olarak verilir?
      </h2>
      <p>
        EDD "kesin bir gün" değil, en olası gün olarak kabul edilir. Gerçekte bebeklerin yalnızca{" "}
        <strong>%5 kadarı</strong> tam olarak bu tarihte doğar. Doğal olarak:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>37–42. haftalar</strong> arası doğumlar zamanında (term) kabul edilir.
        </li>
        <li>
          37. haftadan önce gerçekleşen doğumlara <strong>preterm</strong> (erken doğum) denir.
        </li>
        <li>
          42. haftayı geçen gebelikler <strong>postterm</strong> olarak değerlendirilir ve yakın
          takip gerektirir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan sorular</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Son adet tarihimi hatırlamıyorum, ne olur?
      </h3>
      <p>
        Erken dönemde yapılacak bir ultrason (özellikle 8–12. haftalar arasında) gebelik haftasını
        yüksek doğrulukla belirler. Adet tarihi hatırlanmıyorsa ultrason ölçümü esas alınır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Uygulamaların hesabına güvenebilir miyim?
      </h3>
      <p>
        Telefon uygulamaları Naegele formülünü kullanır ve genel bir fikir verir. Ancak resmi doğum
        tarihi ve tarama testlerinin zamanlaması mutlaka muayene ve ultrason ile doğrulanmalıdır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Ultrasonda haftam geride çıktı, endişelenmeli miyim?
      </h3>
      <p>
        Erken dönemde 5–7 günlük farklar sıktır ve genellikle önemsizdir; adet tarihi hatası veya
        geç yumurtlama sebebiyle olur. Doktorunuz gerekiyorsa 1–2 hafta sonra kontrol ultrasonu ile
        durumu netleştirir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Gebelik takibinizi Adana'da güvenle sürdürün
      </h2>
      <p>
        Doğru hesaplanmış bir gebelik haftası, gebelik boyunca yapılacak tüm tarama testlerinin,
        ultrasonların ve doğum planlamasının temelidir. Adana'da erken dönem ultrason, gebelik
        haftası doğrulama ve kapsamlı gebelik takibi için Op. Dr. Sadık Kükrer ile iletişime
        geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function GebelikTestiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        Gebelik testi, döllenmiş yumurtanın rahim duvarına tutunmasının ardından vücudun salgıladığı{" "}
        <strong>hCG (human chorionic gonadotropin)</strong> hormonunu ölçen basit ama zamanlaması
        kritik bir testtir. Yanlış zamanda yapılan bir test, gebe olduğunuz halde negatif sonuç
        verebilir. Bu rehberde gebelik testinin ne zaman, nasıl ve hangi türde yapılması gerektiğini
        adım adım açıklıyoruz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Gebelik testi ne zaman yapılmalı?
      </h2>
      <p>
        Kısa yanıt: <strong>adet gecikmesinin ilk günü</strong> güvenilir bir idrar testi için en
        uygun zamandır. Adet döngünüz 28 gün ise, son adet tarihinizin üzerinden yaklaşık 28 gün
        geçtiğinde test yapabilirsiniz. Bazı hassas testler adet beklenen tarihten 4–5 gün önce
        sonuç verebilse de, bu erken testlerde yanlış negatif oranı yüksektir.
      </p>
      <p>
        hCG hormonu döllenmeden sonra 6–12. günde kanda tespit edilebilir hale gelir ve{" "}
        <strong>her 48–72 saatte bir ikiye katlanır</strong>. İdrarda tespit edilebilir seviyeye
        ulaşması ise genellikle döllenmeden 10–14 gün sonrasını bulur.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        İdrar testi (evde gebelik testi) nasıl yapılır?
      </h2>
      <p>
        Eczaneden alacağınız evde gebelik testleri, idrardaki hCG hormonunu tespit eder. Doğru sonuç
        için:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Sabah ilk idrarı</strong> kullanın; hCG konsantrasyonu en yüksek bu idrarda olur.
        </li>
        <li>Testten önce aşırı sıvı tüketmeyin, idrarı seyreltebilir.</li>
        <li>
          Kutunun üzerindeki bekleme süresine mutlaka uyun (genellikle 3–5 dakika). Erken okuma
          yanlış negatif, çok geç okuma buharlaşma çizgisi nedeniyle yanlış pozitif sonuç verebilir.
        </li>
        <li>Son kullanma tarihi geçmiş testleri kullanmayın.</li>
      </ul>
      <p>
        Evde gebelik testlerinin doğruluğu doğru zamanlamayla <strong>%97–99</strong> arasındadır.
        Negatif çıkarsa ve adetiniz hâlâ gelmediyse 3–5 gün sonra tekrarlayın.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Kan testi (beta-hCG) ne zaman gerekir?
      </h2>
      <p>Kan testinin iki türü vardır:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Kalitatif beta-hCG:</strong> gebeliği "var / yok" olarak söyler; idrar testine
          benzer ama daha erken pozitifleşir.
        </li>
        <li>
          <strong>Kantitatif beta-hCG:</strong> kandaki hCG miktarını sayısal olarak verir.
          Gebeliğin yaşını tahmin etmede, dış gebelik veya düşük şüphesinde, IVF sonrası takipte ve
          48 saat aralıklı iki ölçüm ile gebeliğin sağlıklı ilerleyip ilerlemediğini değerlendirmede
          kullanılır.
        </li>
      </ul>
      <p>
        Kan testi döllenmeden <strong>6–8 gün</strong> sonra pozitifleşebilir; idrar testinden
        yaklaşık bir hafta daha erken sonuç verir. Adana'da çoğu laboratuvarda sonuç aynı gün çıkar.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">hCG değerleri ne anlama gelir?</h2>
      <p>Referans olarak yaklaşık kan hCG aralıkları:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>3. hafta: 5–50 mIU/mL</li>
        <li>4. hafta: 5–426 mIU/mL</li>
        <li>5. hafta: 18–7.340 mIU/mL</li>
        <li>6. hafta: 1.080–56.500 mIU/mL</li>
        <li>7–8. hafta: 7.650–229.000 mIU/mL</li>
        <li>9–12. hafta: 25.700–288.000 mIU/mL</li>
      </ul>
      <p>
        Değerler kişiden kişiye büyük farklılık gösterir; <strong>tek bir değerden</strong> daha
        önemlisi 48 saat arayla yapılan iki ölçüm arasındaki <strong>artış hızıdır</strong>.
        Sağlıklı erken gebelikte hCG değeri her 48 saatte en az %53 artmalıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Yanlış negatif ve yanlış pozitif sonuçlar
      </h2>
      <h3 className="mt-6 font-display text-xl font-semibold">Yanlış negatif nedenleri</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Test çok erken yapılmış (hCG henüz eşiği geçmemiş)</li>
        <li>İdrar aşırı seyreltilmiş</li>
        <li>Bekleme süresine uyulmamış</li>
        <li>Yumurtlama geç olmuş, gerçek gebelik haftası hesaplananın altında</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">Yanlış pozitif nedenleri</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Çok erken kaybedilen bir gebelik ("kimyasal gebelik")</li>
        <li>hCG içeren kısırlık tedavisi ilaçları (Ovitrelle, Pregnyl vb.)</li>
        <li>Buharlaşma çizgisi (testin çok geç okunması)</li>
        <li>Nadir olarak bazı hipofiz bezi durumları ya da hCG salgılayan tümörler</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Test pozitif çıktı, şimdi ne yapmalıyım?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Folik asit</strong> takviyesine başlayın (günde 400–800 mcg); nöral tüp defekti
          riskini azaltır.
        </li>
        <li>
          Sigara, alkol ve reçetesiz ilaç kullanımını bırakın; ilaçlarınızı doktorunuzla gözden
          geçirin.
        </li>
        <li>
          <strong>6–8. hafta arasında</strong> bir kadın doğum uzmanından randevu alın. İlk
          ultrasonda gebelik kesesi, yolk sac ve fetal kalp atışı değerlendirilir.
        </li>
        <li>Kafein tüketimini günde 200 mg'ın altına düşürün.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Ne zaman hemen doktora başvurmalı?
      </h2>
      <p>Pozitif testle birlikte aşağıdaki belirtiler acil değerlendirme gerektirir:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Şiddetli tek taraflı karın veya omuz ağrısı (dış gebelik şüphesi)</li>
        <li>Yoğun kanama veya pıhtı düşürme</li>
        <li>Baş dönmesi, bayılma hissi</li>
        <li>Yüksek ateş veya kontrol edilemeyen kusma</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan sorular</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Adetim düzensiz, ne zaman test yapmalıyım?
      </h3>
      <p>
        Cinsel ilişkiden en az 3 hafta sonra test yapın. Sonuç negatif ancak şüpheniz varsa 1 hafta
        sonra tekrarlayın veya doğrudan kan testi (beta-hCG) tercih edin.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Doğum kontrol hapı alırken gebelik testi güvenilir mi?
      </h3>
      <p>
        Evet. Doğum kontrol yöntemleri gebelik testinin doğruluğunu etkilemez; test hCG hormonunu
        ölçer, kullandığınız hormonal ilaçlar bu ölçümü değiştirmez.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Emzirirken gebelik testi doğru sonuç verir mi?
      </h3>
      <p>
        Evet, emzirme hCG üretimini etkilemez. Ancak emziren annelerde adet döngüsü düzensiz
        olabileceğinden zamanlama zor olabilir; şüphe halinde kan testi daha nettir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Test soluk bir çizgi gösterdi, gebe miyim?
      </h3>
      <p>
        Belirtilen bekleme süresi içinde görülen <strong>her renkteki</strong> ikinci çizgi pozitif
        kabul edilir. Emin olmak için 48 saat sonra tekrarlayın veya beta-hCG kan testi yaptırın.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Adana'da gebelik testi sonrası takip
      </h2>
      <p>
        Pozitif bir gebelik testi güzel bir başlangıçtır; sağlıklı bir gebelik için düzenli takip
        esastır. Op. Dr. Sadık Kükrer ile erken gebelik ultrasonu, beta-hCG takibi ve kapsamlı
        gebelik izlemi için randevu oluşturabilirsiniz.
      </p>
    </ArticleShell>
  );
}

function GebelikteKanamaPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        Gebelikte kanama, gebelerin yaklaşık <strong>%20–30'unda</strong> ilk üç ayda görülür ve
        çoğu zaman gebeliğin sağlıklı devam etmesini engellemez. Ancak her kanama aynı anlama
        gelmez: bazıları normal fizyolojik bir süreçken, bazıları hızlı müdahale gerektiren acil bir
        durumun habercisi olabilir. Bu rehberde gebeliğin farklı dönemlerinde görülen kanamaların
        nedenlerini, hangisinin bekleyebileceğini ve hangisinin acil olduğunu açıklıyoruz.
      </p>

      <div className="my-8 rounded-2xl border-2 border-[color:var(--rose)] bg-[color:var(--surface)] p-6">
        <p className="font-display text-lg font-semibold text-[color:var(--rose)]">Önemli</p>
        <p className="mt-2">
          Gebelikte görülen <strong>her kanama mutlaka doktora bildirilmelidir</strong>. Bu yazı
          bilgilendirme amaçlıdır; kanamanızın nedenini yalnızca muayene ve ultrason ile doktorunuz
          belirleyebilir.
        </p>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        İlk trimestrda (0–13. hafta) kanama nedenleri
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. İmplantasyon (yerleşim) kanaması
      </h3>
      <p>
        Döllenmiş yumurtanın rahim duvarına tutunması sırasında, döllenmeden yaklaşık 10–14 gün
        sonra görülür. <strong>Açık pembe veya kahverengi lekelenme</strong> şeklindedir, 1–3 gün
        sürer, adet kanaması gibi yoğun olmaz ve pıhtı içermez. Ağrısızdır ya da hafif bir çekilme
        hissi olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        2. Düşük tehdidi (Abortus imminens)
      </h3>
      <p>
        Kanama vardır ancak rahim ağzı kapalıdır ve gebelik hâlâ canlıdır. İlk trimestrdaki
        kanamaların en sık nedenlerinden biridir. Ultrasonda fetal kalp atışı görülüyorsa gebeliğin
        devam etme şansı yüksektir. İstirahat, cinsel ilişkiden kaçınma ve gerekirse progesteron
        desteği önerilebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Erken düşük (Abortus)</h3>
      <p>
        Tanınan gebeliklerin yaklaşık %10–15'i ilk trimestrda düşükle sonuçlanır; çoğunluğu genetik
        anormalliklere bağlıdır. Belirtileri: giderek artan kanama, pıhtı ve doku düşürme, kramp
        tarzı karın ağrısı, gebelik belirtilerinde ani azalma.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Dış (ektopik) gebelik</h3>
      <p>
        Döllenmiş yumurtanın rahim dışında, çoğunlukla tüplerde yerleşmesidir. Tüm gebeliklerin
        yaklaşık <strong>%1–2'sinde</strong> görülür ve
        <strong> hayatı tehdit eden acil bir durumdur</strong>. Belirtileri: tek taraflı şiddetli
        karın ağrısı, kanama, omuz ucuna vuran ağrı, baş dönmesi. 6. haftadan sonra pozitif teste
        rağmen ultrasonda rahim içinde gebelik kesesi görülmemesi güçlü bir işarettir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Molar gebelik (Üzüm gebeliği)</h3>
      <p>
        Plasentanın anormal gelişimidir; nadirdir. Kanama, aşırı bulantı-kusma, çok yüksek hCG
        değerleri ve ultrasonda tipik "kar tanesi" görünümü ile tanı konur. Tedavisi rahim
        boşaltmadır ve sonrasında hCG takibi gerekir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Servikal (rahim ağzı) nedenler</h3>
      <p>
        Gebelikte rahim ağzı damarlanması artar; cinsel ilişki, muayene veya polip nedeniyle temas
        kanaması olabilir. Genellikle az miktarda, açık kırmızı ve ağrısızdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        İkinci ve üçüncü trimestrda (14–40. hafta) kanama nedenleri
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Plasenta previa</h3>
      <p>
        Plasentanın rahim ağzını kısmen veya tamamen kapatmasıdır. Klasik belirtisi{" "}
        <strong>ağrısız, ani başlayan parlak kırmızı kanamadır</strong>. 20. haftadan sonra görülür,
        ultrason ile tanı konur. Kanama sezaryen ile doğum gerektirebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        2. Plasenta dekolmanı (ablatio placentae)
      </h3>
      <p>
        Plasentanın doğumdan önce rahim duvarından erken ayrılmasıdır.
        <strong> Şiddetli karın ağrısı, sert-tahta gibi rahim, koyu renk kanama</strong> ile
        karakterizedir. Bazen kanama dışa yansımaz ancak şok tablosu gelişebilir.{" "}
        <strong>Hayatı tehdit eden acil bir durumdur</strong>; hem anne hem bebek için hızlı doğum
        gerekir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Vasa previa</h3>
      <p>
        Bebeğin damarlarının rahim ağzının üzerinden geçmesidir. Suların gelmesinin ardından görülen
        kanama bebek için son derece tehlikelidir; acil sezaryen gerektirir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Erken doğum eylemi</h3>
      <p>
        37. haftadan önce başlayan düzenli kasılmalar ve rahim ağzı açıklığı ile birlikte "nişan"
        adı verilen pembemsi-mukuslu kanama görülebilir. Değerlendirme ve gerekirse tokolitik tedavi
        için hemen başvurulmalıdır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Nişan (bloody show)</h3>
      <p>
        Doğuma yakın rahim ağzının açılmaya başlamasıyla oluşan mukuslu, pembe veya kahverengi
        akıntıdır. Miadında (37+ hafta) fizyolojiktir ve doğumun yaklaştığının işaretidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Hemen hastaneye başvurmayı gerektiren durumlar
      </h2>
      <div className="rounded-2xl bg-[color:var(--surface)] p-6">
        <ul className="list-disc space-y-2 pl-6">
          <li>Ped/regl pedi 1 saatte tam ıslanacak kadar yoğun kanama</li>
          <li>Ceviz ve daha büyük boyutta pıhtı düşürme</li>
          <li>Şiddetli, sürekli veya tek taraflı karın/pelvis ağrısı</li>
          <li>Sert, tahta gibi rahim ve karın</li>
          <li>Baş dönmesi, bayılma, göz kararması, çarpıntı</li>
          <li>Omuz ucuna vuran ağrı (iç kanama işareti olabilir)</li>
          <li>37. haftadan önce sulu/pıhtılı kanama + düzenli kasılmalar</li>
          <li>Bebek hareketlerinde belirgin azalma</li>
          <li>Ateş (38°C üzeri) ile birlikte kanama</li>
          <li>Rh negatif kan grubuna sahip olup herhangi bir kanama yaşamak</li>
        </ul>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Doktora giderken yapılması ve yapılmaması gerekenler
      </h2>
      <h3 className="mt-6 font-display text-xl font-semibold">Yapın</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Ped kullanın; miktarı tahmin etmek için ped değişim sıklığını not edin.</li>
        <li>Düşürdüğünüz doku/pıhtı varsa temiz bir kaba koyup hastaneye götürün.</li>
        <li>
          Ne zaman başladığını, rengini (parlak kırmızı, koyu, kahverengi) ve varsa ağrı
          özelliklerini not edin.
        </li>
        <li>Yalnız iseniz bir yakınınıza haber verin; ulaşım için hazırlıklı olun.</li>
      </ul>
      <h3 className="mt-6 font-display text-xl font-semibold">Yapmayın</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Tampon kullanmayın; sadece ped kullanın.</li>
        <li>Cinsel ilişkide bulunmayın.</li>
        <li>Kendi kendinize vajinal muayene yapmayın veya duş almaya çalışmayın.</li>
        <li>Doktor önermeden ağrı kesici, aspirin veya kanı sulandıran ilaç almayın.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Rh uyuşmazlığı ve kanama</h2>
      <p>
        Kan grubu Rh (-) olan gebelerde <strong>her kanama epizodu</strong>, bebeğe karşı antikor
        gelişimini önlemek için <strong>Anti-D immünoglobulin</strong> ihtiyacı açısından
        değerlendirilmelidir. Genellikle 72 saat içinde yapılan enjeksiyonla sonraki gebeliklerin
        korunması sağlanır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan sorular</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Kahverengi lekelenme tehlikeli mi?
      </h3>
      <p>
        Kahverengi renk kanamanın eski olduğunu (birkaç gündür yavaşça aktığını) gösterir;
        genellikle taze kanamadan daha az endişe vericidir. Yine de gebelikte her lekelenme doktora
        bildirilmelidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Cinsel ilişki sonrası kanadım, sorun mu?
      </h3>
      <p>
        Gebelikte rahim ağzının damarlanması arttığı için temas kanaması sık görülür ve çoğunlukla
        tehlikesizdir. Ancak kanama miktarı azdan fazlaya değişebileceği ve altta başka bir neden
        bulunabileceği için ilk kez yaşanan bir kanama mutlaka değerlendirilmelidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Kanama gördüm, mutlaka düşük mü yapacağım?
      </h3>
      <p>
        Hayır. İlk trimestrda kanama yaşayan gebelerin büyük çoğunluğu sağlıklı bebekler dünyaya
        getirir. Ultrasonda fetal kalp atışı görülmesi ve rahim ağzının kapalı olması durumunda
        gebeliğin devam etme olasılığı oldukça yüksektir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Kanama sırasında istirahat yardımcı olur mu?
      </h3>
      <p>
        Yatak istirahatinin düşüğü önlediğine dair kesin kanıt yoktur; ancak aktif kanama sırasında
        ağır fiziksel aktivite, yolculuk ve cinsel ilişkiden kaçınmak makul bir yaklaşımdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Adana'da gebelik kanaması değerlendirmesi
      </h2>
      <p>
        Gebelikte yaşanan her kanama sizin için ve bebeğiniz için ciddi endişe kaynağı olabilir.
        Erken değerlendirme, hem nedenin doğru saptanması hem de gerektiğinde uygun tedavinin
        zamanında başlaması açısından kritiktir. Adana'da erken gebelik ultrasonu, kanama
        değerlendirmesi, Anti-D uygulaması ve kapsamlı gebelik takibi için Op. Dr. Sadık Kükrer ile
        iletişime geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function GebelikteBeslenmePost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        Gebelikte beslenme, sadece "iki kişilik yemek yemek" değildir; asıl mesele
        <strong> iki kişilik besin çeşitliliği ve kalitesi</strong> sağlamaktır. Doğru beslenme;
        bebeğin sağlıklı gelişimini, annenin enerjisini, gebeliğe bağlı kabızlık, reflü ve anemi
        gibi rahatsızlıkların önlenmesini ve doğum sonrası toparlanmayı doğrudan etkiler.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Gebelikte ne kadar ekstra kalori gerekir?
      </h2>
      <p>Yaygın inanışın aksine gebelikte kalori ihtiyacı ilk aylarda hemen artmaz. Genel öneri:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>1. trimester (0–13. hafta):</strong> ek kalori gerekmez. Bulantı nedeniyle kilo
          artışı sıklıkla 0–2 kg civarındadır.
        </li>
        <li>
          <strong>2. trimester (14–27. hafta):</strong> günde yaklaşık <strong>+340 kcal</strong>.
        </li>
        <li>
          <strong>3. trimester (28–40. hafta):</strong> günde yaklaşık <strong>+450 kcal</strong>.
        </li>
      </ul>
      <p>
        Beklenen toplam kilo alımı, gebelik öncesi vücut kitle indeksine (BMI) göre değişir: normal
        kilolu bir gebede <strong>11–16 kg</strong>, fazla kilolularda 7–11 kg, obez gebelerde 5–9
        kg makul aralıktır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Her gün mutlaka bulunması gereken besin grupları
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Protein</h3>
      <p>
        Günlük ihtiyaç yaklaşık <strong>1.1 g/kg</strong>'dır. Yumurta, tavuk, hindi, iyi pişmiş
        kırmızı et, balık, kuru baklagiller (mercimek, nohut, fasulye), yoğurt ve peynir dönüşümlü
        tüketilmelidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Kompleks karbonhidratlar</h3>
      <p>
        Tam tahıllı ekmek, bulgur, yulaf, tam buğday makarna, esmer pirinç, kinoa. Beyaz un ve şeker
        yerine kompleks karbonhidrat tercih etmek, gebelik diyabeti riskini azaltır ve enerjiyi
        dengeler.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Sebze ve meyve</h3>
      <p>
        Günde <strong>en az 5 porsiyon</strong> (yaklaşık 400–500 g). Koyu yeşil yapraklılar
        (ıspanak, semizotu, roka), turuncu sebzeler (havuç, balkabağı), narenciye, çilek, kivi, taze
        meyveler öncelikli olmalıdır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Süt ve süt ürünleri</h3>
      <p>
        Günde <strong>3–4 porsiyon</strong> (süt, yoğurt, kefir, peynir). Kalsiyum ihtiyacı 1000
        mg/gündür ve bebeğin kemik gelişimi için şarttır. Peynirlerin{" "}
        <strong>pastörize sütten</strong> yapılmış olmasına dikkat edin.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Sağlıklı yağlar ve omega-3</h3>
      <p>
        Zeytinyağı, ceviz, badem, avokado, keten tohumu. Haftada 2 porsiyon düşük cıvalı balık
        (somon, uskumru, sardalya, hamsi) beynin ve gözün gelişimi için önemli <strong>DHA</strong>{" "}
        sağlar.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kritik vitamin ve mineraller</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Folik asit (400–800 mcg/gün):</strong> nöral tüp defektlerini önler. İdeal olarak
          gebelikten <strong>en az 1 ay önce</strong> başlanmalı ve ilk 12 haftaya kadar
          sürdürülmelidir.
        </li>
        <li>
          <strong>Demir (27 mg/gün):</strong> anemi ve erken doğumu önler. Kırmızı et, karaciğer,
          kuru baklagiller, pekmez, kuru meyveler. C vitamini (portakal, biber, maydanoz) demir
          emilimini artırır; <strong>çay ve kahve emilimi azaltır</strong>, yemek aralarında için.
        </li>
        <li>
          <strong>Kalsiyum (1000 mg/gün):</strong> süt ürünleri, susam, tahin, brokoli.
        </li>
        <li>
          <strong>D vitamini (600–2000 IU/gün):</strong> güneş ışığı yeterli olmadığında takviye
          önerilir; kalsiyum emilimi ve kemik gelişimi için gereklidir.
        </li>
        <li>
          <strong>İyot (220 mcg/gün):</strong> iyotlu tuz kullanmak yeterli olur; tiroid fonksiyonu
          ve beyin gelişimi için kritiktir.
        </li>
        <li>
          <strong>Kolin (450 mg/gün):</strong> yumurta sarısı, et, süt.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Kesinlikle kaçınılması gereken yiyecek ve içecekler
      </h2>
      <div className="rounded-2xl border border-[color:var(--rose)] bg-[color:var(--surface)] p-6">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Çiğ veya az pişmiş et, tavuk, balık</strong> (sushi, tartar, az pişmiş biftek) —
            toksoplazma ve listeria riski.
          </li>
          <li>
            <strong>Pastörize edilmemiş süt ve peynirler</strong> (taze köy peyniri, taze lor).
            Etikette "pastörize" yazdığından emin olun.
          </li>
          <li>
            <strong>Çiğ yumurta içeren ürünler</strong> (ev yapımı mayonez, tiramisu, çiğ hamur,
            dondurmalarda çiğ yumurta).
          </li>
          <li>
            <strong>Yüksek cıvalı balıklar:</strong> köpekbalığı, kılıç balığı, kral uskumrusu, ton
            balığı (sınırlı miktarda konserve olabilir).
          </li>
          <li>
            <strong>Salam, sosis, jambon, sucuk gibi işlenmiş şarküteri ürünleri</strong> — listeria
            riski nedeniyle yenecekse iyice ısıtılmalıdır.
          </li>
          <li>
            <strong>Alkol</strong> — hiçbir miktarı güvenli değildir.
          </li>
          <li>
            <strong>Çiğ filizler</strong> (yonca, brokoli filizi vb.) — bakteri riski.
          </li>
          <li>
            <strong>Bitkisel çaylar</strong> (adaçayı, biberiye, papatya bol tüketilirse); nane ve
            ıhlamur ölçülü tüketilebilir ancak yeni bir bitki çayına başlamadan doktorunuza danışın.
          </li>
        </ul>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ölçülü tüketilmesi gerekenler</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Kafein:</strong> günde <strong>200 mg</strong> altında tutun (yaklaşık 1 fincan
          filtre kahve veya 2 fincan Türk kahvesi). Kola, enerji içecekleri, çay ve çikolatanın
          kafeinini de hesaba katın.
        </li>
        <li>
          <strong>Şeker ve tatlılar:</strong> gebelik diyabeti riski nedeniyle sınırlı; şekerli
          içecekler yerine su, ayran, süt tercih edilmeli.
        </li>
        <li>
          <strong>Tuz:</strong> normal miktarda iyotlu tuz kullanın; aşırı tuz ödem ve tansiyon
          riskini artırır. Hazır çorba, konserve, cips gibi gizli tuz kaynaklarına dikkat.
        </li>
        <li>
          <strong>A vitamini yüksek karaciğer:</strong> nadiren ve az miktarda; çok yüksek A
          vitamini bebeğe zararlı olabilir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Su tüketimi</h2>
      <p>
        Günde <strong>2–2.5 litre su</strong> hedefleyin. Yeterli su; kabızlığı, idrar yolu
        enfeksiyonlarını, erken doğum eylemini ve baş ağrısını azaltır. İdrarınızın açık sarı renkte
        olması iyi bir göstergedir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Trimester bazlı pratik öneriler</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Trimester (bulantı ve iştahsızlık dönemi)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Sık ve az miktarda öğün yiyin; mide asla tam boş olmasın.</li>
        <li>Yatağa kalkmadan önce kuru bisküvi, tuzsuz kraker veya badem.</li>
        <li>Kokulu yemeklerden uzak durun; soğuk servisler daha kolay tolere edilir.</li>
        <li>Zencefil (taze veya çay) bulantıya yardımcı olabilir.</li>
        <li>B6 vitamini takviyesi hakkında doktorunuza danışın.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        2. Trimester (enerji ve iştah yerine geliyor)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Protein ve demir kaynaklarını artırın.</li>
        <li>Kalsiyum için günlük süt/yoğurt/peynir düzenli tüketilmeli.</li>
        <li>50 gr OGTT (şeker yükleme) testi 24–28. hafta arasında yapılır.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Trimester (reflü ve doygunluk dönemi)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Küçük porsiyonlarla günde 5–6 öğün.</li>
        <li>Yemekten hemen sonra uzanmayın; en az 1–2 saat bekleyin.</li>
        <li>Baharatlı, çok yağlı ve asitli yiyecekleri kısıtlayın.</li>
        <li>Lif ve su tüketimini artırarak kabızlıkla mücadele edin.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Örnek bir günlük menü</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Kahvaltı:</strong> haşlanmış yumurta, beyaz peynir, zeytin, domates, salatalık,
          ceviz, tam buğday ekmek, ıhlamur.
        </li>
        <li>
          <strong>Ara öğün:</strong> 1 meyve + 5–6 badem/ceviz.
        </li>
        <li>
          <strong>Öğle:</strong> mercimek çorbası, ızgara tavuk, bulgur pilavı, mevsim salatası
          (limon-zeytinyağı).
        </li>
        <li>
          <strong>Ara öğün:</strong> yoğurt + 1 tatlı kaşığı pekmez veya taze meyve.
        </li>
        <li>
          <strong>Akşam:</strong> fırında somon, buharda brokoli-havuç, esmer pirinç veya kinoa,
          cacık.
        </li>
        <li>
          <strong>Gece:</strong> 1 bardak süt veya küçük bir muz.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan sorular</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Bal, pekmez ve tahin gebelikte güvenli mi?
      </h3>
      <p>
        Evet. Pastörize ya da geleneksel bal, pekmez ve tahin gebelikte tüketilebilir; özellikle
        pekmez ve tahin demir açısından değerlidir. Uyarı sadece{" "}
        <strong>1 yaş altı bebekler</strong> için geçerlidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Vejetaryen/vegan beslenebilir miyim?
      </h3>
      <p>
        Evet, ancak protein, B12, demir, çinko, omega-3 (DHA) ve D vitamini takviyeleri açısından
        mutlaka doktorunuzla ve diyetisyenle plan yapmalısınız.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Ton balığı yiyebilir miyim?</h3>
      <p>
        Konserve ışık ton balığı (light tuna) haftada 1 küçük porsiyona kadar genellikle güvenli
        kabul edilir. Beyaz ton balığından (albacore) kaçınmak daha doğrudur; cıva içeriği daha
        yüksektir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Kilo alımım az/çok, ne yapmalıyım?
      </h3>
      <p>
        Kilo alımı sadece rakamla değil, bebeğin büyüme eğrisiyle birlikte değerlendirilir.
        Kontrolde ultrason ve fetal ölçümlerle her şey yolunda gidiyorsa endişe etmenize gerek
        yoktur. Aşırı ya da yetersiz kilo alımı durumunda doktorunuz bir diyetisyene
        yönlendirebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Doğum sonrası hemen diyete başlayabilir miyim?
      </h3>
      <p>
        Özellikle emziriyorsanız kısıtlı diyetler doğru değildir. Emzirme döneminde günlük
        ihtiyacınız gebelikten bile fazladır (+500 kcal). Kademeli, dengeli beslenme ile 6–12 ay
        içinde sağlıklı kiloya dönüş hedeflenmelidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Adana'da gebelik takibi ve beslenme danışmanlığı
      </h2>
      <p>
        Her gebeliğin ihtiyacı farklıdır: gebelik diyabeti, anemi, tiroid sorunları veya çoğul
        gebelik varlığında beslenme planı kişiselleştirilir. Op. Dr. Sadık Kükrer ile kapsamlı
        gebelik takibi, laboratuvar değerlendirmesi ve gerektiğinde diyetisyen yönlendirmesi için
        Adana'daki muayenehaneye başvurabilirsiniz.
      </p>
    </ArticleShell>
  );
}

function YumurtalikKistiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        Yumurtalık kisti, yumurtalıkların (over) içinde ya da yüzeyinde oluşan sıvı veya karışık
        içerikli keseciklerdir. Üreme çağındaki kadınların büyük çoğunluğu hayatının bir döneminde
        en az bir kez yumurtalık kistiyle karşılaşır.{" "}
        <strong>Kistlerin büyük bölümü iyi huyludur</strong> ve kendiliğinden kaybolur; ancak bir
        kısmı takip veya cerrahi tedavi gerektirir. Bu rehberde kist türlerini, belirtileri ve
        tedavi kararının nasıl verildiğini açıklıyoruz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Yumurtalık kisti türleri</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Fonksiyonel (basit) kistler</h3>
      <p>
        En sık görülen türdür ve <strong>adet döngüsünün bir parçası</strong>
        olarak oluşur. Genellikle 5 cm altında, tek gözlü ve sıvı içeriklidir. Çoğu 1–3 adet döngüsü
        içinde kendiliğinden kaybolur.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Folikül kisti:</strong> yumurtlama olmadığında folikülün büyümeye devam etmesiyle
          oluşur.
        </li>
        <li>
          <strong>Korpus luteum kisti:</strong> yumurtlama sonrası oluşan korpus luteumun içine sıvı
          dolmasıyla ortaya çıkar; erken gebelikte de görülebilir.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Endometrioma (çikolata kisti)</h3>
      <p>
        Endometriozis hastalığında rahim iç zarına benzer dokunun yumurtalıkta yerleşmesiyle oluşur.
        Kist içeriği eski kan olduğu için koyu kahve renklidir. Kronik pelvis ağrısı, ağrılı adet,
        ağrılı cinsel ilişki ve
        <strong> kısırlıkla</strong> ilişkilidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Dermoid kist (teratom)</h3>
      <p>
        Doğuştan gelen embriyonik hücrelerden gelişir; içinde saç, diş, yağ, deri dokusu
        bulunabilir. Genellikle iyi huyludur ancak büyüdüğünde{" "}
        <strong>rüptür veya over torsiyonuna</strong> yol açabileceği için cerrahi olarak
        çıkarılması önerilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        4. Kistadenomlar (seröz / müsinöz)
      </h3>
      <p>
        Yumurtalık yüzeyinin dış dokusundan gelişen, bazen çok büyük boyutlara ulaşabilen iyi huylu
        tümörlerdir. Genellikle cerrahi olarak çıkarılır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Polikistik over sendromu (PKOS)
      </h3>
      <p>
        Aslında klasik anlamda "kist" değildir. Ultrasonda her iki yumurtalıkta çok sayıda küçük
        folikül (2–9 mm) görülür. PKOS; adet düzensizliği, tüylenme, akne, kilo alımı ve insülin
        direnciyle birlikte ortaya çıkan hormonal bir sendromdur ve{" "}
        <strong>ayrı bir tedavi yaklaşımı</strong> gerektirir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Kompleks / şüpheli kistler</h3>
      <p>
        Ultrasonda katı bileşen, kalın septalar, düzensiz duvarlar, kanlanma artışı veya karın içi
        sıvı varlığında kist "kompleks" olarak sınıflanır. Bu tür kistler{" "}
        <strong>kanser açısından</strong> daha ayrıntılı değerlendirme (MR, tümör belirteçleri,
        jinekolojik onkoloji konsültasyonu) gerektirir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Belirtiler</h2>
      <p>
        Kistlerin çoğu <strong>belirti vermez</strong> ve rutin ultrasonda tesadüfen fark edilir.
        Belirti verdiğinde en sık:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Tek taraflı, künt veya batıcı kasık/pelvis ağrısı</li>
        <li>Adet düzensizliği, adet öncesi lekelenme</li>
        <li>Ağrılı adet (dismenore) ve ağrılı cinsel ilişki (özellikle endometrioma)</li>
        <li>Karında şişkinlik, dolgunluk hissi</li>
        <li>Sık idrara çıkma veya bağırsak baskı hissi (büyük kistlerde)</li>
        <li>Kısırlık (özellikle endometrioma ve PKOS)</li>
      </ul>

      <div className="my-8 rounded-2xl border-2 border-[color:var(--rose)] bg-[color:var(--surface)] p-6">
        <p className="font-display text-lg font-semibold text-[color:var(--rose)]">
          Acil belirtiler
        </p>
        <p className="mt-2">
          Ani başlayan <strong>şiddetli tek taraflı karın ağrısı</strong>, bulantı-kusma, ateş, baş
          dönmesi veya bayılma hissi <strong>kist rüptürü</strong> veya{" "}
          <strong>over torsiyonu</strong> işareti olabilir. Bu durumda hemen acil servise başvurun;
          her iki durum da acil cerrahi gerektirebilir.
        </p>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı nasıl konur?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Transvajinal ultrason:</strong> altın standarttır; kistin boyutu, yapısı, tek/çok
          gözlü olması ve kanlanma özellikleri değerlendirilir.
        </li>
        <li>
          <strong>Tümör belirteçleri:</strong> CA-125, HE4, CEA, AFP, hCG, LDH — yaşa ve kist tipine
          göre seçilir. CA-125 endometrioma ve iyi huylu bazı durumlarda da yükselebileceği için tek
          başına tanı koydurmaz.
        </li>
        <li>
          <strong>MR:</strong> ultrasonun yetersiz kaldığı kompleks kistlerde yapıyı daha net
          gösterir.
        </li>
        <li>
          <strong>Hormon testleri:</strong> özellikle PKOS ve düzensiz adet şüphesinde.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Ne zaman sadece takip yeterlidir?
      </h2>
      <p>Aşağıdaki özelliklerdeki kistler genellikle takip edilir:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>5 cm altı, basit görünümlü fonksiyonel kistler</li>
        <li>
          Menopoz öncesi kadınlarda, adet sonrası kontrol ultrasonunda küçülen veya kaybolan kistler
        </li>
        <li>Tümör belirteçleri normal, ağrı yok, kanlanma artışı yok</li>
      </ul>
      <p>
        Takip genellikle 6–12 hafta arayla ultrason ile yapılır. Fonksiyonel kistlerin büyük bölümü
        bu sürede kaybolur.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. İlaç tedavisi</h3>
      <p>
        <strong>Doğum kontrol hapları</strong> yeni fonksiyonel kist oluşumunu engelleyebilir; ancak
        mevcut bir kisti "eritmez". Endometriozis ve PKOS'ta hormonal tedaviler ağrı ve döngü düzeni
        sağlamak için kullanılır. Ağrı için basit ağrı kesiciler (parasetamol, gebelik dışında
        NSAİİ) yardımcı olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Cerrahi tedavi</h3>
      <p>
        Modern jinekolojik cerrahide standart yaklaşım <strong>laparoskopidir</strong> (kapalı
        ameliyat). Küçük kesilerle yapılır, iyileşme hızlıdır ve estetik olarak avantajlıdır.
        Robotik cerrahi seçenek olarak sunulabilir. Açık cerrahi (laparotomi) yalnızca çok büyük
        veya kanser şüpheli kistlerde gerekir.
      </p>
      <p>Cerrahi seçenekler:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Kistektomi:</strong> yumurtalık korunarak sadece kistin çıkarılması — üreme çağı
          için tercih edilir.
        </li>
        <li>
          <strong>Ooforektomi:</strong> yumurtalığın alınması — menopoz sonrası veya kanser
          şüphesinde.
        </li>
        <li>
          <strong>Salpingooforektomi:</strong> yumurtalık ve tüpün birlikte alınması.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman ameliyat gerekir?</h2>
      <div className="rounded-2xl bg-[color:var(--surface)] p-6">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Kist boyutu 5–8 cm ve üzeri</strong> ve takipte küçülmüyor
          </li>
          <li>Kompleks (katı bileşenli, çok gözlü, düzensiz) yapıdaki kistler</li>
          <li>Yüksek tümör belirteçleri, MR/ultrasonda kanser şüphesi</li>
          <li>Kist rüptürü, over torsiyonu veya iç kanama</li>
          <li>Menopoz sonrası saptanan her yeni yumurtalık kisti</li>
          <li>Kısırlığa yol açan endometrioma (özellikle IVF planlanan hastalarda)</li>
          <li>Şiddetli, tedaviye yanıt vermeyen kronik pelvis ağrısı</li>
          <li>Büyüyen dermoid kistler (torsiyon riski)</li>
        </ul>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">Yumurtalık kisti kanser mi?</h2>
      <p>
        Üreme çağındaki kadınlarda saptanan kistlerin{" "}
        <strong>%90'ından fazlası iyi huyludur</strong>. Yumurtalık kanseri riski özellikle{" "}
        <strong>menopoz sonrası</strong>, kompleks yapıda, hızlı büyüyen ve yüksek CA-125
        değerlerine sahip kistlerde artar. Aile öyküsünde yumurtalık veya meme kanseri olan
        kadınlarda BRCA1/BRCA2 gen mutasyon değerlendirmesi gerekebilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kistler tekrar eder mi?</h2>
      <p>
        Fonksiyonel kistlerin tekrarlaması normaldir ve genellikle önemli değildir. Endometriomanın
        5 yıl içinde tekrarlama oranı yaklaşık <strong>%20–30</strong>'dur; bu nedenle ameliyat
        sonrası hormonal tedavi ve düzenli takip önerilir. Dermoid kistler nadiren aynı yerde, ancak
        yaklaşık %10 hastada karşı yumurtalıkta da görülebilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan sorular</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Yumurtalık kisti gebe kalmayı engeller mi?
      </h3>
      <p>
        Küçük fonksiyonel kistlerin çoğu gebeliğe engel değildir. Ancak endometrioma, büyük dermoid
        kistler ve PKOS gebeliği güçleştirebilir. Kısırlık şüphesinde ayrıntılı değerlendirme
        yapılmalıdır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Kistektomi ameliyatı yumurtalık rezervini azaltır mı?
      </h3>
      <p>
        Cerrahi teknik önemlidir. Deneyimli ellerde koter kullanımı sınırlı tutularak yapılan
        kistektomide rezerv kaybı minimuma indirilebilir. Endometrioma ameliyatlarında hafif bir AMH
        (yumurtalık rezervi) düşüşü görülebilir; bu nedenle çocuk isteği olan hastalarda karar
        bireysel yapılmalıdır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Doğum kontrol hapı kisti eritir mi?
      </h3>
      <p>
        Mevcut kisti eritmez ancak yeni fonksiyonel kist gelişimini engelleyebilir. Mevcut
        fonksiyonel kist zaten çoğu zaman kendiliğinden kaybolur.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Ameliyat sonrası iyileşme ne kadar sürer?
      </h3>
      <p>
        Laparoskopik kistektomi sonrası hastalar genellikle 1 gün hastanede kalır; günlük
        aktivitelere 1 hafta, ağır fiziksel aktiviteye 2–4 hafta içinde dönülür. Açık cerrahide
        iyileşme 4–6 haftaya uzayabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Adana'da yumurtalık kisti değerlendirmesi ve laparoskopik cerrahi
      </h2>
      <p>
        Yumurtalık kisti tespit edildiğinde en önemlisi, <strong>doğru sınıflandırma</strong> ve{" "}
        <strong>bireye özel karar</strong> vermektir: aynı kist, farklı yaş, üreme planı ve semptom
        profilinde tamamen farklı bir yaklaşım gerektirebilir. Adana'da yumurtalık kisti
        değerlendirmesi, laparoskopik jinekolojik cerrahi ve endometriozis tedavisi için Op. Dr.
        Sadık Kükrer'e başvurabilirsiniz.
      </p>
    </ArticleShell>
  );
}

function VajinalAkintiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Vajinal akıntı</strong>, kadın üreme sisteminin doğal bir parçasıdır ve büyük
        çoğunluğu tamamen normaldir. Vajina ve serviks (rahim ağzı), günlük olarak salgı üreterek
        kendini temizler, nem dengesi sağlar ve enfeksiyonlara karşı koruyucu bir bariyer oluşturur.
        Ancak akıntının rengi, kokusu, miktarı ya da kıvamı değişirse; kaşıntı, yanma, ağrı gibi
        şikayetler eşlik ediyorsa altta yatan bir{" "}
        <strong>enfeksiyon ya da hormonal bozukluk</strong> söz konusu olabilir.
      </p>

      <p>
        Bu yazıda; normal vajinal akıntının özelliklerini, hangi durumların doktora başvurmayı
        gerektirdiğini, en sık görülen enfeksiyon nedenlerini (mantar, bakteriyel vajinozis,
        trikomoniyaz), tanı yöntemlerini ve tedavi seçeneklerini ayrıntılı biçimde ele alacağız.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Normal Vajinal Akıntı Nasıl Olur?
      </h2>
      <p>Sağlıklı bir vajinal akıntının şu özellikleri vardır:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Renk:</strong> Şeffaf, süt beyazı ya da açık krem rengi; kuruduğunda hafif sarımsı
          görünebilir.
        </li>
        <li>
          <strong>Koku:</strong> Kokusuz ya da çok hafif, rahatsız edici olmayan bir koku.
        </li>
        <li>
          <strong>Kıvam:</strong> Adet döngüsüne göre değişir; ovulasyon döneminde yumurta akı gibi
          berrak ve esnek, diğer zamanlarda daha yoğun ve kremsi olabilir.
        </li>
        <li>
          <strong>Miktar:</strong> Kişiden kişiye değişir; günde 1–4 mL kadar akıntı normaldir.
        </li>
        <li>
          <strong>Şikayet yok:</strong> Kaşıntı, yanma, kızarıklık, ağrı ya da ilişki sırasında
          rahatsızlığa yol açmaz.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Akıntı Miktarını Etkileyen Doğal Faktörler
      </h2>
      <p>Aşağıdaki durumlarda akıntının artması normaldir:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ovulasyon dönemi:</strong> Adet döngüsünün ortasında akıntı artar, berraklaşır ve
          yumurta akı kıvamına dönüşür.
        </li>
        <li>
          <strong>Cinsel uyarılma:</strong> Vajinal kayganlaştırıcı salgı artışına bağlı olarak
          akıntı fazlalaşabilir.
        </li>
        <li>
          <strong>Gebelik:</strong> Östrojen ve kan akımı arttığı için akıntı miktarı belirgin
          biçimde artar (<em>lökore</em>). Renk beyaz, kokusuz ve kaşıntısız ise normaldir.
        </li>
        <li>
          <strong>Doğum kontrol hapları:</strong> Hormonal değişimler akıntı miktarını
          etkileyebilir.
        </li>
        <li>
          <strong>Stres ve yorgunluk:</strong> Hormon dengesi üzerinden akıntı düzenini
          etkileyebilir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Anormal Akıntı: Ne Zaman Endişelenmeli?
      </h2>
      <p>Aşağıdaki bulgular varsa akıntı normal değildir ve doktor değerlendirmesi gerekir:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Sarı, yeşil, gri ya da köpüklü akıntı</li>
        <li>Kötü, balık gibi ya da mayalı koku</li>
        <li>Kesilmiş süt görünümünde, peynirimsi akıntı</li>
        <li>Kaşıntı, yanma, kızarıklık ya da şişlik</li>
        <li>İdrar yaparken ya da cinsel ilişki sırasında ağrı</li>
        <li>Kanlı ya da pembe akıntı (adet dışında)</li>
        <li>Alt karın ağrısı ya da ateş eşlik etmesi</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">En Sık Görülen Nedenler</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Mantar Enfeksiyonu (Vajinal Kandidiyaz)
      </h3>
      <p>
        Vajinal mantar enfeksiyonu, en sık <em>Candida albicans</em> mayasının aşırı çoğalmasıyla
        oluşur. Kadınların yaklaşık %75'i yaşamı boyunca en az bir kez mantar enfeksiyonu geçirir.
      </p>
      <p>
        <strong>Tipik bulgular:</strong>
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Beyaz, kesilmiş süt / lor peyniri kıvamında akıntı</li>
        <li>Belirgin kokusuzluk (ya da hafif mayalı koku)</li>
        <li>Yoğun kaşıntı, yanma ve kızarıklık</li>
        <li>İdrar yaparken ve cinsel ilişkide yanma</li>
      </ul>
      <p>
        <strong>Risk faktörleri:</strong> Antibiyotik kullanımı, gebelik, diyabet, bağışıklık
        baskılanması, sıkı ve nefes almayan iç çamaşırı, ıslak mayo/spor kıyafeti ile uzun süre
        kalmak.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Bakteriyel Vajinozis (BV)</h3>
      <p>
        Bakteriyel vajinozis, vajinadaki doğal bakteri dengesinin bozulup
        <em> laktobasillerin</em> azalması ve anaerobik bakterilerin çoğalmasıyla oluşur. Klasik bir
        cinsel yolla bulaşan hastalık değildir, ancak cinsel aktiviteyle sıklığı artar.
      </p>
      <p>
        <strong>Tipik bulgular:</strong>
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Gri-beyaz, ince ve sulu akıntı</li>
        <li>Belirgin balık kokusu (özellikle ilişki sonrası ve adet döneminde)</li>
        <li>Kaşıntı ve yanma genellikle hafiftir ya da yoktur</li>
      </ul>
      <p>
        BV, tedavi edilmediğinde gebelikte erken doğum, düşük doğum ağırlığı ve pelvik enflamatuar
        hastalık riskini artırabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Trikomoniyaz</h3>
      <p>
        <em>Trichomonas vaginalis</em> adlı parazitin neden olduğu, cinsel yolla bulaşan bir
        enfeksiyondur.
      </p>
      <p>
        <strong>Tipik bulgular:</strong>
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Sarı-yeşil, köpüklü ve bol miktarda akıntı</li>
        <li>Kötü, kokulu akıntı</li>
        <li>Yoğun kaşıntı, yanma, kızarıklık</li>
        <li>İdrar yaparken ağrı, ilişki sırasında rahatsızlık</li>
        <li>Bazı kadınlarda hiç belirti vermeyebilir</li>
      </ul>
      <p>Partnerin de eş zamanlı tedavi edilmesi zorunludur.</p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Servisit (Rahim Ağzı İltihabı)</h3>
      <p>
        Klamidya ve bel soğukluğu (<em>gonore</em>) gibi cinsel yolla bulaşan enfeksiyonlar rahim
        ağzı iltihabına yol açabilir. Sarı-yeşil, kötü kokulu, bazen kanla karışık akıntı, ilişki
        sonrası lekelenme ve alt karın ağrısı görülebilir. Tedavi edilmezse tüplere yayılıp
        <strong> tüp tıkanıklığı ve kısırlık</strong> nedeni olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Atrofik Vajinit (Menopoz Sonrası)
      </h3>
      <p>
        Menopozda östrojen düşüşüne bağlı vajinal mukoza incelir, kuruluk ve hassasiyet artar. Sulu,
        sarı veya kanla karışık akıntı, yanma ve ilişki sırasında ağrı görülebilir. Lokal östrojen
        tedavisi ile büyük oranda düzelir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Yabancı Cisim</h3>
      <p>
        Vajinada unutulmuş tampon, prezervatif parçası ya da başka bir yabancı cisim; kötü kokulu,
        kahverengimsi-yeşilimsi bir akıntıya neden olur. Muayene ile hızlıca teşhis edilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Renk ve Kokuya Göre Kısa Rehber</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Berrak / beyaz, kokusuz:</strong> Genellikle normal.
        </li>
        <li>
          <strong>Kesilmiş süt gibi, kaşıntılı:</strong> Mantar enfeksiyonu.
        </li>
        <li>
          <strong>Gri, balık kokulu:</strong> Bakteriyel vajinozis.
        </li>
        <li>
          <strong>Sarı-yeşil, köpüklü, kötü kokulu:</strong> Trikomoniyaz veya servisit.
        </li>
        <li>
          <strong>Kahverengi:</strong> Genellikle eski kan; adet başında/sonunda normal, dışında
          değerlendirme gerekir.
        </li>
        <li>
          <strong>Pembe / kanlı:</strong> Adet dışında ise mutlaka doktora başvurulmalıdır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı Nasıl Konur?</h2>
      <p>
        Muayenede jinekolog önce dış genital bölge ve vajinayı inceler, ardından spekulum ile
        serviksi görüntüler. Şüpheli akıntıdan alınan örnekle şu testler yapılabilir:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Direkt mikroskopi (wet mount):</strong> Mantar hifleri,
          <em> clue cells</em> (BV) ve trikomonas hareketleri görülebilir.
        </li>
        <li>
          <strong>pH ölçümü:</strong> Normal vajinal pH 3.8–4.5'tir. BV ve trikomoniyazda pH
          yükselir (&gt;4.5).
        </li>
        <li>
          <strong>Amin (whiff) testi:</strong> BV'de akıntıya KOH damlatıldığında keskin balık
          kokusu çıkar.
        </li>
        <li>
          <strong>Kültür veya PCR:</strong> Dirençli mantar, klamidya, gonore şüphesinde.
        </li>
        <li>
          <strong>Servikal smear / HPV testi:</strong> Uzun süredir düzensiz akıntı ya da lekelenme
          varsa değerlendirmenin bir parçasıdır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">Mantar Enfeksiyonu</h3>
      <p>
        Ağızdan tek doz veya kısa süreli antifungal ilaçlar, ya da vajinal ovül/krem şeklinde
        topikal tedaviler kullanılır. Tekrarlayan enfeksiyonlarda daha uzun süreli koruyucu tedavi
        gerekebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Bakteriyel Vajinozis</h3>
      <p>
        Metronidazol veya klindamisin, ağızdan tablet ya da vajinal jel/krem şeklinde reçete edilir.
        Tedavi süresince alkolden kaçınmak önemlidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Trikomoniyaz</h3>
      <p>
        Tek doz veya 7 günlük ağızdan metronidazol/tinidazol tedavisi uygulanır. Cinsel partnerin de
        aynı anda tedavi edilmesi ve tedavi bitene kadar korunmasız ilişkinin ertelenmesi şarttır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Klamidya / Gonore</h3>
      <p>
        Uygun antibiyotik tedavisi (ör. azitromisin, doksisiklin, seftriakson) verilir; partnerin de
        tedavisi ve diğer cinsel yolla bulaşan enfeksiyonlar (HIV, sifiliz, hepatit) açısından
        tarama önerilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Vajinal Sağlık İçin Öneriler</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Vajinal duş yapmayın:</strong> Doğal florayı bozar, BV ve mantar riskini artırır.
        </li>
        <li>
          <strong>Parfümlü ürünlerden kaçının:</strong> Kokulu sabun, ıslak mendil, ped ve tamponlar
          tahriş edicidir.
        </li>
        <li>
          <strong>Pamuklu iç çamaşırı</strong> tercih edin; sentetik, dar kıyafetlerden kaçının.
        </li>
        <li>
          <strong>Islak mayo / spor kıyafetlerini</strong> uzun süre üzerinizde tutmayın.
        </li>
        <li>
          Tuvalet sonrası temizliği <strong>önden arkaya</strong> doğru yapın.
        </li>
        <li>Adet döneminde pedi / tamponu 4–6 saatte bir değiştirin.</li>
        <li>
          Korunmasız cinsel ilişkiden kaçının; kondom, cinsel yolla bulaşan hastalıklara karşı en
          etkili korumadır.
        </li>
        <li>
          Antibiyotik kullanımı sonrası dengenin yeniden kurulması için doktora danışarak probiyotik
          kullanabilirsiniz.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık Sorulan Sorular</h2>

      <h3 className="mt-6 font-display text-lg font-semibold">Her enfeksiyonda kaşıntı olur mu?</h3>
      <p>
        Hayır. Mantar enfeksiyonunda kaşıntı çok belirginken bakteriyel vajinozisde çoğu zaman
        kaşıntı olmaz. Bu yüzden yalnızca belirtiye bakarak eczaneden ilaç almak yanıltıcı olabilir.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        Gebelikte akıntı artarsa endişelenmeli miyim?
      </h3>
      <p>
        Gebelikte akıntı miktarının artması normaldir. Ancak kötü kokulu, sarı-yeşil, kaşıntılı
        akıntı ya da <strong>sulu / kanlı akıntı</strong> mutlaka değerlendirilmelidir. Su gelmesi
        (amniyon sıvısı) erken doğum tehdidinin habercisi olabilir.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        Cinsel yolla bulaşmıyorsa neden partnerim tedavi olsun?
      </h3>
      <p>
        Mantar ve bakteriyel vajinozis klasik cinsel yolla bulaşan hastalıklar değildir; genellikle
        partnerin tedavisi gerekmez. Ancak trikomoniyaz, klamidya ve gonorede partnerin de eş
        zamanlı tedavi edilmesi şarttır; aksi hâlde enfeksiyon tekrarlar.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        Sık tekrarlayan mantar enfeksiyonum var, ne yapmalıyım?
      </h3>
      <p>
        Yılda 4 veya daha fazla atak yaşıyorsanız <em>rekürren vulvovajinal kandidiyaz</em> tanısı
        düşünülür. Altta yatan diyabet, bağışıklık sorunları ve dirençli türler açısından
        değerlendirilmeniz ve uzun süreli koruyucu tedavi almanız gerekebilir.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        Vajinal duş yapmak temizlik için iyi değil mi?
      </h3>
      <p>
        Hayır. Vajina kendini temizleyebilen bir organdır. Vajinal duş, doğal florayı bozarak
        enfeksiyon riskini artırır; pelvik enflamatuar hastalık ve dış gebelik riski ile ilişkili
        bulunmuştur. Dış genital bölge için ılık su ve gerekiyorsa pH dengeli, kokusuz
        temizleyiciler yeterlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da Değerlendirme</h2>
      <p>
        Vajinal akıntı şikayetiyle başvuran kadınların büyük çoğunluğunda sorun{" "}
        <strong>basit muayene ve kısa süreli tedavi</strong> ile çözülür. Ancak tekrarlayan
        enfeksiyonlar, atipik akıntılar, gebelikte yaşanan değişimler ve cinsel yolla bulaşan
        hastalık şüphesi mutlaka bir kadın hastalıkları ve doğum uzmanınca değerlendirilmelidir.
        Adana'da vajinal akıntı, tekrarlayan mantar / bakteriyel vajinozis ve cinsel yolla bulaşan
        enfeksiyonların tanı ve tedavisi için Op. Dr. Sadık Kükrer'e başvurabilirsiniz.
      </p>
    </ArticleShell>
  );
}

function EndometriozisPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Endometriozis, üreme çağındaki her 10 kadından yaklaşık 1'ini etkileyen, östrojen bağımlı ve
        kronik bir hastalıktır. Rahim iç zarına (endometriuma) benzer dokunun; yumurtalıklar, tuba,
        karın zarı, bağırsak veya nadiren daha uzak bölgelerde yerleşmesiyle oluşur. Bu doku her
        adet döngüsünde tıpkı rahim içindeki gibi kanar; ancak dışarı atılamadığı için kronik
        inflamasyon, yapışıklık ve ağrıya yol açar.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Endometriozis nedir?</h2>
      <p>
        Rahim iç tabakasına histolojik olarak benzer bez ve stroma dokusunun rahim dışında bulunması
        durumudur. En sık tutulan bölgeler yumurtalıklardır; yumurtalıkta oluşan koyu kahverengi
        içerikli kistlere <strong>endometrioma</strong> veya halk arasında "çikolata kisti" denir.
        Karın zarı üzerindeki yüzeyel odaklar, rahim–bağırsak arasındaki derin infiltratif lezyonlar
        ve yapışıklıklar hastalığın diğer görünümleridir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Nedenleri ve risk faktörleri</h2>
      <p>
        Kesin nedeni bilinmemekle birlikte birden fazla mekanizmanın rol oynadığı düşünülür: adet
        kanının ters akışıyla (retrograd menstrüasyon) endometrial hücrelerin karın içine ulaşması,
        bağışıklık sisteminin bu hücreleri temizleyememesi, hormonal ve genetik yatkınlık. Riski
        artıran başlıca faktörler:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Ailede endometriozis öyküsü (anne veya kız kardeşte)</li>
        <li>Erken menarş (12 yaşından önce ilk adet), geç menopoz</li>
        <li>Kısa (27 günden az) ve uzun süreli adet döngüleri</li>
        <li>Doğum yapmamış olmak veya geç yaşta ilk gebelik</li>
        <li>Düşük beden kitle indeksi</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Endometriozis belirtileri</h2>
      <p>
        Belirtilerin şiddeti hastalığın yaygınlığıyla her zaman orantılı değildir; küçük odaklar çok
        ağrılı olabilirken büyük kistler sessiz seyredebilir. En sık başvuru nedenleri:
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Şiddetli adet ağrısı (dismenore)
      </h3>
      <p>
        Adetten 1–2 gün önce başlayan, ilk günlerde iş–okul performansını etkileyecek yoğunlukta,
        ağrı kesicilere kısmen yanıt veren, yıllar içinde giderek şiddetlenen kramp tarzı ağrıdır.
        Adet ağrısının klasik dismenoreden farkı, zamanla artması ve normal yaşamı kısıtlamasıdır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Kronik pelvik ağrı</h3>
      <p>
        Adet dönemine bağlı olmayan, en az 6 ay süren kasık ağrısıdır. Bel ve kalçaya yayılabilir;
        uzun süre ayakta kalmakla ve efor ile artabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Cinsel ilişkide ağrı (disparoni)
      </h3>
      <p>
        Özellikle derin penetrasyonla ortaya çıkan, ilişki sonrası da birkaç saat süren ağrı, arka
        forniks ve rahim–bağırsak arasındaki (kul-de-sak) endometriozis odaklarının tipik
        bulgusudur.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Kısırlık (infertilite)</h3>
      <p>
        Endometriozisli kadınların %30–50'sinde gebelik güçlüğü görülür. Yumurtalık rezervinin
        azalması, tüplerdeki yapışıklıklar, kronik pelvik inflamasyon ve embriyo implantasyonunun
        bozulması bu sorunun altında yatan nedenlerdir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Bağırsak ve idrar yolu belirtileri
      </h3>
      <p>
        Adet dönemlerinde dışkılama sırasında ağrı, ishal veya kabızlık, makatta baskı hissi; idrar
        yaparken ağrı veya idrarda kanama (özellikle adet döneminde) bağırsak veya mesane
        tutulumunun işareti olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Diğer belirtiler</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Kronik yorgunluk ve halsizlik</li>
        <li>Adet döneminde bulantı, baş ağrısı</li>
        <li>Adet dışı lekelenme</li>
        <li>Karında şişkinlik ("endo belly")</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Evreleri (aşamaları)</h2>
      <p>
        Amerikan Üreme Tıbbı Derneği (ASRM) sınıflamasına göre 4 evrede değerlendirilir. Evre
        hastalığın yaygınlığını gösterir; ancak ağrı şiddetiyle her zaman uyumlu değildir:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Evre 1 (minimal):</strong> Yüzeyel, izole odaklar.
        </li>
        <li>
          <strong>Evre 2 (hafif):</strong> Daha fazla yüzeyel odak, hafif yapışıklıklar.
        </li>
        <li>
          <strong>Evre 3 (orta):</strong> Endometrioma (çikolata kisti), yapışıklıklar
          belirginleşir.
        </li>
        <li>
          <strong>Evre 4 (şiddetli):</strong> Büyük endometriomalar, yaygın derin infiltratif
          tutulum, rahim–bağırsak yapışıklıkları.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı nasıl konur?</h2>
      <p>
        Endometriozis tanısı klinik değerlendirme, görüntüleme ve gerektiğinde cerrahi doğrulamayla
        konur. Şüpheden kesin tanıya kadar geçen süreç kadından kadına değişir; erken değerlendirme
        önemlidir.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ayrıntılı öykü ve muayene:</strong> Ağrı örüntüsü, adet döngüsü, aile öyküsü ve
          pelvik muayenede hassasiyet ile nodül aranması.
        </li>
        <li>
          <strong>Transvajinal ultrason:</strong> Endometrioma (çikolata kisti), derin infiltratif
          lezyonlar ve rahim–bağırsak yapışıklıkları büyük oranda değerlendirilebilir.
        </li>
        <li>
          <strong>MR görüntüleme:</strong> Bağırsak, mesane ve derin infiltratif tutulumun ayrıntılı
          haritalanmasında kullanılır; cerrahi planlamayı yönlendirir.
        </li>
        <li>
          <strong>CA-125:</strong> Bazı olgularda yüksek çıkabilir; tanı koydurmaz ama takip için
          kullanılabilir.
        </li>
        <li>
          <strong>Laparoskopi:</strong> Kesin tanı için altın standart. Karına küçük deliklerden
          girilerek lezyonlar hem görülür hem aynı seansta tedavi edilir; biyopsi ile doğrulama
          yapılır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>
      <p>
        Tedavi; hastanın yaşı, semptomların şiddeti, hastalığın evresi, gebelik isteği ve yumurtalık
        rezervine göre bireysel planlanır. Tek bir "en iyi tedavi" yoktur; amaç ağrıyı kontrol
        etmek, hastalığın ilerlemesini durdurmak ve doğurganlığı korumaktır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">1. İlaç tedavisi (ağrı yönetimi)</h3>
      <p>
        Non-steroid antiinflamatuar ilaçlar (NSAİİ) hafif–orta ağrı için ilk seçenektir. Adetten 1–2
        gün önce başlayıp adet süresince düzenli kullanıldığında daha etkilidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Hormonal tedavi</h3>
      <p>
        Yumurtlamayı ve adet kanamasını baskılayarak endometriozis odaklarının kanamasını ve
        büyümesini durdurur. Seçenekler:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Kombine oral kontraseptifler (özellikle sürekli kullanım)</li>
        <li>Progestin ağırlıklı tabletler (dienogest, noretisteron asetat)</li>
        <li>Hormonal rahim içi araç (levonorgestrel içeren spiral)</li>
        <li>
          GnRH analogları veya GnRH antagonistleri — geçici menopoz benzeri durum oluşturur;
          genellikle 3–6 ay ve add-back tedavisi ile kullanılır
        </li>
      </ul>
      <p>
        Hormonal tedavi hastalığı iyileştirmez; ilaç kesildikten sonra belirtiler geri dönebilir.
        Gebelik isteyen hastalarda uygun değildir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Laparoskopik cerrahi</h3>
      <p>
        İlaç tedavisine yanıt vermeyen ağrı, 4 cm ve üzeri endometrioma, şüpheli görüntüleme
        bulguları, tıkalı tüpler veya kısırlık değerlendirmesinde cerrahi gündeme gelir.
        Laparoskopik yaklaşım (kapalı yöntem) günümüzde altın standarttır:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Karın duvarına 3–4 küçük kesi (0.5–1 cm) yapılır; iz neredeyse görünmez kalır.</li>
        <li>Endometrioma kist duvarıyla birlikte çıkarılır; yumurtalık dokusu korunur.</li>
        <li>Yüzeyel odaklar koter/lazer ile yakılır veya çıkarılır.</li>
        <li>Yapışıklıklar açılır, tüplerin geçirgenliği değerlendirilir.</li>
        <li>İyileşme süresi kısa (genellikle 1 hafta), hastanede kalış 1 gecedir.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Kısırlık tedavisi</h3>
      <p>
        Gebelik isteyen ve endometriozise bağlı infertilitesi olan hastalarda, evre ve yaşa göre
        yaklaşım değişir. Hafif olgularda laparoskopi sonrası spontan gebelik şansı artar; ileri
        olgularda ve yumurtalık rezervi düşükse tüp bebek (IVF) doğrudan planlanabilir. Cerrahi
        öncesi yumurtalık rezervini korumak için planlama önemlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Yaşam tarzı ve destekleyici öneriler
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Düzenli fiziksel aktivite ve pelvik taban egzersizleri kronik ağrıyı azaltabilir.</li>
        <li>
          Antiinflamatuar beslenme (omega-3'ten zengin, işlenmiş gıda ve trans yağlardan fakir)
          semptomları yumuşatabilir.
        </li>
        <li>Sıcak uygulama ve pelvik gevşetme teknikleri adet ağrısında yardımcıdır.</li>
        <li>Kronik ağrı psikososyal destek gerektirebilir; uzman görüşü almaktan çekinmeyin.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Hangi durumlarda hemen başvurulmalı?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Ani başlayan şiddetli tek taraflı kasık ağrısı (endometrioma rüptürü/torsiyonu olabilir)
        </li>
        <li>Yüksek ateş ve pelvik ağrı</li>
        <li>Adet dışı yoğun kanama, bayılma</li>
        <li>Ağrı kesicilere yanıt vermeyen, günlük yaşamı kısıtlayan ağrı</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da endometriozis tedavisi</h2>
      <p>
        Endometriozis, doğru zamanda konulan tanı ve deneyimli laparoskopik cerrahi ile
        yönetildiğinde yaşam kalitesi büyük ölçüde artırılabilen bir hastalıktır. Op. Dr. Sadık
        Kükrer, Adana'da endometriozis tanı ve tedavisinde laparoskopik cerrahi deneyimi ile hem
        ağrı hem doğurganlığın korunmasına yönelik bireysel bir plan sunar. Şiddetli adet ağrısı,
        cinsel ilişkide ağrı veya gebelik güçlüğü yaşıyorsanız değerlendirme için iletişime
        geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function MiyomPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Miyomlar (uterin fibroidler), rahmin kas tabakasından köken alan iyi huylu tümörlerdir.
        Üreme çağındaki kadınların yaklaşık %70'inde, 50 yaşına gelen kadınların ise büyük
        çoğunluğunda görülür. Boyutları birkaç milimetreden 20 santimetreye kadar değişebilir;
        sayıları tek veya çok sayıda olabilir. Miyomların büyük bir kısmı belirtisiz seyreder ve
        rutin muayenede tesadüfen saptanır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Miyom nedir?</h2>
      <p>
        Rahmin kas tabakası olan miyometriumdan gelişen; östrojen ve progesterona duyarlı, iyi huylu
        düz kas tümörleridir. Tıbbi adı <strong>leiomyom</strong> veya{" "}
        <strong>uterin fibroid</strong>'dir. Menopoz sonrası hormon seviyeleri düştüğünde miyomlar
        genellikle küçülür. Kötü huylu (kanserli) dönüşüm oldukça nadirdir (%0.1'in altında).
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Miyom çeşitleri (yerleşim yerine göre)
      </h2>
      <p>Miyomların belirtileri, boyutlarından çok rahim içindeki yerleşim yerine bağlıdır:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Submüköz miyom:</strong> Rahim iç boşluğuna doğru büyür; küçük boyutta bile yoğun
          kanama ve kısırlığa yol açabilir.
        </li>
        <li>
          <strong>İntramural miyom:</strong> Rahim duvarının içinde, kas tabakasında yerleşir. En
          sık görülen tiptir.
        </li>
        <li>
          <strong>Subseröz miyom:</strong> Rahmin dış yüzeyinden dışa doğru büyür; komşu organlara
          bası yapabilir.
        </li>
        <li>
          <strong>Saplı (pedinküllü) miyom:</strong> Bir sap aracılığıyla rahme tutunur; sap
          etrafında dönerse (torsiyon) ani şiddetli ağrı yapabilir.
        </li>
        <li>
          <strong>Servikal miyom:</strong> Rahim ağzında yerleşen nadir tiptir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Nedenleri ve risk faktörleri</h2>
      <p>
        Miyomların kesin nedeni bilinmemektedir. Hormonal, genetik ve büyüme faktörlerinin birlikte
        rol oynadığı düşünülür. Riski artıran başlıca faktörler:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>30 yaş üstü olmak (menopoza kadar risk artar)</li>
        <li>Ailede miyom öyküsü (anne veya kız kardeşte)</li>
        <li>Erken menarş (12 yaşından önce ilk adet)</li>
        <li>Doğum yapmamış olmak</li>
        <li>Obezite, yüksek tansiyon</li>
        <li>Alkol tüketimi ve D vitamini eksikliği</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Miyom belirtileri</h2>
      <p>
        Miyomların yaklaşık yarısı belirtisizdir. Belirti verenlerde en sık şikayetler şunlardır:
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Yoğun ve uzun süren adet kanaması
      </h3>
      <p>
        En sık başvuru nedenidir. Adet süresinin 7 günden uzun sürmesi, pıhtılı kanama, saatte bir
        ped değiştirme gereksinimi ve kansızlığa (anemi) yol açabilecek düzeyde kanama tipiktir.
        Özellikle submüköz miyomlar bu tabloyu oluşturur.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Kasık ağrısı ve baskı hissi</h3>
      <p>
        Büyük miyomlar karın alt kısmında dolgunluk, baskı ve künt ağrı yaratabilir. Saplı bir
        miyomun dönmesi durumunda ani şiddetli ağrı ortaya çıkar ve acil değerlendirme gerekir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Sık idrara çıkma veya kabızlık</h3>
      <p>
        Mesaneye bası yapan ön yerleşimli miyomlar sık idrara çıkma ve idrar tutamama hissine;
        bağırsağa bası yapan arka yerleşimli miyomlar kabızlık ve rektal baskıya neden olabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        4. Cinsel ilişkide ağrı ve bel ağrısı
      </h3>
      <p>
        Rahim ağzına yakın veya derin yerleşimli miyomlar ilişki sırasında ağrıya, geniş miyomlar
        ise bel ve kalçalara yayılan ağrıya yol açabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Kısırlık ve tekrarlayan gebelik kayıpları
      </h3>
      <p>
        Rahim iç boşluğunu bozan submüköz miyomlar ve büyük intramural miyomlar embriyonun
        tutunmasını engelleyebilir. Miyomlar ayrıca erken doğum, plasenta yerleşim bozuklukları ve
        sezaryen ihtiyacını artırabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Karında büyüme ve şişkinlik</h3>
      <p>
        Çok büyük miyomlarda karın hacminde belirgin artış, palpe edilebilir kitle ve gebelik
        izlenimi veren şişkinlik görülebilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı nasıl konur?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Pelvik muayene:</strong> Büyümüş, düzensiz konturlu rahim ele gelebilir.
        </li>
        <li>
          <strong>Transvajinal ultrason:</strong> Miyomların sayısı, boyutu ve yerleşim yerini
          belirlemede ilk tercih edilen yöntemdir; oldukça yüksek duyarlılıkla tanı koyar.
        </li>
        <li>
          <strong>Salin infüzyon sonohisterografi (SIS):</strong> Rahim içine serum verilerek
          yapılan ultrasondur; submüköz miyomların değerlendirilmesinde önemlidir.
        </li>
        <li>
          <strong>Histeroskopi:</strong> İnce bir kamera ile rahim içi doğrudan görüntülenir; hem
          tanı hem tedavi imkânı sunar.
        </li>
        <li>
          <strong>MR görüntüleme:</strong> Çok sayıda ve büyük miyomlarda, cerrahi öncesi haritalama
          ve adenomyozisten ayrımda kullanılır.
        </li>
        <li>
          <strong>Tam kan sayımı:</strong> Yoğun kanamaya bağlı demir eksikliği anemisini gösterir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>
      <p>
        Tedavi kararı; hastanın yaşı, gebelik isteği, belirtilerin şiddeti, miyomların sayısı,
        boyutu ve yerleşim yerine göre bireysel olarak verilir. Belirtisi olmayan küçük miyomlar
        çoğunlukla düzenli takip ile izlenir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Takip (izlem)</h3>
      <p>
        Belirti vermeyen, 4–5 cm'den küçük miyomlar 6–12 aylık aralıklarla ultrasonla takip edilir.
        Hızlı büyüme veya belirti gelişimi durumunda tedavi planlanır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. İlaç tedavisi</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>NSAİİ ve traneksamik asit:</strong> Adet kanamasını azaltır, ağrıyı hafifletir.
        </li>
        <li>
          <strong>Kombine oral kontraseptifler:</strong> Kanama düzenini iyileştirebilir; miyomu
          küçültmez.
        </li>
        <li>
          <strong>Hormonal rahim içi araç (LNG-RİA):</strong> Yoğun kanamayı belirgin azaltır; rahim
          boşluğu uygun olan olgularda tercih edilir.
        </li>
        <li>
          <strong>GnRH analog/antagonistleri:</strong> Miyomları geçici olarak küçültür; cerrahi
          öncesi hazırlıkta veya menopoza yakın hastalarda kullanılır. Uzun süreli kullanımda
          add-back tedavi gerekir.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Histeroskopik miyomektomi</h3>
      <p>
        Rahim içine bakan (submüköz) miyomların çıkarılmasında altın standarttır. Rahim ağzından
        girilerek yapılır, karına kesi yapılmaz. Ameliyat sonrası aynı gün taburculuk ve hızlı
        iyileşme mümkündür; doğurganlık üzerinde olumlu etkisi vardır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Laparoskopik miyomektomi</h3>
      <p>
        İntramural ve subseröz miyomlarda tercih edilen kapalı yöntemdir. Karın duvarına 3–4 küçük
        kesi (0.5–1 cm) yapılarak miyomlar çıkarılır; rahim korunur. Avantajları:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Daha az ağrı ve kan kaybı</li>
        <li>Kısa hastanede kalış (genellikle 1 gece)</li>
        <li>Hızlı iyileşme ve minimal kozmetik iz</li>
        <li>Gebelik isteyen kadınlar için doğurganlığın korunması</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Açık (abdominal) miyomektomi</h3>
      <p>
        Çok sayıda, çok büyük veya derin yerleşimli miyomlarda açık cerrahi gerekebilir. Rahim
        korunur; gebelik planı olan kadınlarda tercih edilebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Histerektomi (rahmin alınması)</h3>
      <p>
        Gebelik planı olmayan, ilaç ve miyomektomiye yanıt vermeyen, çok sayıda ve büyük miyomu
        bulunan hastalarda kesin çözümdür. Laparoskopik veya vajinal yolla yapılabilir; miyomların
        tekrar etme olasılığını ortadan kaldırır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        7. Uterin arter embolizasyonu (UAE)
      </h3>
      <p>
        Miyomu besleyen damarların girişimsel radyoloji ile tıkanması yöntemidir. Cerrahiye uygun
        olmayan veya cerrahi istemeyen hastalarda alternatiftir; gebelik planlayanlarda önerilmez.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Miyom ve gebelik</h2>
      <p>
        Çoğu miyomlu kadın sorunsuz gebe kalır ve doğum yapar. Ancak miyomların yerleşim yeri ve
        boyutuna göre şu riskler artabilir: düşük, erken doğum, plasenta yerleşim bozuklukları,
        doğumun ilerlememesi ve sezaryen ihtiyacı. Gebelik planlayan, submüköz veya büyük intramural
        miyomu bulunan kadınlarda gebelik öncesi miyomektomi düşünülebilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Hangi durumlarda hemen başvurulmalı?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Ani başlayan şiddetli kasık ağrısı (saplı miyom torsiyonu olabilir)</li>
        <li>Bayılma hissi veya güçsüzlükle birlikte yoğun kanama</li>
        <li>Ateş, kötü kokulu akıntı ile birlikte ağrı</li>
        <li>Hızla büyüyen karın ve idrar yapamama</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da miyom tedavisi</h2>
      <p>
        Miyom tedavisinde amaç, yalnızca miyomu çıkarmak değil; hastanın yaşı, gebelik planı ve
        yaşam kalitesi hedefleriyle uyumlu, doğru yöntemi seçmektir. Op. Dr. Sadık Kükrer, Adana'da
        miyom tanı ve tedavisinde histeroskopik ve laparoskopik cerrahi deneyimi ile rahmin
        korunmasına yönelik bireysel bir plan sunar. Yoğun adet kanaması, kasık ağrısı veya gebelik
        güçlüğü yaşıyorsanız değerlendirme için iletişime geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function HpvAsisiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        HPV (Human Papillomavirus / İnsan Papilloma Virüsü), cinsel yolla en sık bulaşan virüstür.
        Cinsel yönden aktif kadın ve erkeklerin yaşamları boyunca en az bir kez HPV ile karşılaşma
        olasılığı %80'in üzerindedir. 200'den fazla alt tipi bulunan bu virüs; başta rahim ağzı
        (serviks) kanseri olmak üzere birçok kanser türünün ve genital siğillerin başlıca
        sorumlusudur. HPV aşısı, bu kanserlerin büyük çoğunluğunu <strong>önlenebilir</strong> hâle
        getiren en etkili koruyucu sağlık uygulamalarından biridir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">HPV nedir, ne yapar?</h2>
      <p>
        HPV, cilt ve mukoza hücrelerini enfekte eden bir DNA virüsüdür. Enfeksiyonların çoğu (%90)
        bağışıklık sistemi tarafından 2 yıl içinde temizlenir. Ancak <strong>yüksek riskli</strong>{" "}
        tiplerle (özellikle HPV 16 ve 18) uzun süren enfeksiyon; hücre değişikliklerine (displazi)
        ve sonrasında kansere yol açabilir. Düşük riskli tipler (HPV 6, 11) ise genital siğillere
        neden olur.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        HPV hangi kanserlere neden olur?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Rahim ağzı (serviks) kanseri:</strong> Vakaların yaklaşık %99'u HPV kaynaklıdır;
          dünyada kadınlarda 4. sıradaki kanserdir.
        </li>
        <li>
          <strong>Vulva ve vajina kanserleri:</strong> Vakaların %70'e yakını HPV bağlantılıdır.
        </li>
        <li>
          <strong>Anal kanser:</strong> Hem kadın hem erkeklerde vakaların %90'ı HPV kaynaklıdır.
        </li>
        <li>
          <strong>Penis kanseri:</strong> Vakaların yaklaşık %60'ı HPV ile ilişkilidir.
        </li>
        <li>
          <strong>Orofarenks (baş-boyun) kanseri:</strong> Bademcik ve dil kökü kanserlerinin
          %70'inde HPV saptanır; son yıllarda erkeklerde belirgin artış göstermektedir.
        </li>
        <li>
          <strong>Genital siğiller (kondiloma):</strong> Vakaların %90'ından fazlası HPV 6 ve 11
          kaynaklıdır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">HPV aşısı türleri</h2>
      <p>
        Türkiye'de ve dünyada güncel olarak kullanılan aşı <strong>Gardasil 9</strong>'dur; 9 HPV
        tipine karşı koruma sağlar: 6, 11, 16, 18, 31, 33, 45, 52 ve 58. Bu tipler; rahim ağzı
        kanseri vakalarının yaklaşık %90'ı ve genital siğillerin %90'ından fazlasından sorumludur.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">HPV aşısı kimlere yapılır?</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. 9–14 yaş: İdeal aralık</h3>
      <p>
        Aşının en etkili uygulandığı dönem cinsel aktivite öncesidir. Bu yaş grubunda immün yanıt en
        güçlüdür ve <strong>yalnızca 2 doz</strong> (0. ve 6–12. ay) yeterli koruma sağlar. Dünya
        Sağlık Örgütü, tek doz şemasının bile bu yaşta yeterli olabileceğine dair güncel kanıtlar
        sunmaktadır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. 15–26 yaş: Rutin öneri</h3>
      <p>
        Cinsel aktivite başlamış olsa dahi aşı önerilir. Bu yaşta <strong>3 doz</strong> uygulanır
        (0, 2 ve 6. ay). Bir veya iki HPV tipi ile karşılaşılmış olsa bile aşı, karşılaşılmamış
        diğer tiplere karşı koruma sağlar.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. 27–45 yaş: Bireysel karar</h3>
      <p>
        FDA 2018'de aşının kullanım yaşını 45'e yükseltmiştir. Yeni partner, evlilik değişikliği,
        HPV negatif smear gibi durumlarda hekiminizle birlikte fayda–maliyet değerlendirmesi
        yapılarak uygulanabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Erkeklerde HPV aşısı</h3>
      <p>
        HPV yalnızca kadınları etkileyen bir virüs değildir. Erkeklerde anal, penis ve orofarenks
        kanserlerine karşı koruyucudur. Ayrıca aşılanan erkekler, partnerlerine virüs bulaştırma
        riskini azaltarak toplum bağışıklığına katkı sağlar. 9–26 yaş aralığındaki erkeklere aşı
        önerilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Doz şeması özet</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>9–14 yaş:</strong> 2 doz — 0. ay ve 6–12. ay
        </li>
        <li>
          <strong>15 yaş ve üzeri:</strong> 3 doz — 0. ay, 2. ay ve 6. ay
        </li>
        <li>
          <strong>Bağışıklığı baskılanmış kişilerde (HIV, transplant vb.):</strong> Yaş fark
          etmeksizin 3 doz
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Aşı güvenli mi? Yan etkiler</h2>
      <p>
        HPV aşısı, 2006'dan bu yana 100'den fazla ülkede uygulanmakta; Dünya Sağlık Örgütü, FDA ve
        EMA tarafından güvenli kabul edilmektedir. En sık görülen yan etkiler:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Enjeksiyon yerinde ağrı, kızarıklık, şişlik (%80)</li>
        <li>Hafif ateş, baş ağrısı (%10–15)</li>
        <li>Yorgunluk, halsizlik (geçici)</li>
        <li>Bulantı (nadir)</li>
      </ul>
      <p>
        Nadir olarak ilk 15 dakikada bayılma (özellikle ergenlerde) görülebilir; bu nedenle aşı
        sonrası 15 dakika gözlem önerilir. Aşının kısırlık, otoimmün hastalık veya kanser gibi ciddi
        yan etkilerle ilişkisi bulunmamaktadır ve bu iddialar bilimsel çalışmalarla çürütülmüştür.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Gebelik ve emzirme dönemi</h2>
      <p>
        HPV aşısı gebelikte önerilmez; aşı programı gebelik sonrasına ertelenir. Yanlışlıkla gebe
        iken uygulanmış olması gebeliğin sonlandırılması için neden değildir. Emzirme döneminde ise
        aşı <strong>güvenli</strong> kabul edilir ve uygulanabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Aşı olanlar smear (Pap testi) yaptırmalı mı?
      </h2>
      <p>
        <strong>Evet.</strong> Aşı Gardasil 9 kapsamındaki 9 HPV tipine karşı korur; ancak rahim
        ağzı kanserine yol açabilen tüm tipleri (yaklaşık %10 kadar) kapsamaz. Bu nedenle aşılı
        kadınlarda da 21–25 yaştan itibaren düzenli aralıklarla{" "}
        <strong>servikal tarama (Pap smear ve HPV DNA testi)</strong> yaptırılmalıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan pratik sorular</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Aşı bulaşmış HPV'yi tedavi eder mi?</strong> Hayır, aşı önleyicidir; mevcut
          enfeksiyonu tedavi etmez.
        </li>
        <li>
          <strong>Aşı sonrası cinsel yaşam kısıtlanır mı?</strong> Hayır, herhangi bir kısıtlama
          gerekmez.
        </li>
        <li>
          <strong>Menopoz dönemindeki kadınlara yapılır mı?</strong> 45 yaş üstünde rutin
          önerilmemekle birlikte, özel durumlarda hekim önerisi ile uygulanabilir.
        </li>
        <li>
          <strong>Aşı ücretsiz mi?</strong> Türkiye'de HPV aşısı henüz ulusal aşı takvimine dâhil
          değildir; özel olarak temin edilir. Bazı ülkelerde 9–14 yaş grubuna ücretsiz uygulanır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Adana'da HPV aşısı ve danışmanlık
      </h2>
      <p>
        HPV aşısı; kızınızın, oğlunuzun ve kendi geleceğinizin kadın sağlığına yapılabilecek en
        değerli yatırımlardan biridir. Op. Dr. Sadık Kükrer, Adana'da HPV aşısı danışmanlığı, smear
        ve HPV DNA taraması ile servikal displazi tedavisi (kolposkopi, LEEP/konizasyon) konularında
        bireysel değerlendirme sunar. Aşı yaşı, doz şeması veya HPV testi sonuçlarınız hakkında soru
        sormak için iletişime geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function PcosPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Polikistik Over Sendromu (PCOS), üreme çağındaki kadınların yaklaşık %8–13'ünü etkileyen, en
        sık görülen hormonal ve metabolik bozukluklardan biridir. Adet düzensizliği, istenmeyen
        tüylenme, akne ve kilo alımı gibi görünür bulguların yanında; insülin direnci, diyabet ve
        kalp-damar hastalıkları riskini de artıran, ömür boyu takip gerektiren bir durumdur. Doğru
        tanı ve bireysel tedavi ile hem şikayetler kontrol altına alınabilir hem de uzun vadeli
        riskler belirgin ölçüde azaltılabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">PCOS ile PCOM arasındaki fark</h2>
      <p>
        Son yıllarda güncellenen tanı kılavuzları (2023 International PCOS Guideline, Teede ve ark.)
        bu iki kavramın ayrımını daha net ortaya koydu:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>PCOM (Polycystic Ovarian Morphology):</strong> Ultrasonda yumurtalıkta çok sayıda
          küçük follikül (≥20) veya yumurtalık hacminde artış (&gt;10 mL) görülmesidir. Yalnızca bir{" "}
          <strong>bulgudur</strong>. Sağlıklı, düzenli adet gören kadınların yaklaşık %20–30'unda
          saptanabilir.
        </li>
        <li>
          <strong>PCOS (Polycystic Ovary Syndrome):</strong> Klinik bir <strong>sendromdur</strong>.
          Adet düzensizliği ve hiperandrojenizm gibi klinik bulgularla birlikte konan bir tanıdır.
        </li>
      </ul>
      <p>
        Yani "polikistik over görünümü" ile "polikistik over sendromu" aynı şey değildir. PCOM,
        PCOS'un olası kriterlerinden yalnızca biridir. 2023 kılavuzu ayrıca ultrason yerine{" "}
        <strong>anti-Müllerian hormon (AMH)</strong> ölçümünün de tanıda kullanılabileceğini ve
        düzenli sikluslu, hiperandrojenizmi bulunan ergenlerde ultrason gerekmeden tanı
        konulabileceğini vurgular.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">PCOS'un nedenleri</h2>
      <p>
        Kesin bir neden yoktur; genetik yatkınlık, insülin direnci, hafif inflamasyon ve
        yumurtalıkta artmış androjen (erkeklik hormonu) üretimi bir arada rol oynar. Ailede PCOS,
        tip 2 diyabet veya erken kalp hastalığı öyküsü riski artırır. Obezite tabloyu şiddetlendirir
        ancak zayıf kadınlarda da (yaklaşık %20) PCOS görülebilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">PCOS belirtileri</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Adet düzensizliği ve ovulasyon bozukluğu
      </h3>
      <p>
        En sık başvuru şikayetidir. Sikluslar 35 günden uzun (oligomenore) olabilir, yılda 8'den az
        adet görülebilir veya adet tamamen kesilebilir (amenore). Ovulasyon olmadığı için gebelik
        güçleşir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Hiperandrojenizm bulguları</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Hirsutizm:</strong> Yüz, çene, göğüs, karın ve sırtta erkek tipi tüylenme.
        </li>
        <li>
          <strong>Akne:</strong> Özellikle çene, çene altı ve sırtta, ergenlik sonrasında devam eden
          veya erişkinlikte başlayan inatçı sivilceler.
        </li>
        <li>
          <strong>Androjenik saç dökülmesi:</strong> Alın hattı ve tepe bölgesinde saçların
          incelmesi.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Kilo artışı ve insülin direnci bulguları
      </h3>
      <p>
        Bel çevresinde yağlanma, kilo vermede zorluk, öğün sonrası ani açlık ve halsizlik, boyun
        arkası ve koltuk altında koyu kadifemsi cilt (akantozis nigrikans) insülin direncinin klinik
        işaretleridir. PCOS'lu kadınların yaklaşık %70'inde insülin direnci saptanır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. İnfertilite (kısırlık)</h3>
      <p>
        PCOS, ovulatuar infertilitenin en sık nedenidir. Ancak "PCOS = kısırlık" değildir; uygun
        tedavi ile büyük çoğunluk gebe kalabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Ruhsal belirtiler</h3>
      <p>
        Depresyon, anksiyete ve yeme bozuklukları PCOS'lu kadınlarda topluma göre 2–3 kat sık
        görülür ve tanının bir parçası olarak değerlendirilmelidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı: Rotterdam kriterleri</h2>
      <p>
        PCOS tanısı için şu üç bulgudan <strong>en az ikisinin</strong> bulunması ve benzer tabloya
        yol açan diğer nedenlerin dışlanması gerekir:
      </p>
      <ol className="list-decimal space-y-2 pl-6">
        <li>
          <strong>Ovulatuar disfonksiyon:</strong> Adet düzensizliği (oligo/amenore) veya
          progesteron ölçümüyle kanıtlanmış anovulasyon.
        </li>
        <li>
          <strong>Klinik veya biyokimyasal hiperandrojenizm:</strong> Hirsutizm/akne ya da kanda
          yüksek serbest testosteron.
        </li>
        <li>
          <strong>PCOM veya yüksek AMH:</strong> Ultrasonda polikistik over görünümü veya yaşa göre
          yüksek AMH düzeyi (2023 kılavuz güncellemesi).
        </li>
      </ol>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Ayırıcı tanıda dışlanması gerekenler
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Tiroid fonksiyon bozuklukları (TSH)</li>
        <li>Hiperprolaktinemi (prolaktin)</li>
        <li>Konjenital adrenal hiperplazi (17-OH-progesteron)</li>
        <li>Cushing sendromu ve androjen salgılayan tümörler (nadir)</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hangi tetkikler istenir?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Hormon paneli: LH, FSH, östradiol, prolaktin, TSH, serbest testosteron, DHEAS,
          17-OH-progesteron
        </li>
        <li>AMH (özellikle ultrason yeterli değilse)</li>
        <li>Açlık glukozu, insülin, HbA1c ve gerekirse OGTT (glukoz tolerans testi)</li>
        <li>Lipid profili (total kolesterol, LDL, HDL, trigliserit)</li>
        <li>Vitamin D, karaciğer enzimleri</li>
        <li>Transvajinal veya abdominal ultrason</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">PCOS tedavisi</h2>
      <p>
        Tedavi tek bir ilaca indirgenemez; hastanın <strong>hedefine</strong> göre
        kişiselleştirilir: gebelik isteği, adet düzenlenmesi, tüylenme/akne kontrolü ve uzun vadeli
        metabolik risklerin azaltılması.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Yaşam tarzı: tedavinin temeli</h3>
      <p>
        Fazla kilolu PCOS'lu kadınlarda vücut ağırlığının yalnızca <strong>%5–10'unu</strong> vermek
        bile ovulasyonun geri dönmesini, insülin direncinin düzelmesini ve tüylenme/aknenin
        azalmasını sağlayabilir. Akdeniz tipi beslenme, düşük glisemik indeksli karbonhidratlar,
        yeterli protein ve haftada en az 150 dakika orta düzey egzersiz önerilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Kombine oral kontraseptifler</h3>
      <p>
        Gebelik istemeyen kadınlarda ilk basamak tedavidir. Adet düzenini sağlar, androjen
        seviyelerini düşürür; hirsutizm ve akneyi belirgin şekilde iyileştirir. Ayrıca uzun süreli
        anovulasyona bağlı endometrium kanseri riskini azaltır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Metformin</h3>
      <p>
        İnsülin direnci, bozulmuş glukoz toleransı veya tip 2 diyabet riski yüksek olan PCOS'lu
        kadınlarda kullanılır. Kilo kontrolüne yardımcı olur, adet düzenini iyileştirebilir. Tek
        başına gebelik indüksiyonu için ilk tercih değildir, ancak seçilmiş olgularda letrozole
        eklenebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Antiandrojen tedaviler</h3>
      <p>
        Spironolakton, siproteron asetat veya finasterid; şiddetli hirsutizm ve androjenik saç
        dökülmesinde kullanılır. Bu ilaçlar mutlaka etkili bir kontraseptif yöntemle birlikte
        verilmelidir (fetüs üzerinde teratojen etki riski).
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Ovulasyon indüksiyonu (gebelik isteyenlerde)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Letrozol:</strong> Güncel kılavuzlarda PCOS'a bağlı anovulatuar infertilitede{" "}
          <strong>ilk tercih</strong> ilaçtır. Klomifen sitrata göre canlı doğum oranları daha
          yüksektir.
        </li>
        <li>
          <strong>Klomifen sitrat:</strong> Uzun yıllardır kullanılan alternatif seçenektir.
        </li>
        <li>
          <strong>Gonadotropinler:</strong> Oral tedavilere yanıt vermeyen olgularda düşük dozda
          uygulanır; yakın folliküler takip gerektirir.
        </li>
        <li>
          <strong>Tüp bebek (IVF):</strong> Diğer yöntemlere yanıt alınmayan veya başka infertilite
          faktörleri eşlik eden hastalarda önerilir.
        </li>
        <li>
          <strong>Laparoskopik ovaryan drilling:</strong> Seçilmiş olgularda düşünülebilen cerrahi
          bir seçenektir.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        6. Cilt bulgularının yerel tedavisi
      </h3>
      <p>
        Lazer ve elektroepilasyon istenmeyen tüylenmede etkili yardımcı yöntemlerdir. Akne için
        topikal retinoidler, benzoil peroksit ve inatçı olgularda oral izotretinoin kullanılabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        PCOS ve uzun vadeli sağlık riskleri
      </h2>
      <p>
        PCOS yalnızca üreme çağını ilgilendiren bir sorun değildir. Menopoz sonrasında da devam eden
        metabolik bir tablodur ve şu risklerle ilişkilidir:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Tip 2 diyabet (topluma göre 3–7 kat)</li>
        <li>Kardiyovasküler hastalık ve hipertansiyon</li>
        <li>Metabolik sendrom, karaciğer yağlanması (NAFLD)</li>
        <li>Uyku apnesi</li>
        <li>Endometrium (rahim iç zarı) kanseri (uzun süreli anovulasyona bağlı)</li>
        <li>Gebelikte gestasyonel diyabet ve preeklampsi</li>
        <li>Depresyon ve anksiyete</li>
      </ul>
      <p>
        Bu nedenle PCOS tanısı konan kadınlarda düzenli kan şekeri, lipid profili, tansiyon takibi
        ve yıllık jinekolojik değerlendirme çok önemlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">PCOS ve gebelik</h2>
      <p>
        Doğru tedaviyle PCOS'lu kadınların büyük çoğunluğu gebe kalabilir. Gebelik döneminde
        gestasyonel diyabet, gebeliğin indüklediği hipertansiyon ve preeklampsi riskleri
        artabileceğinden yakın takip önerilir. Gebelik öncesi kilo kontrolü ve folik asit desteği
        önemlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da PCOS tanı ve tedavisi</h2>
      <p>
        PCOS, tek bir laboratuvar değeriyle tanı konan bir hastalık değil; klinik değerlendirme,
        ultrason ve hormonal profilin birlikte yorumlanmasını gerektiren bir sendromdur. Op. Dr.
        Sadık Kükrer, Adana'da PCOS tanı ve tedavisinde; adet düzensizliği, tüylenme, kilo kontrolü,
        insülin direnci ve gebelik planlaması konularında bireysel bir yaklaşım sunar.
        Şikayetleriniz varsa ya da mevcut tedaviniz sonuç vermiyorsa, kapsamlı bir değerlendirme
        için iletişime geçebilirsiniz.
      </p>
    </ArticleShell>
  );
}

function HisteroskopiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Histeroskopi</strong>, rahim iç boşluğunun (kavitesinin) ince, ışıklı bir kamera
        (histeroskop) yardımıyla doğrudan görüntülenmesini sağlayan bir jinekolojik işlemdir.
        Ultrason veya HSG (rahim filmi) gibi görüntüleme yöntemleri rahim içi hakkında dolaylı bilgi
        verirken, histeroskopi rahim boşluğunu gerçek zamanlı ve yüksek çözünürlükle "içeriden"
        görme imkânı tanır. Bu sayede birçok patoloji hem <strong>tanınabilir</strong> hem de aynı
        seansta <strong>tedavi edilebilir</strong>.
      </p>

      <p>
        Son yirmi yılda gelişen minyatür optik sistemler sayesinde histeroskopi büyük ölçüde
        muayenehane/ofis şartlarında, anestezisiz veya çok hafif sedasyonla yapılabilen bir işleme
        dönüşmüştür. Anormal kanama, tekrarlayan düşük, kısırlık araştırması ve rahim içi
        yapışıklıkların tedavisinde günümüzde altın standart yöntemdir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Histeroskopi çeşitleri</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Tanısal (ofis) histeroskopi</h3>
      <p>
        Amacı yalnızca rahim içini görmek ve gerekirse odak biyopsisi almaktır. Genellikle 2,7–3 mm
        çapında ince histeroskoplarla, servikal (rahim ağzı) dilatasyona gerek kalmadan yapılır.
        Anestezisiz veya hafif sedasyonla 5–15 dakikada tamamlanır. İşlemden çıkan hastalar aynı gün
        normal yaşamlarına dönebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Operatif histeroskopi</h3>
      <p>
        Rahim içindeki bir patolojiyi (polip, miyom, septum, yapışıklık) aynı seansta çıkarmak
        amacıyla yapılır. Daha kalın (8–10 mm) operatif histeroskoplar veya hem tanı hem tedavi
        yapabilen ince "see-and-treat" sistemleri kullanılabilir. Ameliyathane koşullarında, genel
        veya spinal anestezi altında uygulanır. İşlem süresi patolojiye göre 20–60 dakika arasında
        değişir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Histeroskopi hangi durumlarda yapılır?
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">Anormal uterin kanama</h3>
      <p>
        Uzayan, düzensiz veya yoğun adet kanamaları; adet aralarındaki lekelenmeler; özellikle
        menopoz sonrası herhangi bir kanama histeroskopinin en sık endikasyonudur. Endometrial
        polip, submukoz miyom, endometrial hiperplazi veya nadiren endometrium kanseri gibi nedenler
        hedefe yönelik biyopsi ile ayırt edilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Endometrial polip</h3>
      <p>
        Rahim iç zarından kaynaklanan iyi huylu büyümelerdir. Kanama ve kısırlığa neden olabilirler.
        Histeroskopik polipektomi ile kökünden çıkarılmaları hem şikayetleri gidermek hem de
        malignite riskini değerlendirmek için altın standarttır. Kör küretaj poliplerin %30–50'sini
        atlayabilir; histeroskopide böyle bir risk yoktur.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Submukoz miyom</h3>
      <p>
        Rahim iç boşluğuna doğru büyüyen miyomlar (FIGO tip 0, 1 ve seçilmiş tip 2) genellikle
        histeroskopik olarak çıkarılabilir. Yoğun adet kanaması, kısırlık ve tekrarlayan düşük gibi
        sorunlara yol açan bu miyomlarda histeroskopik miyomektomi karın açmayı gerektirmeyen, rahmi
        tümüyle koruyan bir tedavi seçeneğidir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Uterin septum ve konjenital anomaliler
      </h3>
      <p>
        Rahim boşluğunu ikiye bölen fibröz-müsküler bir perde olan uterin septum, tekrarlayan
        gebelik kayıplarının ve preterm doğumun düzeltilebilir nedenlerinden biridir. Histeroskopik
        septum rezeksiyonu (metroplasti) düşük oranlarını belirgin biçimde azaltır ve canlı doğum
        şansını artırır.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Rahim içi yapışıklık (Asherman sendromu)
      </h3>
      <p>
        Küretaj, enfeksiyon veya cerrahi sonrası rahim iç zarında oluşan yapışıklıklar; adet
        azlığı/kesilmesi, kısırlık ve tekrarlayan düşüklerle karşımıza çıkar. Histeroskopik
        adezyolizis (yapışıklıkların açılması) tedavinin temelidir; işlem sonrası östrojen tedavisi
        ve bazı olgularda geçici rahim içi balon uygulaması yeniden yapışmayı önlemek için
        kullanılabilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Kısırlık araştırması</h3>
      <p>
        HSG veya ultrasonografide şüpheli bulgu olan çiftlerde, tekrarlayan tüp bebek
        başarısızlıklarında ve tekrarlayan gebelik kayıplarında histeroskopi rahim içini net biçimde
        değerlendirir. Sessiz kalmış polip, miyom, septum veya yapışıklıklar bu şekilde tanınıp aynı
        seansta düzeltilebilir.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Yerinden çıkmış / gömülü spiral (RİA)
      </h3>
      <p>
        İpi görünmeyen veya rahim duvarına gömülmüş rahim içi araçlar, histeroskopik olarak güvenle
        çıkarılabilir. Bu durum, kör çekme denemeleri sonrası oluşabilecek yaralanmaların önüne
        geçer.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">İşlem öncesi hazırlık</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Zamanlama:</strong> Adet gören kadınlarda işlem, adetin bitiminden hemen sonraki
          erken foliküler dönemde (siklusun 6.–12. günleri arasında) planlanır. Bu dönemde rahim iç
          zarı ince olduğundan görüş netliği en yüksektir.
        </li>
        <li>
          <strong>Gebelik dışlanır:</strong> İşlem öncesi mutlaka gebelik olmadığından emin olunur.
        </li>
        <li>
          <strong>Enfeksiyon taraması:</strong> Aktif vajinal veya pelvik enfeksiyon varsa önce
          tedavi edilir.
        </li>
        <li>
          <strong>Ağrı kesici:</strong> Ofis histeroskopide işlem öncesi ağızdan ağrı kesici
          (naproksen, ibuprofen) verilebilir.
        </li>
        <li>
          <strong>Anestezi:</strong> Operatif histeroskopide 6–8 saatlik açlık gerekir; kullanılan
          kan sulandırıcı ilaçlar önceden değerlendirilir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">İşlem nasıl yapılır?</h2>
      <ol className="list-decimal space-y-2 pl-6">
        <li>Hasta jinekolojik pozisyonda yatırılır; vajina antiseptik solüsyonla temizlenir.</li>
        <li>
          Ofis histeroskopide çoğu zaman spekulum kullanılmadan, "vajinoskopik" yaklaşımla
          histeroskop doğrudan vajina yoluyla rahim ağzından geçirilir.
        </li>
        <li>
          Rahim boşluğunun görülebilmesi için serum fizyolojik (izotonik) sıvı düşük basınçla rahim
          içine verilir; bu sayede kavite genişler ve duvarlar birbirinden ayrılır.
        </li>
        <li>
          Rahim iç yüzeyi, tüplerin rahime açıldığı noktalar (tuba ostiumları) ve rahim ağzı kanalı
          sistematik olarak incelenir; şüpheli alanlardan biyopsi alınabilir.
        </li>
        <li>
          Operatif işlemde polip, miyom veya septum; monopolar/bipolar rezektoskop, ince makas veya
          morselatör gibi araçlarla çıkarılır. Yapışıklıklar keskin diseksiyonla açılır.
        </li>
      </ol>

      <h2 className="mt-10 font-display text-2xl font-semibold">İşlem sonrası: iyileşme süreci</h2>
      <p>
        Tanısal histeroskopiden sonra hafif kramp ve 1–3 gün süren az miktarda lekelenme
        beklenebilir. Operatif histeroskopi sonrası da lekelenme genellikle 1 haftaya kadar
        uzayabilir. Aşağıdaki öneriler yaygın olarak verilir:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>İlk 1–2 hafta cinsel ilişki, tampon ve havuz/kaplıca kullanımından kaçınılması.</li>
        <li>Ağır egzersiz ve ağır kaldırmanın 1 hafta ertelenmesi.</li>
        <li>Hafif ağrı için parasetamol veya doktorun önerdiği bir NSAİİ kullanılması.</li>
        <li>
          Ateş, kötü kokulu akıntı, artan ağrı veya ıslak-ped dolduran kanama halinde vakit
          kaybetmeden hekime başvurulması.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Histeroskopi güvenli midir? Riskleri nelerdir?
      </h2>
      <p>
        Histeroskopi jinekolojik cerrahinin en güvenli işlemlerinden biridir; genel komplikasyon
        oranı %1'in altındadır. Yine de her cerrahi girişimde olduğu gibi bilinmesi gereken riskler
        vardır:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Uterin perforasyon:</strong> Histeroskopun rahim duvarını delmesi (%0,1–1).
          Çoğunlukla ek girişim gerektirmez, gözlem yeterlidir; nadiren laparoskopik onarım
          gerekebilir.
        </li>
        <li>
          <strong>Enfeksiyon:</strong> Nadirdir (%0,1–0,9). Ateş ve kötü kokulu akıntı ile kendini
          gösterir; antibiyotik ile tedavi edilir.
        </li>
        <li>
          <strong>Sıvı yüklenmesi (fluid overload):</strong> Özellikle uzun süren operatif
          işlemlerde, kullanılan distansiyon sıvısının damara emilimine bağlı olabilir. Modern takip
          sistemleri ve serum fizyolojik kullanımı bu riski minimuma indirir.
        </li>
        <li>
          <strong>Kanama:</strong> Genellikle hafif ve kendiliğinden durur.
        </li>
        <li>
          <strong>Servikal yaralanma:</strong> Rahim ağzının hafif yırtılması; nadir ve çoğunlukla
          tedavi gerektirmez.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Histeroskopi ile küretaj (D&amp;C) arasındaki fark
      </h2>
      <p>
        Klasik küretaj rahim içinin körlemesine kazınmasıdır; işlemi yapan hekim rahim içini
        göremez. Bu nedenle odaksal bir patoloji (polip, submukoz miyom) atlanabilir. Histeroskopide
        rahim içi doğrudan görülür ve patoloji hedefli olarak çıkarılır. Bu, histeroskopiyi hem daha
        doğru hem daha az travmatik bir yöntem yapar. Günümüzde birçok merkezde tanısal küretajın
        yerini "görerek biyopsi" (see-and-treat histeroskopi) almıştır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Histeroskopi ve gebelik şansı</h2>
      <p>
        Endometrial polip, submukoz miyom, uterin septum ve rahim içi yapışıklıkların histeroskopik
        olarak giderilmesinin gebelik ve canlı doğum oranlarını iyileştirdiği çok sayıda çalışmada
        gösterilmiştir. Tekrarlayan tüp bebek başarısızlıklarında transfer öncesi "ofis
        histeroskopi" birçok merkezde rutin öncesi değerlendirme olarak yer alır. İşlem sonrası 1–2
        adet sonrası gebelik denemelerine başlanabilir; miyomektomi gibi rahim duvarına derin
        müdahale gerektiren operatif histeroskopilerde bekleme süresi hekim tarafından bireysel
        olarak belirlenir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <p>
        Aşağıdaki durumlarda histeroskopiden fayda görebileceğiniz için kadın hastalıkları ve doğum
        uzmanına danışmanız önerilir:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Uzayan, düzensiz veya yoğun adet kanamaları.</li>
        <li>Menopoz sonrası her türlü kanama veya lekelenme.</li>
        <li>Bir yıllık düzenli ilişkiye rağmen gebe kalamama (35 yaş üstünde 6 ay).</li>
        <li>İki veya daha fazla ardışık gebelik kaybı.</li>
        <li>Ultrasonografi veya HSG'de rahim içinde şüpheli bulgu.</li>
        <li>İpi kaybolmuş veya rahime gömülmüş rahim içi araç (spiral).</li>
      </ul>

      <p className="mt-8">
        Histeroskopi bugün jinekoloji pratiğinin en değerli araçlarından biridir: rahim içindeki
        birçok sorunu hem doğru şekilde tanır hem de aynı seansta, karın kesisi olmadan, hastanın
        günlük yaşamına hızla dönmesini sağlayacak biçimde tedavi eder. Doğru endikasyonla
        planlandığında yüksek başarı ve düşük komplikasyon oranıyla çalışan bir seçenektir.
      </p>
    </ArticleShell>
  );
}

function MenopozPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Menopoz</strong>, yumurtalıkların hormon üretiminin doğal olarak sona ermesiyle adet
        döngüsünün kalıcı biçimde kesilmesidir. Tıbbi tanım geriye dönüktür: başka bir neden
        olmaksızın <strong>12 ay boyunca hiç adet görülmemesi</strong> menopoz olarak kabul edilir.
        Türkiye'de ortalama menopoz yaşı 48–51 arasındadır. Bu, bir hastalık değil fizyolojik bir
        geçiş dönemidir; ancak beraberinde getirdiği belirtiler ve uzun vadeli sağlık etkileri çoğu
        kadında tedavi edilebilir niteliktedir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Menopoz öncesi dönem: perimenopoz
      </h2>
      <p>
        Menopozdan önceki 4–8 yıllık geçiş dönemine <strong>perimenopoz</strong> denir. Bu dönemde
        yumurtalık rezervi azalırken östrojen düzeyi düzensiz biçimde iniş çıkış gösterir. Adetler
        önce sıklaşır, sonra seyrekleşir; kanama miktarı değişkenleşir. Ateş basması, uyku bozukluğu
        ve ruh hâli dalgalanmaları genellikle ilk bu dönemde başlar. Önemli bir ayrıntı:
        perimenopozda gebelik hâlâ mümkündür; korunmaya son adetten sonraki 12 ay boyunca devam
        edilmelidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Menopoz belirtileri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Vazomotor belirtiler:</strong> Ateş basması ve gece terlemesi. Kadınların %75'inde
          görülür, ortalama 7 yıl sürebilir.
        </li>
        <li>
          <strong>Uyku bozukluğu ve yorgunluk:</strong> Gece terlemeleriyle bölünen uyku, gündüz
          konsantrasyon güçlüğü yaratır.
        </li>
        <li>
          <strong>Genitoüriner sendrom:</strong> Vajinal kuruluk, ilişkide ağrı, sık idrara çıkma ve
          tekrarlayan idrar yolu enfeksiyonu. Diğer belirtilerin aksine tedavi edilmezse zamanla
          ilerler.
        </li>
        <li>
          <strong>Ruhsal belirtiler:</strong> Duygudurum dalgalanması, sinirlilik, kaygı ve depresif
          belirtiler.
        </li>
        <li>
          <strong>Kemik kaybı:</strong> Östrojen düşüşüyle kemik yıkımı hızlanır; ilk 5–7 yılda
          kemik yoğunluğunun %10–20'si kaybedilebilir. Sessiz ilerler, kırıkla fark edilir.
        </li>
        <li>
          <strong>Metabolik değişiklikler:</strong> Karın bölgesinde yağlanma, kolesterol profilinde
          bozulma, kardiyovasküler risk artışı.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı nasıl konur?</h2>
      <p>
        45 yaş üstünde, tipik şikâyetleri olan ve adetleri kesilmiş bir kadında tanı kliniktir;
        rutin hormon testine gerek yoktur. FSH ve estradiol ölçümü şu durumlarda yardımcıdır: 40–45
        yaş arasında adet kesilmesi, 40 yaş altında adet kesilmesi (prematüre ovaryan yetmezlik
        şüphesi), rahmi alınmış kadınlar ve hormonal doğum kontrolü kullananlar. Tiroid fonksiyon
        testleri, benzer şikâyet yaratan tiroid hastalıklarını dışlamak için sıklıkla birlikte
        istenir.
      </p>
      <p>
        <strong>Uyarı:</strong> Menopoz sonrası ortaya çıkan her türlü vajinal kanama normal
        değildir ve mutlaka değerlendirilmelidir. Endometrium kalınlığının ultrasonla ölçülmesi ve
        gerekirse endometrial biyopsi veya histeroskopi yapılması gerekir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Hormon replasman tedavisi (HRT) nedir?
      </h2>
      <p>
        HRT, azalan östrojenin dışarıdan yerine konmasıdır. Rahmi yerinde olan kadınlarda rahim iç
        zarını korumak için mutlaka <strong>progesteron</strong> eklenir; rahmi alınmış kadınlarda
        tek başına östrojen yeterlidir. Uygulama biçimleri:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Transdermal (bant/jel):</strong> Karaciğerden ilk geçiş etkisi olmadığı için
          pıhtılaşma riski ağızdan alınan forma göre daha düşüktür; migren, hipertansiyon ve yüksek
          trigliserid varlığında tercih edilir.
        </li>
        <li>
          <strong>Ağızdan (tablet):</strong> Kullanımı pratiktir, etkinliği yüksektir.
        </li>
        <li>
          <strong>Lokal vajinal östrojen:</strong> Yalnız vajinal kuruluk ve idrar şikâyetleri varsa
          kullanılır. Kana geçişi ihmal edilebilir düzeydedir, sistemik risk taşımaz ve progesteron
          eklenmesi gerekmez.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">HRT kimlere uygundur?</h2>
      <p>
        Güncel kılavuzların ortak görüşü olan <strong>"fırsat penceresi"</strong> yaklaşımına göre;
        tedaviye <strong>60 yaş altında</strong> ve <strong>menopozun ilk 10 yılı içinde</strong>{" "}
        başlanan sağlıklı kadınlarda fayda/risk dengesi olumludur. Bu kadınlarda HRT ateş basmasını
        %75'e varan oranda azaltır, uyku ve yaşam kalitesini iyileştirir, kemik kaybını durdurur ve
        kırık riskini düşürür. 40 yaş altında menopoza giren kadınlarda ise HRT bir tercih değil, en
        az doğal menopoz yaşına kadar sürdürülmesi gereken bir gerekliliktir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Riskler ve kontrendikasyonlar</h2>
      <p>
        HRT'nin riskleri, başlama yaşı ve kullanılan rejimle yakından ilişkilidir. Kombine
        (östrojen+progesteron) tedavide 5 yılı aşan kullanımda meme kanseri riskinde küçük ama
        gerçek bir artış bildirilmiştir; yalnız östrojen tedavisinde böyle bir artış
        gösterilmemiştir. Ağızdan östrojen venöz tromboz riskini artırırken transdermal formlarda bu
        artış belirgin değildir. Tedavi <strong>kontrendikasyon</strong> oluşturan durumlar:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Aktif veya geçirilmiş meme kanseri ve östrojene duyarlı diğer kanserler.</li>
        <li>Açıklanamayan vajinal kanama.</li>
        <li>Geçirilmiş derin ven trombozu veya pulmoner emboli.</li>
        <li>Geçirilmiş inme veya kalp krizi, kontrolsüz hipertansiyon.</li>
        <li>Aktif karaciğer hastalığı.</li>
      </ul>
      <p>
        Tedavi başlandıktan sonra ilk 3. ayda, ardından yılda bir kez yeniden değerlendirilir; meme
        muayenesi ve mamografi takibi aksatılmamalıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hormonsuz tedavi seçenekleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ateş basmasında:</strong> Düşük doz SSRI/SNRI grubu ilaçlar, gabapentin, klonidin
          ve yeni kuşak nonhormonal NK3 reseptör antagonistleri.
        </li>
        <li>
          <strong>Vajinal kurulukta:</strong> Su bazlı kayganlaştırıcılar, uzun etkili vajinal
          nemlendiriciler ve çok düşük doz lokal östrojen.
        </li>
        <li>
          <strong>Kemik sağlığında:</strong> Günlük 1000–1200 mg kalsiyum, D vitamini desteği,
          yürüyüş ve direnç egzersizleri; gerekirse bifosfonatlar.
        </li>
        <li>
          <strong>Yaşam biçimi:</strong> Sigaranın bırakılması, kilo kontrolü, alkol ve kafeinin
          azaltılması, katmanlı giyinme ve düzenli uyku hijyeni.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Menopozda düzenli kontroller</h2>
      <p>
        Menopoz, yıllık genel sağlık taramasının önem kazandığı bir dönemdir: meme muayenesi ve
        mamografi, servikal kanser taraması (smear/HPV), tansiyon ve kan şekeri ölçümü, lipit
        profili, tiroid fonksiyonları ve uygun yaşta DEXA kemik yoğunluğu ölçümü planlanmalıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Menopoz sonrası herhangi bir kanama veya lekelenme.</li>
        <li>40 yaşından önce adetlerin kesilmesi.</li>
        <li>Günlük yaşamı bozan ateş basması, uykusuzluk veya duygudurum değişiklikleri.</li>
        <li>İlişkide ağrı, vajinal kuruluk ve tekrarlayan idrar yolu enfeksiyonları.</li>
        <li>Boy kısalması, sırt ağrısı veya küçük travmayla oluşan kırık.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da menopoz takibi</h2>
      <p>
        Menopoz yönetiminde doğru yaklaşım, herkese aynı reçeteyi yazmak değil; yaş, menopoza giriş
        süresi, şikâyetlerin şiddeti, kişisel ve ailesel risk faktörlerini birlikte değerlendirerek
        bireysel bir plan kurmaktır. Op. Dr. Sadık Kükrer, Adana'da menopoz döneminin
        değerlendirilmesi, hormon replasman tedavisinin uygunluk analizi, hormonsuz seçenekler ve
        kemik sağlığının takibi konularında kapsamlı bir yaklaşım sunar.
      </p>
    </ArticleShell>
  );
}

function KisirlikPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>İnfertilite (kısırlık)</strong>, düzenli ve korunmasız cinsel ilişkiye rağmen
        gebeliğin oluşmamasıdır. Tanı için kabul edilen süre 35 yaşın altındaki çiftlerde{" "}
        <strong>12 ay</strong>, 35 yaş ve üzerindeki kadınlarda <strong>6 ay</strong>dır. Toplumda
        her 6–7 çiftten biri bu tanımın içine girer. Önemli olan, sorunun kalıcı bir kısırlık
        anlamına gelmemesidir: çiftlerin büyük bölümünde neden bulunabilir ve uygun tedaviyle
        gebelik sağlanabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Değerlendirme ne zaman başlamalı?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>35 yaş altı: 12 ay denemeye rağmen gebelik yoksa.</li>
        <li>35 yaş ve üzeri: 6 ay yeterlidir.</li>
        <li>40 yaş üzeri: beklemeden, ilk başvuruda değerlendirme yapılır.</li>
        <li>
          Adet düzensizliği, geçirilmiş pelvik enfeksiyon, endometriozis, geçirilmiş pelvik cerrahi,
          iki ve üzeri gebelik kaybı ya da bilinen erkek faktörü varsa süre beklenmez.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kadına bağlı nedenler</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Yumurtlama bozuklukları:</strong> En sık neden polikistik over sendromudur (PCOS).
          Tiroid hastalıkları ve yüksek prolaktin de yumurtlamayı bozar.
        </li>
        <li>
          <strong>Tubal faktör:</strong> Geçirilmiş enfeksiyon, dış gebelik veya cerrahiye bağlı tüp
          tıkanıklığı ve yapışıklıklar.
        </li>
        <li>
          <strong>Endometriozis:</strong> Hem yumurta kalitesini hem tüp–yumurtalık ilişkisini
          bozar.
        </li>
        <li>
          <strong>Rahim içi patolojiler:</strong> Polip, submüköz miyom, rahim içi yapışıklık
          (Asherman) ve septum embriyonun tutunmasını engelleyebilir.
        </li>
        <li>
          <strong>Azalmış yumurtalık rezervi:</strong> Yaş, geçirilmiş yumurtalık cerrahisi veya
          kemoterapi sonrası görülür.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Erkeğe bağlı nedenler</h2>
      <p>
        Çiftlerin yaklaşık üçte birinde neden erkeğe aittir; bu nedenle{" "}
        <strong>sperm analizi ilk basamak tetkiktir</strong> ve kadın tetkikleriyle eş zamanlı
        istenmelidir. Sperm sayısı, hareketliliği veya yapısındaki bozukluklar; varikosel;
        geçirilmiş kabakulak orşiti, enfeksiyonlar, hormonal bozukluklar, bazı ilaçlar, sigara ve
        aşırı sıcak maruziyeti başlıca nedenlerdir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hangi tetkikler yapılır?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Sperm analizi (spermiyogram):</strong> 2–5 günlük cinsel perhiz sonrası verilir;
          bozuk çıkarsa 2–4 hafta sonra tekrarlanır.
        </li>
        <li>
          <strong>Yumurtlamanın gösterilmesi:</strong> Adet düzeni, siklusun 21. günü progesteron
          ölçümü veya folikül takibi.
        </li>
        <li>
          <strong>Yumurtalık rezervi:</strong> Adetin 2–4. günü FSH, estradiol, AMH ve ultrasonla
          antral folikül sayımı.
        </li>
        <li>
          <strong>Tüplerin açıklığı:</strong> İlaçlı rahim filmi (HSG) veya ultrason eşliğinde
          HyCoSy.
        </li>
        <li>
          <strong>Hormonal tarama:</strong> TSH ve prolaktin; klinik gerekçe varsa androjenler.
        </li>
        <li>
          <strong>İleri tetkikler:</strong> Şüpheli rahim içi bulguda histeroskopi, endometriozis ya
          da yapışıklık düşünülen olguda laparoskopi.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>
      <p>
        Tedavi, tanıya ve kadının yaşına göre basamaklı biçimde planlanır. Amaç, en az girişimsel
        olan yöntemle en kısa sürede gebelik sağlamaktır.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Yaşam tarzı düzenlemesi:</strong> Kilo kontrolü, sigaranın bırakılması, folik asit
          desteği ve yumurtlama döneminde düzenli ilişki. PCOS'ta %5–10 kilo kaybı tek başına
          yumurtlamayı geri getirebilir.
        </li>
        <li>
          <strong>Yumurtlama tedavisi:</strong> Letrozol veya klomifen sitrat; gerekirse düşük doz
          gonadotropinler ile folikül takibi.
        </li>
        <li>
          <strong>Cerrahi:</strong> Histeroskopiyle polip, submüköz miyom, septum ve yapışıklığın
          giderilmesi; laparoskopiyle endometriozis odaklarının temizlenmesi.
        </li>
        <li>
          <strong>Aşılama (IUI):</strong> Hafif erkek faktörü, servikal faktör ve açıklanamayan
          infertilitede, tüpleri açık çiftlerde 3–4 siklus denenir.
        </li>
        <li>
          <strong>Tüp bebek (IVF/ICSI):</strong> İki taraflı tüp tıkanıklığı, ağır erkek faktörü,
          ileri yaş, azalmış rezerv ve önceki tedavilerin başarısız olduğu durumlarda uygulanır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Açıklanamayan infertilite</h2>
      <p>
        Çiftlerin yaklaşık %10–15'inde tüm testler normal bulunur. Bu durum tedavi edilemeyeceği
        anlamına gelmez: yumurtlama tedavisi ile birlikte aşılama, belirli bir süre sonunda gebelik
        sağlanamazsa tüp bebek başarılı sonuç verir. Kadının yaşı, bu grupta bekleme süresini
        belirleyen en önemli etkendir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>35 yaş altında 12 ay, 35 yaş üzerinde 6 ay denemeye rağmen gebelik olmaması.</li>
        <li>Adetlerin düzensiz olması veya hiç görülmemesi.</li>
        <li>Şiddetli adet ağrısı, ilişkide ağrı ya da bilinen endometriozis.</li>
        <li>İki veya daha fazla ardışık gebelik kaybı.</li>
        <li>Erkekte bilinen varikosel, geçirilmiş testis cerrahisi veya kabakulak orşiti.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Adana'da infertilite değerlendirmesi
      </h2>
      <p>
        İnfertilitede en değerli adım, doğru sırayla ve gereksiz test yapmadan ilerleyen bir
        değerlendirmedir. Op. Dr. Sadık Kükrer, Adana'da çiftin birlikte değerlendirilmesi,
        yumurtlama ve rahim içi patolojilerin araştırılması, histeroskopik ve laparoskopik cerrahi
        ile tedavi planlamasında kapsamlı bir yaklaşım sunar.
      </p>
    </ArticleShell>
  );
}

function TaramaTestleriPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Gebelik takibinin en çok merak edilen başlığı tarama testleridir. Amaç, bebekte kromozom
        anomalisi veya yapısal bir sorun bulunma <strong>olasılığını</strong> hesaplamak ve
        gerektiğinde ileri tetkiklere yönlendirmektir. Tarama testleri tanı koymaz; “yüksek risk”
        sonucu bebeğin hasta olduğu anlamına gelmez.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tarama testi takvimi</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>6–10. hafta:</strong> Gebeliğin doğrulanması, kalp atımının görülmesi, ilk kan
          tetkikleri (kan grubu, hemogram, tiroit, enfeksiyon taramaları).
        </li>
        <li>
          <strong>10. haftadan itibaren:</strong> NIPT (anne kanından serbest fetal DNA testi) —
          isteğe bağlı, en yüksek yakalama oranına sahip tarama.
        </li>
        <li>
          <strong>11–14. hafta:</strong> İkili test + ense kalınlığı (NT) ölçümü; burun kemiği ve
          erken yapısal değerlendirme.
        </li>
        <li>
          <strong>16–18. hafta:</strong> Üçlü/dörtlü test (ikili test yapılamadıysa), nöral tüp
          defekti taraması için AFP.
        </li>
        <li>
          <strong>20–22. hafta:</strong> Detaylı (ikinci düzey) ultrason.
        </li>
        <li>
          <strong>24–28. hafta:</strong> Şeker yükleme testi (OGTT) ve kansızlık taraması.
        </li>
        <li>
          <strong>28. hafta:</strong> Kan grubu Rh negatif anne adaylarına Anti-D immünglobulin.
        </li>
        <li>
          <strong>35–37. hafta:</strong> Grup B streptokok (GBS) kültürü ve doğum planlaması.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        İkili test ve ense kalınlığı (NT)
      </h2>
      <p>
        11 hafta 0 gün – 13 hafta 6 gün arasında yapılır. Anne kanında <strong>PAPP-A</strong> ve{" "}
        <strong>serbest beta-hCG</strong> ölçülür; ultrasonda ensedeki sıvı kalınlığı (NT) ve
        baş-popo mesafesi (CRL) değerlendirilir. Bu değerler anne yaşı ile birleştirilerek trizomi
        21 (Down sendromu), trizomi 18 ve 13 riski hesaplanır. Yakalama oranı yaklaşık %90, yanlış
        pozitiflik oranı %5 civarındadır. Aynı muayenede burun kemiği, ductus venosus ve triküspit
        akımı değerlendirilirse doğruluk daha da artar.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Üçlü ve dörtlü test</h2>
      <p>
        İkili test penceresi kaçırıldıysa 16–18. haftalar arasında yapılır. Üçlü testte AFP, hCG ve
        estriol; dörtlü testte bunlara inhibin-A eklenir. Dörtlü testin yakalama oranı yaklaşık
        %80'dir. AFP yüksekliği ayrıca <strong>nöral tüp defekti</strong> (spina bifida) açısından
        uyarıcıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">NIPT: anne kanından DNA testi</h2>
      <p>
        NIPT (non-invaziv prenatal test), anne kanında dolaşan plasenta kaynaklı serbest DNA'yı
        analiz eder ve 10. haftadan itibaren yapılabilir. Down sendromunu yakalama oranı{" "}
        <strong>%99'un üzerindedir</strong>; yanlış pozitiflik oranı binde 1'in altındadır. Cinsiyet
        ve cinsiyet kromozomu anomalileri hakkında da bilgi verir.
      </p>
      <p>
        Buna karşın NIPT bir <strong>tarama</strong> testidir: pozitif sonuç mutlaka amniyosentez
        veya CVS ile doğrulanmalıdır. Ayrıca yapısal anomalileri (kalp, omurga, böbrek) göstermez;
        bu nedenle ultrason taramalarının yerini almaz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Detaylı ultrason</h2>
      <p>
        20–22. haftalarda yapılan ikinci düzey ultrasonda bebeğin beyin ve omurgası, kalp odacıkları
        ve büyük damarları, yüz, dudak-damak, mide, böbrekler, mesane, eller ve ayaklar sistematik
        biçimde incelenir; plasentanın yerleşimi, amniyon sıvısı ve serviks uzunluğu
        değerlendirilir. Yapısal anomalilerin önemli bölümü bu incelemede saptanır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        “Yüksek risk” sonucu ne anlama gelir?
      </h2>
      <p>
        Risk, bir oran olarak verilir. Örneğin 1/150 sonucu, 150 benzer gebeden birinde ilgili
        durumun beklendiğini; yani bu gebelikte{" "}
        <strong>%99,3 olasılıkla etkilenme olmadığını</strong> gösterir. Eşik değerin (genellikle
        1/250–1/300) üzerindeki sonuçlarda seçenekler şunlardır:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Ara riskte NIPT ile ileri tarama.</li>
        <li>
          Kesin tanı için <strong>koryon villus biyopsisi (CVS, 11–14. hafta)</strong> veya{" "}
          <strong>amniyosentez (16–20. hafta)</strong>.
        </li>
        <li>Ayrıntılı fetal ultrason ve gerektiğinde fetal ekokardiyografi.</li>
      </ul>
      <p>
        Girişimsel testlerde gebelik kaybı riski, deneyimli merkezlerde günümüzde yaklaşık binde 1–3
        olarak bildirilmektedir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık yapılan hatalar</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Tarama testini tanı testi sanmak ve sonuçtan panikle karar vermek.</li>
        <li>İkili test penceresini (13 hafta 6 gün) kaçırmak.</li>
        <li>NIPT yaptırdığı için detaylı ultrasonu atlamak.</li>
        <li>Hem ikili hem dörtlü hem NIPT yaptırıp sonuçları birbirinden bağımsız yorumlamak.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Gebelik testi pozitifleşir çıkmaz ilk kontrol için (6–8. hafta).</li>
        <li>Tarama testi sonucunuz eşik değerin üzerinde çıktıysa, karar vermeden önce.</li>
        <li>Ailede kalıtsal hastalık veya önceki gebelikte kromozom anomalisi öyküsü varsa.</li>
        <li>İleri anne yaşı (35 ve üzeri) ya da tekrarlayan gebelik kaybı öyküsü varsa.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da gebelik takibi</h2>
      <p>
        Tarama testlerinin değeri, doğru haftada yapılmasına ve sonuçların bir bütün olarak
        yorumlanmasına bağlıdır. Op. Dr. Sadık Kükrer, Adana'da gebelik takibi, ense kalınlığı
        ölçümü, detaylı ultrason ve riskli gebelik yönetiminde kapsamlı bir değerlendirme sunar.
      </p>
    </ArticleShell>
  );
}

function DogumKontrolPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Doğum kontrolünde “en iyi yöntem” yoktur; <strong>size en uygun yöntem</strong> vardır.
        Seçim yaparken yaş, doğum planı, emzirme durumu, adet düzeni, eşlik eden hastalıklar ve
        yöntemi ne kadar düzenli kullanabileceğiniz birlikte değerlendirilir. Aşağıda yöntemler
        etkinlik, kullanım kolaylığı ve doğurganlığa dönüş açısından karşılaştırılmıştır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Etkinliğe göre yöntemler</h2>
      <p>
        Aşağıdaki oranlar, yöntemi bir yıl boyunca kullanan 100 kadından kaçının gebe kaldığını
        gösterir (gerçek kullanım):
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Kol altı implant:</strong> %0,1 — 3 yıl korur, en etkili geri dönüşümlü yöntem.
        </li>
        <li>
          <strong>Hormonlu spiral (RİA):</strong> %0,1–0,4 — 5–8 yıl korur, adet kanamasını azaltır.
        </li>
        <li>
          <strong>Bakırlı spiral:</strong> %0,8 — 5–10 yıl korur, hormon içermez.
        </li>
        <li>
          <strong>Tüp ligasyonu / vazektomi:</strong> %0,1–0,5 — kalıcı yöntemler.
        </li>
        <li>
          <strong>Üç aylık iğne:</strong> %4 — östrojensizdir, emzirmede kullanılabilir.
        </li>
        <li>
          <strong>Doğum kontrol hapı, vajinal halka, bant:</strong> %7 — düzenli kullanım şarttır.
        </li>
        <li>
          <strong>Prezervatif:</strong> %13 — enfeksiyonlardan koruyan tek yöntem.
        </li>
        <li>
          <strong>Takvim ve geri çekme:</strong> %20–23 — güvenilirliği düşüktür.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hormonal yöntemler</h2>
      <p>
        <strong>Kombine haplar</strong> östrojen ve progesteron içerir; yumurtlamayı baskılar. Adet
        düzensizliği, ağrılı adet, akne ve tüylenmede de fayda sağlar. Aurası olan migren,
        kontrolsüz hipertansiyon, geçirilmiş tromboz, aktif meme kanseri ve 35 yaş üzeri sigara
        kullanımı bu grup için engeldir.
      </p>
      <p>
        <strong>Yalnız progesteron içeren haplar</strong>, <strong>implant</strong> ve{" "}
        <strong>iğneler</strong> östrojen içermediği için emzirenlerde ve östrojenin sakıncalı
        olduğu durumlarda tercih edilir. En sık yan etki, ilk aylarda görülen düzensiz lekelenmedir;
        zamanla azalır ve birçok kadında adet tamamen kesilir. Bu durum zararsızdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Spiral (rahim içi araç)</h2>
      <p>
        Spiral, poliklinik şartlarında birkaç dakikada takılır. <strong>Bakırlı spiral</strong>{" "}
        hormon içermez ancak adet kanamasını ve sancıyı bir miktar artırabilir.{" "}
        <strong>Hormonlu spiral</strong> ise kanamayı belirgin biçimde azaltır; bu nedenle aşırı
        adet kanaması, adenomyozis ve miyoma bağlı kanamalarda tedavi amacıyla da kullanılır. Doğum
        yapmamış kadınlar dahil her yaşta uygulanabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kalıcı yöntemler</h2>
      <p>
        Tüp ligasyonu (kadında tüplerin bağlanması) ve vazektomi (erkekte kanalların bağlanması)
        kalıcı olarak kabul edilir. Ailesini tamamladığından emin olan çiftler için uygundur; geri
        döndürme ameliyatlarının başarısı sınırlıdır. Vazektomi daha basit, daha düşük riskli ve
        lokal anestezi ile yapılabilen bir işlemdir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Acil korunma</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Levonorgestrel içeren hap: ilk 72 saatte, ne kadar erken alınırsa o kadar etkili.</li>
        <li>Ulipristal asetat: 120 saate kadar kullanılabilir, daha yüksek etkinlik sağlar.</li>
        <li>
          Bakırlı spiral: 5 gün içinde takıldığında en etkili yöntemdir ve sonrasında uzun süreli
          korunma sağlar.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Doğurganlığa dönüş</h2>
      <p>
        Hap, halka, bant, implant ve spiral bırakıldıktan sonra yumurtlama genellikle ilk 1–3 ay
        içinde geri döner. Yalnızca üç aylık iğnede gebeliğe dönüş 6–12 ayı bulabilir; yakın zamanda
        gebelik planlayanlarda bu yöntem tercih edilmez.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Yöntem seçimi öncesinde, tıbbi öykünüzün değerlendirilmesi için.</li>
        <li>Hap kullanırken şiddetli baş ağrısı, görme bulanıklığı, bacakta ağrı-şişlik olursa.</li>
        <li>Üç aydan uzun süren düzensiz kanama veya ara kanama varsa.</li>
        <li>Spiral ipini hissetmiyorsanız veya kasık ağrınız-ateşiniz varsa.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da aile planlaması</h2>
      <p>
        Op. Dr. Sadık Kükrer, Adana'da doğum kontrol danışmanlığı, spiral uygulama ve çıkarma,
        implant yerleştirme ve aile planlaması yöntemlerinin kişiye özel seçiminde kapsamlı bir
        değerlendirme sunar.
      </p>
    </ArticleShell>
  );
}

function DogumSekliPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Doğum şekli kararı, anne ve bebeğin sağlığı önceliğiyle verilir. Tıbbi bir engel yoksa
        vajinal doğum çoğu gebelik için en uygun seçenektir; sezaryen ise belirli durumlarda hayat
        kurtaran bir ameliyattır. Doğru karar, gebeliğin seyrine, bebeğin duruşuna ve annenin
        öyküsüne göre birlikte verilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Vajinal doğumun avantajları</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Hastanede kalış 1–2 gün; toparlanma genellikle 1–2 hafta.</li>
        <li>Enfeksiyon, kanama ve tromboz (pıhtı) riski daha düşük.</li>
        <li>Emzirmeye başlama ve bebekle erken temas daha kolay.</li>
        <li>Bebeğin akciğerlerindeki sıvının atılması ve sağlıklı flora oluşumu desteklenir.</li>
        <li>Sonraki gebeliklerde plasenta yerleşim bozukluğu riski artmaz.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Sezaryenin gerekli olduğu durumlar
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Plasenta previa veya plasenta dekolmanı.</li>
        <li>Enine duruş, bazı makat gelişler, kordon sarkması.</li>
        <li>Fetal distres (bebeğin kalp atımında bozulma).</li>
        <li>İlerlemeyen doğum eylemi, baş-pelvis uyumsuzluğu.</li>
        <li>Önceki klasik (dikey) uterus kesisi veya birden fazla sezaryen öyküsü.</li>
        <li>Aktif genital herpes, kontrolsüz HIV gibi bulaş riski yüksek enfeksiyonlar.</li>
        <li>Ağır preeklampsi, bazı çoğul gebelikler ve ileri derecede gelişme geriliği.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sezaryenin riskleri</h2>
      <p>
        Sezaryen büyük bir karın ameliyatıdır. Kanama, enfeksiyon, mesane veya bağırsak yaralanması,
        tromboz ve anesteziye bağlı riskler taşır. İyileşme 2–4 hafta sürer; ağır kaldırma için 4–6
        hafta beklenmelidir. Tekrarlayan sezaryenlerde karın içi yapışıklıklar ve{" "}
        <strong>plasenta akreta</strong> gibi ciddi plasenta yerleşim bozukluklarının riski her
        ameliyatla artar. Bu nedenle çok sayıda çocuk planlayan kadınlarda ilk doğumun şekli
        özellikle önemlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Sezaryen sonrası normal doğum (VBAC)
      </h2>
      <p>
        Tek bir <strong>alt segment (yatay) kesi</strong> ile sezaryen olmuş, bebeği baş gelişte ve
        ek riski bulunmayan kadınlarda vajinal doğum denenebilir. Başarı oranı %60–80'dir. Uterus
        rüptürü riski yaklaşık binde 5 olduğundan, doğum acil sezaryen olanağı bulunan bir merkezde
        ve yakın izlemle yapılmalıdır. Önceki doğumun vajinal olması başarı şansını artırır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ağrı yönetimi ve doğum planı</h2>
      <p>
        Epidural anestezi, doğum ağrısını azaltmanın en etkili ve güvenli yoludur; sezaryen oranını
        artırmaz. Doğum havuzu, hareket serbestliği, nefes ve masaj teknikleri de ağrıyı azaltır.
        Doğum planınızı 32–34. haftalarda hekiminizle yazılı olarak konuşmanız — ağrı tercihleri,
        eşlik durumu, ten tene temas ve erken emzirme — beklentileri netleştirir. Planın esnek
        olması, doğum sırasında gelişebilecek durumlara uyum için gereklidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık yapılan hatalar</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Doğum korkusu nedeniyle danışmanlık almadan sezaryene karar vermek.</li>
        <li>Sezaryen sonrası erken hareketi ihmal etmek (tromboz riski artar).</li>
        <li>39. haftadan önce tıbbi gerekçesiz planlı sezaryen yaptırmak.</li>
        <li>Doğum planını hiç konuşmadan doğum gününe gelmek.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Düzenli ve giderek sıklaşan kasılmalar başladığında.</li>
        <li>Su gelmesi (amniyon sıvısı) durumunda, kasılma olmasa bile hemen.</li>
        <li>Kanama, şiddetli baş ağrısı, görme bulanıklığı veya ani şişlik varsa.</li>
        <li>Bebek hareketlerinde azalma hissediyorsanız.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da doğum planlaması</h2>
      <p>
        Op. Dr. Sadık Kükrer, Adana'da gebelik takibi, doğum planlaması, sezaryen sonrası normal
        doğum değerlendirmesi ve riskli gebelik yönetiminde bireysel bir yaklaşım sunar.
      </p>
    </ArticleShell>
  );
}

function GestasyonelDiyabetPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Gebelik şekeri (gestasyonel diyabet), gebelikte plasenta hormonlarının yol açtığı insülin
        direnci nedeniyle kan şekerinin yükselmesidir. Gebeliklerin yaklaşık{" "}
        <strong>%7–14'ünde</strong> görülür ve çoğu kadında hiçbir belirti vermez. Bu nedenle tanı,
        belirtiye değil <strong>taramaya</strong> dayanır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kimlerde risk daha yüksek?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Vücut kitle indeksi 30'un üzerinde olmak.</li>
        <li>Birinci derece akrabada tip 2 diyabet öyküsü.</li>
        <li>Önceki gebelikte gebelik şekeri veya 4000 gramın üzerinde bebek doğurmuş olmak.</li>
        <li>Polikistik over sendromu (PCOS).</li>
        <li>35 yaş üzeri gebelik, çoğul gebelik.</li>
        <li>Açıklanamayan ölü doğum öyküsü.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tarama ve tanı</h2>
      <p>
        Tarama tüm gebelere <strong>24–28. haftalar</strong> arasında önerilir; risk faktörü
        olanlarda ilk kontrolde de bakılır.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Tek aşamalı (75 g OGTT):</strong> Açlık ≥92 mg/dL, 1. saat ≥180 mg/dL veya 2. saat
          ≥153 mg/dL değerlerinden biri aşılırsa tanı konur.
        </li>
        <li>
          <strong>İki aşamalı:</strong> Açlık gerektirmeyen 50 g tarama testinde 1. saat &gt;140
          mg/dL ise, 100 g doğrulama testi yapılır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hedef kan şekeri değerleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Açlık: 95 mg/dL'nin altı</li>
        <li>Yemekten 1 saat sonra: 140 mg/dL'nin altı</li>
        <li>Yemekten 2 saat sonra: 120 mg/dL'nin altı</li>
      </ul>
      <p>
        Ölçümler genellikle günde dört kez (sabah açlık ve üç ana öğün sonrası) yapılır ve bir
        günlük ile kayıt altına alınır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Beslenme ve egzersiz</h2>
      <p>
        Kadınların yaklaşık <strong>%70–85'i</strong> yalnızca yaşam tarzı düzenlemesiyle hedeflere
        ulaşır.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Günlük karbonhidratı üç ana ve 2–3 ara öğüne bölün; öğün atlamayın.</li>
        <li>Şekerli içecekler, meyve suyu, beyaz un ve hazır tatlıları tamamen çıkarın.</li>
        <li>Tam tahıl, baklagil, sebze ve yeterli protein tüketin.</li>
        <li>Karbonhidratı protein veya sağlıklı yağla birlikte alın; şeker daha yavaş yükselir.</li>
        <li>Her öğünden sonra 15–20 dakika tempolu yürüyün.</li>
        <li>
          Kahvaltı, insülin direncinin en yüksek olduğu öğündür; en düşük karbonhidratlı öğün
          olmalıdır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">İlaç tedavisi</h2>
      <p>
        Beslenme ve egzersize rağmen 1–2 hafta içinde hedeflere ulaşılamazsa insülin başlanır.
        İnsülin plasentayı geçmez ve gebelikte en güvenli seçenektir; doz gebelik ilerledikçe
        artırılabilir ve doğumdan sonra genellikle kesilir. Metformin bazı durumlarda alternatif
        olarak kullanılabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Bebek ve gebelik takibi</h2>
      <p>
        Gestasyonel diyabette bebeğin büyümesi 4 haftada bir ultrason ile izlenir; amniyon sıvısı ve
        bebek hareketleri değerlendirilir. Kan şekeri kontrol altındaysa doğum genellikle 39–40.
        haftalarda planlanır. Tahmini bebek ağırlığı 4500 gramın üzerindeyse sezaryen konuşulur.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Doğum sonrası izlem</h2>
      <p>
        Kan şekeri çoğu kadında doğumdan sonra normale döner. Ancak{" "}
        <strong>6–12 hafta sonra 75 g OGTT</strong> tekrarlanmalı, sonrasında 1–3 yılda bir tarama
        yapılmalıdır. Gebelik şekeri geçiren kadınlarda ilerleyen yıllarda tip 2 diyabet gelişme
        riski belirgin biçimde artar; emzirme, kilo kontrolü ve haftada 150 dakika egzersiz bu riski
        azaltır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman hekime başvurmalı?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Ölçümlerinizin üçte birinden fazlası hedeflerin üzerindeyse.</li>
        <li>Açlık şekeri sürekli 95 mg/dL'nin üzerinde seyrediyorsa.</li>
        <li>Şiddetli baş ağrısı, görme bulanıklığı, ani şişlik (preeklampsi belirtileri) varsa.</li>
        <li>Bebek hareketlerinde azalma fark ederseniz.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Adana'da riskli gebelik takibi</h2>
      <p>
        Op. Dr. Sadık Kükrer, Adana'da gebelik şekeri taraması, beslenme planlaması, insülin
        tedavisinin yönetimi ve riskli gebelik takibinde bütüncül bir izlem sunar.
      </p>
    </ArticleShell>
  );
}

function LaparoskopiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Laparoskopi, halk arasında <strong>&quot;kapalı ameliyat&quot;</strong> olarak bilinen
        yöntemdir. Karın duvarına açılan 5–10 mm&apos;lik birkaç küçük kesiden yerleştirilen kamera
        ve ince cerrahi aletlerle yapılır. Karın boşluğu karbondioksit gazıyla şişirilerek
        organların rahat görülmesi sağlanır ve ameliyat, yüksek çözünürlüklü monitör eşliğinde
        yürütülür. Günümüzde jinekolojik ameliyatların büyük bölümü bu yolla yapılabilmektedir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hangi hastalıklarda uygulanır?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Yumurtalık kistleri:</strong> kistin çıkarılması (kistektomi) veya gerekirse
          yumurtalığın alınması.
        </li>
        <li>
          <strong>Endometriozis ve çikolata kisti:</strong> odakların temizlenmesi, yapışıklıkların
          açılması, ağrının azaltılması.
        </li>
        <li>
          <strong>Miyomlar:</strong> rahmi koruyan miyom çıkarılması (miyomektomi).
        </li>
        <li>
          <strong>Dış gebelik ve yumurtalık torsiyonu:</strong> acil ve organ koruyucu cerrahi.
        </li>
        <li>
          <strong>Kısırlık araştırması:</strong> tüplerin açıklığının boya testiyle
          değerlendirilmesi, pelvik yapışıklıkların giderilmesi.
        </li>
        <li>
          <strong>Kronik pelvik ağrı:</strong> nedeni görüntülemeyle açıklanamayan olgularda tanısal
          laparoskopi.
        </li>
        <li>
          <strong>Rahim alınması (histerektomi)</strong> ve seçilmiş jinekolojik onkoloji
          ameliyatları.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ameliyat öncesi hazırlık</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Kan sayımı, biyokimya, pıhtılaşma testleri ve gerekli görülürse EKG-akciğer grafisi.
        </li>
        <li>Ultrason, gerekirse MR ile lezyonun ayrıntılı değerlendirilmesi.</li>
        <li>Ameliyattan 6–8 saat önce aç kalınması.</li>
        <li>
          Kan sulandırıcı ilaçların hekim onayıyla düzenlenmesi; sigaranın en az iki hafta önce
          bırakılması iyileşmeyi hızlandırır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">İşlem nasıl yapılır?</h2>
      <p>
        Ameliyat genel anestezi altında yapılır. Göbek içinden girilerek karın boşluğu gazla
        şişirilir ve kamera yerleştirilir. Alt karın bölgesine açılan 1–3 ek kesiden aletler
        girilir. İşlem tamamlandığında gaz boşaltılır, kesiler estetik dikişlerle kapatılır. Süre
        tanısal işlemlerde 20–40 dakika, ileri cerrahilerde 1,5–3 saattir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Açık ameliyata göre avantajları</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Daha az kan kaybı ve daha düşük yara enfeksiyonu oranı.</li>
        <li>Daha az ameliyat sonrası ağrı ve daha az ağrı kesici ihtiyacı.</li>
        <li>Kısa hastanede kalış: çoğu hastada aynı gün veya ertesi gün taburculuk.</li>
        <li>Daha az karın içi yapışıklık ve daha küçük ameliyat izleri.</li>
        <li>Günlük yaşama ve işe belirgin biçimde daha hızlı dönüş.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Riskler ve komplikasyonlar</h2>
      <p>
        Ciddi komplikasyon oranı <strong>%1&apos;in altındadır</strong>. Nadiren damar, bağırsak,
        mesane veya üreter yaralanması, kanama, enfeksiyon, kesi yerinde fıtık ve anesteziye bağlı
        sorunlar görülebilir. Yaygın yapışıklık, çok büyük kitleler veya yüksek vücut kitle indeksi
        durumunda ameliyat güvenlik gerekçesiyle açık cerrahiye çevrilebilir; bu bir başarısızlık
        değil, hasta güvenliğini önceleyen bir karardır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">İyileşme süreci</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          İlk 24–48 saatte omuz ve sırt ağrısı olabilir; bu, karın içinde kalan gaza bağlıdır ve
          yürümekle hızla geçer.
        </li>
        <li>
          Kesi yerleri 7–10 günde iyileşir; su geçirmeyen kapamayla duş genellikle serbesttir.
        </li>
        <li>Masa başı işe dönüş 5–10 gün, ağır iş ve spora dönüş 3–4 haftadır.</li>
        <li>
          Ateş, artan karın ağrısı, kesi yerinden akıntı, bacakta şişlik veya nefes darlığı halinde
          vakit kaybetmeden hekime başvurulmalıdır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sık sorulan sorular</h2>
      <p>
        Aşağıdaki başlıklar poliklinikte en sık sorulan sorulardır; kendi durumunuz için karar,
        muayene ve görüntüleme sonuçlarınızla birlikte verilmelidir.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da jinekolojik laparoskopi (kapalı ameliyat), endometriozis
        ve miyom cerrahisi konularında hizmet vermektedir.
      </p>
    </ArticleShell>
  );
}

function LaparoskopikHisterektomiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Histerektomi, rahmin cerrahi olarak alınmasıdır. <strong>Laparoskopik histerektomi</strong>{" "}
        ise bu ameliyatın karın duvarındaki 5–10 mm&apos;lik birkaç küçük kesiden, kamera eşliğinde
        yapılan biçimidir. Rahim çoğunlukla vajinal yoldan çıkarılır ve vajina üst ucu laparoskopik
        olarak dikilir; böylece büyük karın kesisine gerek kalmaz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hangi durumlarda gerekir?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>İlaç tedavisine ve rahim içi girişimlere yanıt vermeyen aşırı adet kanaması.</li>
        <li>Çok sayıda, büyük veya belirti veren miyomlar.</li>
        <li>Adenomyozis kaynaklı şiddetli ağrı ve kanama.</li>
        <li>İleri evre endometriozis.</li>
        <li>Rahim sarkması (uterin prolapsus).</li>
        <li>Rahim içi kanser öncesi lezyonlar (atipili hiperplazi) ve seçilmiş kanser olguları.</li>
      </ul>
      <p>
        Doğurganlığın korunması isteniyorsa önce miyomektomi, rahim içi ablasyon, hormonlu spiral
        veya embolizasyon gibi rahmi koruyan seçenekler değerlendirilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ameliyat teknikleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Total laparoskopik histerektomi:</strong> rahim ve rahim ağzı birlikte alınır; en
          sık uygulanan yöntemdir.
        </li>
        <li>
          <strong>Subtotal (supraservikal) histerektomi:</strong> rahim ağzı yerinde bırakılır; bu
          durumda smear taraması sürdürülür.
        </li>
        <li>
          <strong>Laparoskopi yardımlı vajinal histerektomi:</strong> ameliyatın bir bölümü vajinal
          yoldan tamamlanır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Yumurtalıklar alınır mı?</h2>
      <p>
        Rahmin alınması yumurtalıkların da alınacağı anlamına gelmez. Menopoz öncesi kadınlarda
        tıbbi bir gereklilik yoksa yumurtalıklar korunur; hormon üretimi sürer ve{" "}
        <strong>cerrahi menopoz gelişmez</strong>. Yumurtalıkların alınması gerekirse menopoz
        belirtileri aniden başlayabilir ve uygun olgularda hormon tedavisi konuşulur. Tüplerin
        alınması (salpenjektomi) ise yumurtalık kanseri riskini azalttığı için sıklıkla ameliyata
        eklenir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ameliyat ve hastane süreci</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Genel anestezi altında, ortalama 1,5–3 saat.</li>
        <li>İdrar sondası genellikle ilk 24 saat içinde alınır.</li>
        <li>
          İlk 6 saat içinde ayağa kalkmak, tromboz riskini azaltır ve bağırsakları çalıştırır.
        </li>
        <li>Taburculuk çoğunlukla 1–2 gün içindedir.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">İyileşme takvimi</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>1. hafta:</strong> ağrı basit ağrı kesicilerle kontrol edilir; kısa ve sık
          yürüyüşler önerilir.
        </li>
        <li>
          <strong>2–4. hafta:</strong> günlük yaşam etkinliklerine dönüş; 5 kilogramdan ağır
          kaldırmaktan kaçınılır.
        </li>
        <li>
          <strong>4–6. hafta:</strong> ağır iş, spor ve cinsel ilişkiye dönüş — vajinal dikişlerin
          iyileşmesi tamamlandıktan sonra.
        </li>
      </ul>
      <p>
        Bu dönemde hafif lekelenme normaldir. Ateş, kötü kokulu akıntı, yoğun kanama, artan ağrı,
        bacakta şişlik veya nefes darlığı acil değerlendirme gerektirir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ameliyat sonrası yaşam</h2>
      <p>
        Adet kanaması sona erer ve gebelik mümkün olmaz. Yumurtalıklar korunduğunda hormonal dengede
        belirgin bir değişiklik beklenmez. Çalışmalar, iyileşme tamamlandıktan sonra cinsel işlevin
        çoğu kadında değişmediğini, ağrı ve kanama ortadan kalktığı için sıklıkla iyileştiğini
        göstermektedir. Pelvik taban egzersizleri, mesane ve cinsel işlev açısından yararlıdır.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da laparoskopik histerektomi ve ileri jinekolojik kapalı
        cerrahi uygulamaları yapmaktadır.
      </p>
    </ArticleShell>
  );
}

function PreeklampsiPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Preeklampsi</strong>, halk arasında gebelik zehirlenmesi olarak da bilinir.
        Gebeliğin 20. haftasından sonra ilk kez ortaya çıkan yüksek tansiyona idrarda protein veya
        böbrek, karaciğer, kan pıhtılaşması, beyin ya da plasenta işlevini etkileyen bulguların
        eşlik etmesiyle tanımlanır. Gebeliklerin yaklaşık %2–8&apos;inde görülür ve erken tanı ile
        yakın takip, anne ve bebek sağlığı için belirleyicidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Uyarı belirtileri: ne zaman acil başvurmalı?
      </h2>
      <p>
        Preeklampsi başlangıçta hiçbir belirti vermeyebilir; bu nedenle her gebelik kontrolünde
        tansiyon ölçülür. Aşağıdaki belirtilerden biri varsa beklemeden hastaneye başvurun:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Ağrı kesiciyle geçmeyen şiddetli baş ağrısı</li>
        <li>Görme bulanıklığı, ışık çakmaları veya çift görme</li>
        <li>Sağ üst karın ya da mide bölgesinde ağrı, bulantı-kusma</li>
        <li>Yüzde, ellerde veya ayaklarda ani ve belirgin şişlik</li>
        <li>Nefes darlığı, idrar miktarında azalma</li>
        <li>Bebek hareketlerinde azalma</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risk faktörleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Önceki gebelikte preeklampsi</li>
        <li>Kronik hipertansiyon, gebelik öncesi diyabet, böbrek hastalığı</li>
        <li>Lupus veya antifosfolipid sendromu</li>
        <li>İkiz veya çoğul gebelik, tüp bebek gebeliği</li>
        <li>İlk gebelik, 35 yaş üstü, vücut kitle indeksi 30 ve üzeri</li>
        <li>Annede veya kız kardeşte preeklampsi öyküsü</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Düşük doz aspirin ile korunma</h2>
      <p>
        Risk değerlendirmesi ilk trimesterde yapılır. Yüksek riskli gebelerde{" "}
        <strong>12–16. haftalar arasında başlanan düşük doz aspirin</strong>, 36. haftaya kadar
        sürdürüldüğünde preeklampsi ve özellikle erken başlangıçlı ağır preeklampsi riskini belirgin
        biçimde azaltır. Kalsiyum alımı düşük olan gebelerde kalsiyum desteği de önerilebilir.
        Aspirin yalnızca hekim önerisiyle kullanılmalıdır.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı ve takip</h2>
      <p>
        Tanı; tekrarlanan tansiyon ölçümleri, idrarda protein bakılması (protein/kreatinin oranı) ve
        kan tetkikleri (trombosit, karaciğer enzimleri, böbrek fonksiyonları) ile konur. Seçilmiş
        olgularda sFlt-1/PlGF oranı kısa vadeli riskin öngörülmesine yardımcı olur. Bebeğin
        büyümesi, amniyon sıvısı ve kan akımı ultrason ve Doppler ile izlenir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi ve doğum zamanlaması</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>37. hafta ve sonrası:</strong> genellikle doğum önerilir.
        </li>
        <li>
          <strong>34–37. hafta:</strong> ağır bulgu yoksa yakın takip; anne veya bebekte bozulma
          olursa doğum.
        </li>
        <li>
          <strong>34. haftadan önce:</strong> hastanede takip, tansiyon ilaçları, bebeğin akciğer
          gelişimi için kortikosteroid, gerektiğinde nöbet önleyici magnezyum sülfat.
        </li>
        <li>
          Ağır preeklampsi, HELLP sendromu veya eklampside gebelik haftasından bağımsız olarak doğum
          planlanır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Doğumdan sonra</h2>
      <p>
        Preeklampsi doğumdan sonraki ilk 6 hafta içinde de başlayabilir veya ağırlaşabilir; bu
        dönemde baş ağrısı, görme bozukluğu veya nefes darlığı acil değerlendirme gerektirir.
        Preeklampsi geçiren kadınlarda ileride hipertansiyon ve kalp-damar hastalığı riski
        arttığından yıllık tansiyon ölçümü ve sağlıklı yaşam önerileri önemlidir. Ayrıntılı bilgi
        için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gestasyonel-diyabet" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelik şekeri
        </Link>{" "}
        ve{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gebelikte-tarama-testleri" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelikte tarama testleri
        </Link>{" "}
        yazılarına da göz atabilirsiniz.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da riskli gebelik takibi ve preeklampsi yönetimi konusunda
        hizmet vermektedir.
      </p>
    </ArticleShell>
  );
}

function SmearHpvPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Rahim ağzı (serviks) kanseri, düzenli taramayla önlenebilen birkaç kanserden biridir.
        Kanserlerin neredeyse tamamı kalıcı yüksek riskli{" "}
        <strong>HPV (insan papilloma virüsü)</strong> enfeksiyonuna bağlıdır ve hücrelerin kansere
        dönüşmesi genellikle yıllar alır. <strong>Smear testi</strong> ve <strong>HPV testi</strong>{" "}
        bu süreçte kanser öncesi değişiklikleri erken yakalamayı sağlar.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Smear ve HPV testi arasındaki fark
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Smear (sitoloji):</strong> rahim ağzı hücrelerinde anormal değişiklik olup
          olmadığını gösterir.
        </li>
        <li>
          <strong>HPV testi:</strong> kansere yol açabilen yüksek riskli HPV tiplerinin varlığını
          saptar; HPV 16 ve 18 ayrıca bildirilebilir.
        </li>
        <li>
          İki test aynı örnekten çalışılabilir. HPV testi, tek başına smeara göre daha duyarlıdır ve
          negatif sonuç uzun süreli güvence sağlar.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kimler, ne sıklıkla taranmalı?</h2>
      <p>
        Türkiye&apos;de Sağlık Bakanlığı ulusal tarama programı{" "}
        <strong>30–65 yaş arası kadınlara 5 yılda bir HPV testi ve smear</strong> önerir; bu tarama
        KETEM&apos;lerde ve aile hekimliklerinde ücretsizdir. Uluslararası rehberler 21–25 yaşta
        başlayan taramayı tanımlar. HIV pozitif, organ nakli olmuş veya bağışıklığı baskılanmış
        kadınlarda daha sık tarama gerekir. Rahim ağzı da alınmış ve öncesinde anormal sonuç öyküsü
        olmayan kadınlarda tarama sonlandırılabilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Teste hazırlık</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Adet döneminin dışında, tercihen adetin bitiminden birkaç gün sonra yaptırın.</li>
        <li>
          Testten önceki 48 saat cinsel ilişki, vajinal duş, vajinal ilaç ve kayganlaştırıcıdan
          kaçının.
        </li>
        <li>Gebelikte de smear güvenle alınabilir.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Sonuçlar ne anlama gelir?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>HPV negatif, smear normal:</strong> 5 yıl sonra tekrar tarama.
        </li>
        <li>
          <strong>HPV pozitif (16/18 dışı), smear normal:</strong> genellikle 1 yıl sonra tekrar
          test; çoğu enfeksiyon bu sürede temizlenir.
        </li>
        <li>
          <strong>HPV 16 veya 18 pozitif:</strong> smear normal olsa bile kolposkopi önerilir.
        </li>
        <li>
          <strong>ASCUS / LSIL:</strong> HPV durumuna göre takip veya kolposkopi.
        </li>
        <li>
          <strong>ASC-H / HSIL / AGC:</strong> kolposkopi ve biyopsi; gerekirse tedavi.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kolposkopi ve tedavi</h2>
      <p>
        Kolposkopide rahim ağzı büyütülerek incelenir, asetik asit ile belirginleşen anormal
        alanlardan küçük biyopsiler alınır. Yüksek dereceli kanser öncesi lezyon (CIN 2–3)
        saptanırsa, anormal dokunun lokal anestezi altında ince bir tel halka ile çıkarıldığı{" "}
        <strong>LEEP / konizasyon</strong> uygulanır. Bu işlem rahmi korur; sonrasında HPV testi ile
        takip sürdürülür.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Korunma</h2>
      <p>
        Düzenli tarama,{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "hpv-asisi" }}
          className="text-[color:var(--primary-text)] underline"
        >
          HPV aşısı
        </Link>
        , prezervatif kullanımı ve sigarayı bırakmak rahim ağzı kanseri riskini belirgin biçimde
        azaltır. Aşı olmuş kadınlar da taramaya devam etmelidir. Anormal kanama, özellikle cinsel
        ilişki sonrası kanama, tarama zamanını beklemeden değerlendirilmelidir.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da rahim ağzı kanseri taraması, kolposkopi ve takip hizmeti
        vermektedir.
      </p>
    </ArticleShell>
  );
}

function AnormalKanamaPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Anormal rahim kanaması</strong>, adet sıklığı, süresi, düzeni veya miktarı normal
        sınırların dışına çıkan ya da adetler arasında görülen kanamaları kapsar. Üreme çağındaki
        kadınların yaklaşık üçte biri hayatının bir döneminde bu sorunu yaşar. Çoğu neden iyi
        huyludur ve tedavi edilebilir; ancak doğru tanı için sistematik değerlendirme gerekir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Normal adetin sınırları</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Döngü uzunluğu: 24–38 gün</li>
        <li>Kanama süresi: 8 günden kısa</li>
        <li>Döngüler arası değişkenlik: genellikle 7–9 günden az</li>
        <li>Miktar: günlük yaşamı ve iş gücünü etkilemeyen düzeyde</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Nedenler: PALM-COEIN sınıflaması
      </h2>
      <p>Uluslararası Jinekoloji ve Obstetrik Federasyonu (FIGO) nedenleri iki gruba ayırır:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Yapısal (PALM):</strong>{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "histeroskopi" }}
            className="text-[color:var(--primary-text)] underline"
          >
            endometrial polip
          </Link>
          , adenomyozis,{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "miyom" }}
            className="text-[color:var(--primary-text)] underline"
          >
            miyom
          </Link>
          , kanser öncesi değişiklik (hiperplazi) ve kanser.
        </li>
        <li>
          <strong>Yapısal olmayan (COEIN):</strong> pıhtılaşma bozuklukları, yumurtlama bozuklukları
          (
          <Link
            to="/blog/$slug"
            params={{ slug: "pcos" }}
            className="text-[color:var(--primary-text)] underline"
          >
            PCOS
          </Link>
          , tiroid, prolaktin yüksekliği, aşırı kilo değişimi, stres), endometriyal nedenler, ilaç
          ve spiral gibi tıbbi nedenler ve henüz sınıflanmamış diğer nedenler.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Yaşa göre sık nedenler</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ergenlik:</strong> yumurtlama sisteminin olgunlaşmaması, pıhtılaşma bozuklukları.
        </li>
        <li>
          <strong>Üreme çağı:</strong> gebelik ve gebelik komplikasyonları, polip, miyom, PCOS,
          hormonal ilaçlar.
        </li>
        <li>
          <strong>Menopoz geçişi:</strong> düzensiz yumurtlama, polip, miyom, endometrial
          hiperplazi.
        </li>
        <li>
          <strong>Menopoz sonrası:</strong> atrofi, polip; endometrium kanseri mutlaka
          dışlanmalıdır.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı yöntemleri</h2>
      <p>
        Değerlendirme gebelik testi ile başlar. Ayrıntılı öykü ve muayeneyi transvajinal ultrason,
        tam kan sayımı ve ferritin izler; gerektiğinde tiroid, prolaktin ve pıhtılaşma testleri
        eklenir. Rahim zarından örnek alınması (endometrial biyopsi) 45 yaş üstü, obezite veya PCOS
        gibi risk faktörü bulunan ve tedaviye yanıt vermeyen kadınlarda önerilir. Rahim içi polip
        veya miyom şüphesinde <strong>histeroskopi</strong> hem tanı hem tedavi sağlar.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Hormonsuz ilaçlar:</strong> traneksamik asit ve steroid olmayan ağrı kesiciler
          kanama miktarını azaltır.
        </li>
        <li>
          <strong>Hormonal tedavi:</strong> kombine doğum kontrol hapları, progesteron ve kanamayı
          %90&apos;a varan oranda azaltan hormonlu rahim içi araç.
        </li>
        <li>
          <strong>Histeroskopik cerrahi:</strong> polip ve rahim içi miyomların kesisiz çıkarılması.
        </li>
        <li>
          <strong>Endometrial ablasyon veya histerektomi:</strong> çocuk isteği olmayan ve diğer
          tedavilere yanıt vermeyen seçilmiş kadınlarda.
        </li>
        <li>Demir eksikliği varsa demir tedavisi eklenir.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Ne zaman acil başvurmalı?</h2>
      <p>
        Bir saatte bir veya daha sık ped değiştirmeyi gerektiren kanama, baş dönmesi veya bayılma
        hissi, gebelik olasılığı varken kanama ve şiddetli kasık ağrısı acil değerlendirme
        gerektirir. Gebelikteki kanamalar için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gebelikte-kanama" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelikte kanama
        </Link>{" "}
        yazısına bakabilirsiniz.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da anormal rahim kanamasının tanısı ve histeroskopik
        tedavisi konusunda hizmet vermektedir.
      </p>
    </ArticleShell>
  );
}

function DisGebelikPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Dış gebelik (ektopik gebelik)</strong>, döllenmiş yumurtanın rahim boşluğu dışında,
        en sık fallop tüpünde yerleşmesidir. Gebeliklerin yaklaşık %1–2&apos;sinde görülür ve ilk
        trimesterde anneyi tehdit eden kanamaların en önemli nedenidir. Erken tanı konduğunda çoğu
        zaman tüp korunarak, ilaçla veya küçük kesili kapalı ameliyatla güvenle tedavi edilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Acil belirtiler</h2>
      <p>
        Gebelik testi pozitifken veya adet gecikmişken aşağıdakilerden biri varsa beklemeden acil
        servise başvurun:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Şiddetli veya giderek artan, tek taraflı kasık ağrısı</li>
        <li>Baş dönmesi, bayılma hissi, soğuk terleme</li>
        <li>Omuz ucu ağrısı (karın içi kanamaya bağlı)</li>
        <li>Makata baskı hissi</li>
        <li>Yoğun vajinal kanama</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risk faktörleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Önceki dış gebelik</li>
        <li>Tüp ameliyatı, tüp ligasyonu</li>
        <li>Geçirilmiş pelvik enfeksiyon, özellikle klamidya</li>
        <li>Endometriozis</li>
        <li>Tüp bebek ve yardımcı üreme teknikleri</li>
        <li>Sigara kullanımı</li>
        <li>Rahim içi araç varken gebe kalma</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı</h2>
      <p>
        Tanı; kanda <strong>beta-hCG</strong> düzeyinin 48 saat arayla izlenmesi ve{" "}
        <strong>transvajinal ultrason</strong> ile konur. Normal gebelikte beta-hCG belirli bir
        hızla yükselir ve belirli bir düzeyin üstünde rahim içinde gebelik kesesi görülür. Kesenin
        rahim içinde görülmemesi, tüp bölgesinde kitle veya karın içinde serbest sıvı dış gebeliği
        düşündürür. Kesin yer belirlenemiyorsa yakın takip yapılır. Ayrıntılar için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gebelik-testi-ne-zaman-nasil-yapilir" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelik testi
        </Link>{" "}
        ve{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gebelikte-kanama" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelikte kanama
        </Link>{" "}
        yazılarına bakabilirsiniz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Bekleyerek izlem:</strong> beta-hCG düzeyi düşük ve kendiliğinden azalan,
          belirtisiz seçilmiş olgularda.
        </li>
        <li>
          <strong>Metotreksat:</strong> stabil, kanama bulgusu olmayan, küçük kitleli olgularda tüpü
          koruyan ilaç tedavisi; beta-hCG sıfırlanana kadar izlem gerekir.
        </li>
        <li>
          <strong>Laparoskopik cerrahi:</strong> iç kanama, yırtılma şüphesi, yüksek beta-hCG veya
          ilaca yanıtsızlıkta; tüp korunarak gebelik dokusu çıkarılır ya da tüp alınır.{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "laparoskopi-nedir" }}
            className="text-[color:var(--primary-text)] underline"
          >
            Laparoskopi
          </Link>{" "}
          hızlı iyileşme sağlar.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedaviden sonra</h2>
      <p>
        Kan grubu Rh negatif olan kadınlara anti-D iğnesi yapılır. Metotreksat sonrası yeni gebelik
        için genellikle en az 3 ay beklenmesi önerilir. Sonraki gebelikte test pozitif olur olmaz
        erken ultrason ile gebeliğin rahim içinde olduğu doğrulanmalıdır. Kayıp yaşamanın duygusal
        yükü göz ardı edilmemeli, gerektiğinde destek alınmalıdır.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da dış gebeliğin tanısı, ilaç tedavisi ve laparoskopik
        cerrahisi konusunda hizmet vermektedir.
      </p>
    </ArticleShell>
  );
}

function DusukPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Gebelik kaybı, gebelik olduğu bilinen kadınların yaklaşık %10–15&apos;inde ilk 12 haftada
        yaşanan, yaygın ama derin duygusal etki bırakan bir durumdur. Erken düşüklerin büyük
        çoğunluğu bebekteki rastlantısal kromozom farklılıklarından kaynaklanır ve annenin yaptığı
        bir şeyle ilişkili değildir. İki veya daha fazla kayıp yaşayan çiftlerde ise nedene yönelik
        araştırma önerilir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Belirtiler ve ne zaman başvurmalı?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Vajinal kanama veya lekelenme</li>
        <li>Adet sancısına benzer ya da daha şiddetli kasık ve bel ağrısı</li>
        <li>Doku parçalarının gelmesi</li>
        <li>Gebelik belirtilerinde ani azalma</li>
        <li>
          Saatte bir pedi dolduran kanama, ateş, kötü kokulu akıntı veya bayılma hissi acil başvuru
          gerektirir.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Nedenler</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Bebekte rastlantısal kromozom farklılıkları (en sık neden)</li>
        <li>İleri anne yaşı</li>
        <li>Antifosfolipid sendromu</li>
        <li>Kontrolsüz diyabet ve tiroid hastalıkları</li>
        <li>
          Rahim içi yapısal sorunlar: septum,{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "histeroskopi" }}
            className="text-[color:var(--primary-text)] underline"
          >
            polip
          </Link>
          , rahim içi{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "miyom" }}
            className="text-[color:var(--primary-text)] underline"
          >
            miyom
          </Link>
          , yapışıklıklar
        </li>
        <li>Sigara, aşırı alkol ve bazı ilaçlar</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tanı</h2>
      <p>
        Tanı transvajinal ultrason ve gerekirse beta-hCG takibi ile konur. Erken haftalarda kesin
        karar için 7–14 gün sonra tekrar ultrason gerekebilir. Kanamanın diğer nedenleri için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gebelikte-kanama" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelikte kanama
        </Link>{" "}
        yazımıza, dış gebelik ayrımı için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "dis-gebelik" }}
          className="text-[color:var(--primary-text)] underline"
        >
          dış gebelik
        </Link>{" "}
        yazımıza bakabilirsiniz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi seçenekleri</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Bekleyerek izlem:</strong> dokunun kendiliğinden atılması; çoğu kadında 2 hafta
          içinde tamamlanır.
        </li>
        <li>
          <strong>İlaç tedavisi:</strong> misoprostol (gerekirse mifepriston ile) süreci
          hızlandırır.
        </li>
        <li>
          <strong>Vakum aspirasyonu:</strong> yoğun kanama, enfeksiyon veya tamamlanamayan düşükte;
          kısa süreli ve güvenli bir işlemdir.
        </li>
        <li>Kan grubu Rh negatif olan kadınlarda uygun durumlarda anti-D iğnesi yapılır.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Tekrarlayan gebelik kaybında tetkikler
      </h2>
      <p>İki veya daha fazla gebelik kaybında önerilen değerlendirme şunları içerir:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Antifosfolipid antikorları (lupus antikoagülanı, antikardiyolipin, anti-beta2
          glikoprotein)
        </li>
        <li>Tiroid fonksiyonları ve tiroid antikorları</li>
        <li>Rahim yapısının 3 boyutlu ultrason, histeroskopi veya MR ile değerlendirilmesi</li>
        <li>Seçilmiş çiftlerde kromozom analizi ve düşük dokusunun genetik incelemesi</li>
        <li>Şeker, vücut kitle indeksi ve yaşam tarzı değerlendirmesi</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tedavi ve sonraki gebelik</h2>
      <p>
        Antifosfolipid sendromunda düşük doz aspirin ve heparin, rahim septumu veya polipte
        histeroskopik cerrahi, tiroid bozukluğunda ilaç tedavisi uygulanır. Nedeni bulunamayan
        çiftlerde bile erken gebelik takibi ve destekle sonraki gebelikte canlı doğum şansı
        yüksektir. Gebelik öncesi folik asit, sigaranın bırakılması ve sağlıklı kilo önerilir;
        yaşanan kaybın duygusal yükü için destek almaktan çekinmeyin.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da gebelik kaybı yönetimi ve tekrarlayan gebelik kaybının
        değerlendirilmesi konusunda hizmet vermektedir.
      </p>
    </ArticleShell>
  );
}

function LohusalikPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Lohusalık</strong>, doğumdan sonraki yaklaşık 6 haftalık dönemdir. Bu sürede rahim
        gebelik öncesi boyutuna döner, hormonlar yeniden dengelenir ve vücut doğumun etkilerinden
        toparlanır. Yeni bir bebekle geçen bu haftalarda annenin kendi sağlığı da en az bebeğinki
        kadar önemlidir.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Lohusa kanaması (loşi)</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>İlk 3–4 gün:</strong> kırmızı, adetten yoğun olabilir; küçük pıhtılar normaldir.
        </li>
        <li>
          <strong>1–2. hafta:</strong> pembe-kahverengi, azalan miktarda.
        </li>
        <li>
          <strong>2–6. hafta:</strong> sarımsı-beyaz akıntı; genellikle 4–6 haftada sona erer.
        </li>
        <li>Bu dönemde tampon yerine ped kullanılmalıdır.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Dikiş ve sezaryen yarası bakımı</h2>
      <p>
        Normal doğumdaki dikişler her tuvaletten sonra ılık suyla önden arkaya yıkanıp
        kurulanmalıdır; ilk günlerde soğuk uygulama şişliği azaltır ve dikişler 2–3 haftada
        iyileşir. Sezaryen sonrası ilk gün ayağa kalkmak, tromboz riskini azaltır; yara 1–2 haftada
        kapanır ve ilk 6 hafta ağır yük kaldırılmamalıdır. Doğum şekline göre iyileşme farkları için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "normal-dogum-mu-sezaryen-mi" }}
          className="text-[color:var(--primary-text)] underline"
        >
          normal doğum ve sezaryen
        </Link>{" "}
        yazımıza bakabilirsiniz.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Acil başvuru gerektiren belirtiler
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Saatte bir pedi dolduran kanama veya büyük pıhtılar</li>
        <li>38°C üzeri ateş, kötü kokulu akıntı</li>
        <li>
          Şiddetli baş ağrısı, görme bozukluğu — doğum sonrası{" "}
          <Link
            to="/blog/$slug"
            params={{ slug: "preeklampsi" }}
            className="text-[color:var(--primary-text)] underline"
          >
            preeklampsi
          </Link>{" "}
          belirtisi olabilir
        </li>
        <li>Göğüs ağrısı, nefes darlığı, bir bacakta ağrılı şişlik</li>
        <li>Memede kızarık, ağrılı sertlik ve ateş</li>
        <li>Kendinize veya bebeğinize zarar verme düşünceleri</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Ruh sağlığı: hüzün mü, depresyon mu?
      </h2>
      <p>
        Annelerin büyük bölümü ilk günlerde duygu dalgalanması ve ağlamaklılık yaşar; bu{" "}
        <strong>doğum sonrası hüzün</strong> genellikle 2 hafta içinde geçer. İki haftadan uzun
        süren mutsuzluk, zevk alamama, yoğun kaygı, uyku ve iştah bozukluğu{" "}
        <strong>doğum sonrası depresyonu</strong> düşündürür. Bu durum yaklaşık her 7 anneden
        birinde görülür, annenin suçu değildir ve tedaviyle düzelir. Yardım istemekten çekinmeyin.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Emzirme, beslenme ve hareket</h2>
      <p>
        Emzirme anne ve bebek için pek çok yarar sağlar; ilk günlerde ten tene temas ve sık emzirme
        süt yapımını destekler. Emziren annelerin dengeli beslenmesi, bol sıvı alması ve hekim
        önerisiyle vitamin desteklerini sürdürmesi önerilir. Hafif yürüyüşlere erken başlanabilir;
        pelvik taban egzersizleri idrar kaçırmayı önlemeye yardımcı olur. Yoğun egzersize genellikle
        6. hafta kontrolünden sonra dönülür.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Kontroller ve doğum kontrolü</h2>
      <p>
        İlk haftalarda tansiyon, kanama, yara ve emzirme kontrolü; 6. hafta civarında kapsamlı
        muayene önerilir. Gebelikte{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "gestasyonel-diyabet" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gebelik şekeri
        </Link>{" "}
        saptanan kadınlarda doğumdan 4–12 hafta sonra şeker yükleme testi yapılır. Emzirme tek
        başına güvenilir korunma sağlamaz; iki doğum arasında en az 18 ay olması önerilir.
        Emzirmeyle uyumlu seçenekler için{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "dogum-kontrol-yontemleri" }}
          className="text-[color:var(--primary-text)] underline"
        >
          doğum kontrol yöntemleri
        </Link>{" "}
        yazımıza bakabilirsiniz.
      </p>

      <MedicalDisclaimer />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, Adana&apos;da doğum sonrası takip ve lohusalık dönemi kontrolleri
        konusunda hizmet vermektedir.
      </p>
    </ArticleShell>
  );
}
